import {
  useEffect,
  useState,
  useRef,
  useCallback,
  useMemo,
  Fragment,
} from "react";
import {
  useParams,
  useNavigate,
  useSearchParams,
  useLocation,
} from "react-router-dom";
import {
  Box,
  Grid,
  Group,
  Text,
  Button,
  Loader,
  Alert,
  Stack,
  Paper,
  Badge,
  Card,
  Divider,
  Menu,
  Modal,
  TextInput,
  Collapse,
  ActionIcon,
  Tooltip,
  Textarea,
  Checkbox,
  Skeleton,
  NavLink,
  ScrollArea,
  Kbd,
  Popover,
  Select,
  Image,
  Center,
  FileButton,
  Progress,
} from "@mantine/core";
import {
  IconAlertCircle,
  IconCheck,
  IconArrowLeft,
  IconAlertTriangle,
  IconChevronDown,
  IconChevronUp,
  IconStethoscope,
  IconFileText,
  IconHistory,
  IconPhoto,
  IconFlask,
  IconMessageCircle,
  IconExternalLink,
  IconCamera,
  IconUpload,
  IconEye,
  IconSearch,
  IconCloud,
  IconCloudCheck,
  IconCloudOff,
  IconDownload,
  IconTrash,
  IconEdit,
  IconLayoutGrid,
  IconAddressBook,
  IconScissors,
  IconNotes,
  IconMessage,
  IconCircleOff,
  IconSettings,
  IconPlus,
  IconMicroscope,
  IconFile,
  IconX,
  IconReplace,
  IconFlag,
  IconSend,
  IconLockOpen,
  IconDots,
  IconActivity,
  IconPackages,
} from "@tabler/icons-react";
import { notifications } from "@mantine/notifications";
import { useDisclosure, useDebouncedValue } from "@mantine/hooks";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import dayjs from "dayjs";
import {
  overlayAmendmentCase,
  useAmendmentDiagnosticDraft,
} from "../../hooks/useAmendmentDiagnosticDraft";
import type { FragmentCountQualifier } from "../../utils/fragmentCount";
import type { AssignmentState } from "../../types/casePathologistAssignment";
import { getCasePathologistAssignmentError } from "../../types/casePathologistAssignment";
import { useAuth } from "../../hooks/useAuth";
import { useHasPermission } from "../../hooks/useHasPermission";
import {
  useCaseForPathologistReview,
  useStartPathologistReview,
  useCaseLock,
  useReleaseLock,
  useExtendLock,
  useForceReleaseLock,
  usePathologistReviewQueue,
  useSaveDiagnosisDraft,
  useDiagnosisMacros,
  useDiagnosisCategories,
  useUserPreference,
  useSaveUserPreference,
  usePathologistComments,
  useOpenCaseByBarcode,
  useControlSummary,
  useArchiveSpecimen,
  useAddSpecimen,
  useRecoverSpecimen,
} from "../../hooks/useWorkflows";
import { useSendReport } from "../../hooks/useSendReport";
import {
  useAddendumDraft,
  useAddendumEligibility,
} from "../../hooks/useAddendum";
import { SendReportModal } from "../../components/report-delivery/SendReportModal";
import { useReportDeliveryRecall } from "../../hooks/useReportDeliveryRecall";
import { getPathologistQueueReturnTo } from "./pathologistQueueNavigation";
import { RecallReportButton } from "../../components/report-delivery/RecallReportButton";
import { RecallReportModal } from "../../components/report-delivery/RecallReportModal";
import { ReportDeliveryStateBadge } from "../../components/report-delivery/ReportDeliveryStateBadge";
import { ReportDeliveryHistoryModal } from "../../components/report-delivery/ReportDeliveryHistoryModal";
import { SignedOffDeliveryAlert } from "../../components/report-delivery/SignedOffDeliveryAlert";
import { CaseNotesSection } from "../../components/accessioning/CaseNotesSection";
import { CaseFlagsSection } from "../../components/cases/CaseFlagsSection";
import { CaseCommentsSection } from "../../components/accessioning/CaseCommentsSection";
import ErrorsCard from "../../components/accessioning/ErrorsCard";
import MacroSearchModal from "../../components/pathologist-review/MacroSearchModal";
import {
  ReviewPreferencesModal,
  REVIEW_SCREEN_PREFERENCE_KEY,
  DEFAULT_REVIEW_PREFERENCES,
  type ReviewScreenPreferences,
} from "../../components/pathologist-review/ReviewPreferencesModal";
import {
  hasApplicableOrganMap,
  OrganMapSection,
  type OrganMapSpecimen,
} from "../../components/pathologist-review/OrganMapSection";
import RequestStainModal from "../../components/pathologist-review/RequestStainModal";
import RequestExternalOpinionModal from "../../components/pathologist-review/RequestExternalOpinionModal";
import PendingStainsPanel from "../../components/pathologist-review/PendingStainsPanel";
import { AdditionalStainNoReportChangeAction } from "../../components/pathologist-review/AdditionalStainNoReportChangeAction";
import { NotesCenter } from "../../components/pathologist-review/NotesCenter";
import { CommentsCenter } from "../../components/pathologist-review/CommentsCenter";
import { ErrorsCenter } from "../../components/pathologist-review/ErrorsCenter";
import { StainsCenter } from "../../components/pathologist-review/StainsCenter";
import { DiscussionsCenter } from "../../components/pathologist-review/DiscussionsCenter";
import { ExternalOpinionCenter } from "../../components/pathologist-review/ExternalOpinionCenter";
import { DocumentsImagesCenter } from "../../components/pathologist-review/DocumentsImagesCenter";
import { GrossingCenter } from "../../components/pathologist-review/GrossingCenter";
import RequisitionCenter, {
  type RequisitionCenterHandle,
  type LiveRequisitionData,
} from "../../components/pathologist-review/RequisitionCenter";
import { DiagnosisEntryGrid } from "../../components/pathologist-review/DiagnosisEntryGrid";
import { AddSpecimenModal } from "../../components/pathologist-review/AddSpecimenModal";
import { RecoverSpecimensModal } from "../../components/pathologist-review/RecoverSpecimensModal";
import { CaseLevelSection } from "../../components/pathologist-review/CaseLevelSection";
import { AddendumSection } from "../../components/pathologist-review/AddendumSection";
import { AddendumEditorModal } from "../../components/pathologist-review/AddendumEditorModal";
import { AddendumReportPreviewModal } from "../../components/pathologist-review/AddendumReportPreviewModal";
import { AmendmentEditorModal } from "../../components/pathologist-review/AmendmentEditorModal";
import ActiveSendOutsBanner, {
  useActiveSendOuts,
} from "../../components/send-outs/ActiveSendOutsBanner";
import { usePendingStains } from "../../hooks/useStainRequest";
import {
  useGrossCodes,
  useIcdCodes,
  useSourcesWithSubsites,
} from "../../hooks/useReferenceData";
import {
  ReportPreviewModal,
  SignedReportViewerModal,
  SignOffAuthModal,
  SignOffSuccessModal,
  ReportHistoryModal,
  FlagDiscussionModal,
  MalignancyNotificationModal,
  DiscussionRequiredModal,
} from "../../components/workflows";
import { PendingDiscussionsModal } from "../../components/workflows/PendingDiscussionsModal";
import * as signOffService from "../../services/signOff.service";
import * as cancerNotificationService from "../../services/cancerNotification.service";
import { CaseStatusBadge } from "../../components/cases";
import ImageUploadModal from "../../components/cases/ImageUploadModal";
import CameraCaptureModal from "../../components/cases/CameraCaptureModal";
// FolderWatchPanel — available but not used on this page currently
// import FolderWatchPanel from "../../components/cases/FolderWatchPanel";
import { caseImagesService, type CaseImage } from "../../services";
import { DocumentUpload } from "../../components/accessioning";
import { casesService, type CaseFile } from "../../services/cases.service";
import { usePriorCases } from "../../hooks/useCases";
import { useCaseDiscussions } from "../../hooks/useInternalDiscussions";
import { DiscussionStatus } from "../../services/internalDiscussions.service";
import type { RequiredDiscussionInfo } from "../../services/signOff.service";
import { pathologistReviewService } from "../../services/workflows.service";
import { documentViewService } from "../../services/documentView.service";
import type { UploadedFile } from "../../types";
import { parseDateString } from "../../utils/dates";
import {
  calculateAgeFromDateOnly,
  formatDateOnlyForDisplay,
  parseDateOnly,
  requireDateOnlyString,
} from "../../utils/dateOnly";
import { useLabTodayDateOnly } from "../../hooks/useLabTodayDateOnly";
import { getUploadSourceBadge } from "../../utils/uploadSource";
import { RelatedCaseCard } from "../../components/pathologist-review/RelatedCaseCard";
import { RightPanelWidget } from "../../components/pathologist-review/RightPanelWidget";
import { CaseInfoWidget } from "../../components/pathologist-review/widgets/CaseInfoWidget";
import { StainsWidget } from "../../components/pathologist-review/widgets/StainsWidget";
import { HistoricalReportsWidget } from "../../components/pathologist-review/widgets/HistoricalReportsWidget";
import { MicroscopicDescriptionWidget } from "../../components/pathologist-review/widgets/MicroscopicDescriptionWidget";
import { GrossingWidget } from "../../components/pathologist-review/widgets/GrossingWidget";
import { IcdCodesWidget } from "../../components/pathologist-review/widgets/IcdCodesWidget";
import { CptCodesWidget } from "../../components/pathologist-review/widgets/CptCodesWidget";
import { CaseQueueWidget } from "../../components/pathologist-review/widgets/CaseQueueWidget";
import { QAReviewStatusWidget } from "../../components/pathologist-review/widgets/QAReviewStatusWidget";
import { ALL_REVIEW_WIDGETS } from "../../components/pathologist-review/ReviewPreferencesModal";
import { DocumentView } from "../../components/pathologist-review/DocumentView";
import { useActiveReportTemplates } from "../../hooks/admin/useReportTemplates";
import { isEmptyHtml } from "../../utils/helpers";
import { PdfPagesRenderer } from "../../components/workflows/PdfPagesRenderer";
import { useBarcodeScanner } from "../../hooks/useBarcodeScanner";
import { resolveVariableChipValues } from "../../components/shared/tiptap/VariableChip";
import { CorrectionsCenter } from "../../components/pathologist-review/CorrectionsCenter";
import { useCorrectionRequests } from "../../hooks/useCorrectionRequests";
import UpdatePatientInfoModal from "../../components/modals/UpdatePatientInfoModal";
import LegacyReportCorrectionModal from "../../components/admin/legacy-reports/LegacyReportCorrectionModal";
import { useLegacyImportedCase } from "../../hooks/useLegacyCases";
import { getReportPhotoEligibility } from "../../utils/reportPhotoEligibility";
import { api } from "../../lib";
import { downloadFileFromUrl } from "../../utils/downloadFile";
import { CaseImageReportControls } from "../../components/pathologist-review/CaseImageReportControls";
import { CaseFileReportControls } from "../../components/pathologist-review/CaseFileReportControls";
import { PendingReportAttachmentNotice } from "../../components/pathologist-review/PendingReportAttachmentNotice";
import { CaseImageCaptionUpdateQueue } from "../../components/pathologist-review/CaseImageCaptionUpdateQueue";
import { buildPathologistReviewDraftPayload } from "./pathologistReviewDraftPolicy";
import { buildReflexSignOutContinuation } from "./reflexSignOutFlow";
import { isImageMimeType } from "../../utils/reportAttachments";
import AssignmentHistoryTransferContext from "../../components/pathologist-review/AssignmentHistoryTransferContext";
import StalePathologistAssignmentIndicator from "../../components/pathologist-review/StalePathologistAssignmentIndicator";
import ReflexPanelDecisionModal from "../../components/pathologist-review/ReflexPanelDecisionModal";
import PanelSummarySection from "../../components/pathologist-review/PanelSummarySection";
import {
  reflexPanelsService,
  type ReflexPanelProposal,
  type TissueCypherSignOutProposal,
} from "../../services/reflexPanels.service";
import { useDraftSaveCoordinator } from "../../hooks/useDraftSaveCoordinator";
import {
  diagnosisLocationForGrossingSubsiteChange,
  projectLiveGrossingSpecimens,
} from "../../utils/liveGrossingSpecimens";
import {
  buildSignOutWarningPresentation,
  getGrossTemplateSignOutGuidance,
  hasWholeCaseIcdCode,
  normalizeBackendSignOutWarning,
  toSignOutWarning,
  type SignOutIssue,
  type SignOutWarning,
} from "./pathologistSignOutWarnings";

// Diagnosis data type for each specimen
interface SpecimenDiagnosisData {
  specimenId: string;
  macroCode: string;
  diagnosisText: string;
  microscopicDescription: string;
  location: string | null;
  category: string | null;
  cptCode: string | null;
  cptDisplayCode?: string | null;
  icdCode: string | null;
  icdCodes: string[];
  color: string | null;
  sizeCm: string | null;
  sizeMm: string | null;
  consistency: string | null;
}

// Grossing data type for each specimen
interface SpecimenGrossingData {
  specimenId: string;
  grossCode: string;
  grossCodeId: string;
  grossDescription: string;
  fragmentCount?: number | null;
  fragmentCountQualifier?: FragmentCountQualifier | null;
  sourceId?: string | null;
  subsiteId?: string | null;
  subsiteName?: string | null;
  collectionMethod?: string | null;
  findings?: string | null;
  material?: string | null;
  variableValues?: Record<string, string>;
}

function buildGrossingDraftEntry(grossingData: SpecimenGrossingData) {
  return {
    specimenId: grossingData.specimenId,
    grossCodeId: grossingData.grossCodeId || "",
    rawGrossDescription: grossingData.grossDescription,
    variableValues: grossingData.variableValues,
    grossCode: grossingData.grossCode,
    subsiteName: grossingData.subsiteName,
    grossDescription:
      resolveGrossVariables(
        grossingData.grossDescription,
        grossingData.variableValues
      ) ?? "",
    fragmentCount: grossingData.fragmentCount,
    fragmentCountQualifier: grossingData.fragmentCountQualifier,
    sourceId: grossingData.sourceId,
    subsiteId: grossingData.subsiteId,
    findings: grossingData.findings,
    collectionMethod: grossingData.collectionMethod,
    material: grossingData.material,
  };
}

function buildGrossingDraftEntries(
  grossingDataMap: Map<string, SpecimenGrossingData>
) {
  return Array.from(grossingDataMap.values()).map(buildGrossingDraftEntry);
}

function buildDraftFingerprint(
  caseId: string,
  diagnosisDataMap: Map<string, SpecimenDiagnosisData>,
  grossingDataMap: Map<string, SpecimenGrossingData>,
  frozenInterpretation: string,
  retainedSpecimenIds?: readonly string[]
): string {
  return JSON.stringify({
    caseId,
    d: Array.from(diagnosisDataMap.entries()),
    g: Array.from(grossingDataMap.entries()),
    frozenInterpretation,
    retainedSpecimenIds: retainedSpecimenIds ?? null,
  });
}

/**
 * Scans HTML content for unresolved template placeholders.
 * Returns a deduplicated list of tokens found, e.g. ["{{size1_mm}}", "[[QUANTITY]]"].
 * Detects:
 *   1. Bare {{variable}} tokens not inside a resolved chip
 *   2. Legacy [[VARIABLE]] tokens
 *   3. TipTap <span data-variable="name"> chips that have no data-variable-value set
 */
function getUnresolvedVariables(html: string | null | undefined): string[] {
  if (!html) return [];
  const found: string[] = [];

  // [[VARIABLE]] legacy tokens
  const bracketMatches = html.match(/\[\[([^\]]+)\]\]/g);
  if (bracketMatches) found.push(...bracketMatches);

  // TipTap variable chips without a filled value
  const chipRegex =
    /<span[^>]*data-variable="([^"]*)"(?![^>]*data-variable-value="[^"]+")[^>]*>/gi;
  let chipMatch = chipRegex.exec(html);
  while (chipMatch !== null) {
    found.push(`{{${chipMatch[1]}}}`);
    chipMatch = chipRegex.exec(html);
  }

  // Bare {{variable}} tokens (not inside a chip span)
  const stripped = html.replace(
    /<span[^>]*data-variable="[^"]*"[^>]*data-variable-value="[^"]+"[^>]*>.*?<\/span>/gi,
    ""
  );
  const braceMatches = stripped.match(/\{\{([^}]+)\}\}/g);
  if (braceMatches) found.push(...braceMatches);

  return Array.from(new Set(found));
}

// Resolve {{variable}} tokens and their <span data-variable> wrappers with actual values.
// data-variable-value attrs (filled by user in TipTap) take precedence over the vars map.
function resolveGrossVariables(
  html: string,
  vars: Record<string, string> | undefined
): string {
  if (!html) return html;
  // First pass: data-variable-value attrs + vars map
  let resolved = resolveVariableChipValues(html, vars);
  // Second pass: any bare {{name}} tokens not inside a span
  if (vars) {
    Object.entries(vars).forEach(([name, val]) => {
      if (!val) return;
      resolved = resolved.replace(new RegExp(`\\{\\{${name}\\}\\}`, "gi"), val);
    });
  }
  return resolved;
}

// Build a resolved copy of diagnosisDataMap for report preview, replacing all
// {{variable}} tokens and <span data-variable> wrappers with their actual values
function buildResolvedDiagnosisMap(
  diagnosisDataMap: Map<string, SpecimenDiagnosisData>,
  specimens: any[],
  caseData: any
): Map<string, SpecimenDiagnosisData> {
  const resolved = new Map<string, SpecimenDiagnosisData>();
  Array.from(diagnosisDataMap.entries()).forEach(([specimenId, diagData]) => {
    const specimen = specimens.find((s: any) => s.id === specimenId);
    const vars: Record<string, string> = {};
    const addVar = (key: string, val: string | null | undefined) => {
      if (val != null && val !== "") vars[key] = val;
    };
    addVar("specimen_letter", specimen?.number);
    addVar("source", specimen?.source?.name);
    addVar("subsite", diagData.location ?? specimen?.subsite?.name);
    addVar("collection_method", specimen?.collectionMethod);
    addVar(
      "block_count",
      specimen?.blocks?.length != null ? String(specimen.blocks.length) : null
    );
    addVar("case_number", caseData?.caseNumber);
    addVar(
      "patient_name",
      caseData?.patient
        ? `${caseData.patient.firstName || ""} ${
            caseData.patient.lastName || ""
          }`.trim()
        : null
    );
    addVar("date_collected", caseData?.dateCollected);
    addVar("status", caseData?.currentStatus?.name || caseData?.statusName);
    addVar("color", diagData.color);
    addVar("size_cm", diagData.sizeCm);
    addVar("sizeCm", diagData.sizeCm);
    addVar("size_mm", diagData.sizeMm);
    addVar("consistency", diagData.consistency);
    addVar("location", diagData.location ?? specimen?.subsite?.name);
    addVar("LOCATION", diagData.location ?? specimen?.subsite?.name);

    resolved.set(specimenId, {
      ...diagData,
      diagnosisText: resolveGrossVariables(diagData.diagnosisText, vars),
      microscopicDescription: resolveGrossVariables(
        diagData.microscopicDescription,
        vars
      ),
    });
  });
  return resolved;
}

// Helper function to convert technical error messages to user-friendly messages
const getUserFriendlyErrorMessage = (error: any): string => {
  const message = error?.message || error?.toString() || "";

  // Authentication errors
  if (
    message.includes("Invalid password") ||
    message.includes("Invalid credentials")
  ) {
    return "The password you entered is incorrect. Please try again.";
  }
  if (
    message.includes("Authentication required") ||
    message.includes("requiresPassword")
  ) {
    return "Please enter your password to continue.";
  }
  if (
    message.includes("session expired") ||
    message.includes("Session expired")
  ) {
    return "Your session has expired. Please enter your password again.";
  }
  if (
    message.includes("not authenticated") ||
    message.includes("Unauthorized")
  ) {
    return "You are not authorized to perform this action. Please log in again.";
  }

  // Validation errors
  if (message.includes("Diagnosis text is required")) {
    return "Please enter a diagnosis for all specimens before signing out.";
  }
  if (message.includes("Category is required")) {
    return "Please select a category for all specimens.";
  }
  if (message.includes("CPT code is required")) {
    return "Please select a CPT code for all specimens.";
  }
  if (message.includes("ICD code is required")) {
    return "Please select an ICD code for all specimens.";
  }
  if (message.includes("blocked")) {
    return "This case is blocked and cannot be signed out. Please resolve the blocking issues first.";
  }
  if (message.includes("already been signed")) {
    return "This case has already been signed out.";
  }
  if (message.includes("internal discussion")) {
    return "There are unresolved internal discussions that must be resolved before sign-out.";
  }
  if (message.includes("External opinion pending")) {
    return "External consultation is pending. You may proceed with caution.";
  }
  if (message.includes("TC-only cases")) {
    return "This is a Technical Component (TC) only case and does not require pathologist review.";
  }

  // HTTP status code errors
  if (message.includes("status code 422") || message.includes("422")) {
    return "Please complete all required fields before signing out.";
  }
  if (message.includes("status code 401") || message.includes("401")) {
    return "Authentication required. Please enter your password.";
  }

  // Network/Server errors
  if (message.includes("Network Error") || message.includes("timeout")) {
    return "Unable to connect to the server. Please check your internet connection and try again.";
  }
  if (message.includes("500") || message.includes("Internal Server Error")) {
    return "Something went wrong on our end. Please try again or contact support if the issue persists.";
  }
  if (
    message.includes("404") ||
    message.includes("not found") ||
    message.includes("Not Found")
  ) {
    return "The requested resource was not found. Please refresh and try again.";
  }

  // Generic fallback - avoid showing technical details
  if (
    message.length > 100 ||
    message.includes("Error:") ||
    message.includes("Exception")
  ) {
    return "An unexpected error occurred. Please try again or contact support.";
  }

  // If the message seems user-friendly already, return it
  return message || "An error occurred. Please try again.";
};

export const getStalePathologistAssignment = (
  error: unknown
): AssignmentState | null => {
  const assignmentError = getCasePathologistAssignmentError(error);
  return assignmentError?.code === "PATHOLOGIST_ASSIGNMENT_STALE"
    ? assignmentError.currentAssignment
    : null;
};

// Backend sign-off issues arrive as flat strings ("Specimen A: Diagnosis text
// is required"). Pull the specimen label out so the validation modal groups
// them under their specimen instead of the "Case-Level Fields" bucket.
const parseBackendSignOffIssue = (
  message: string
): { specimenNumber: string; message: string } => {
  const match = message.match(/^Specimen ([A-Z]+):\s*(.+)$/);
  if (match) {
    return { specimenNumber: match[1], message: match[2] };
  }
  return { specimenNumber: "", message };
};

export default function PathologistReviewDetailPage() {
  const { caseId } = useParams<{ caseId: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const queueReturnTo = useMemo(
    () => getPathologistQueueReturnTo(location),
    [location]
  );
  const handleReturnToQueue = useCallback(
    () => navigate(queueReturnTo),
    [navigate, queueReturnTo]
  );
  const [searchParams, setSearchParams] = useSearchParams();
  const { user } = useAuth();
  const userId = user?.id;
  const { data: reportReviewStatus } = useQuery({
    queryKey: ["report-review-status", caseId],
    queryFn: () => signOffService.getReportReviewStatus(caseId!),
    enabled: Boolean(caseId),
  });

  // State
  const [expandedSpecimens, setExpandedSpecimens] = useState<Set<string>>(
    new Set()
  );

  // Left nav bar state - synced to URL query param "tab"
  const VALID_TABS = [
    "grid",
    "report",
    "requisition",
    "grossing",
    "stains",
    "discussions",
    "external-opinion",
    "documents",
    "notes",
    "comments",
    "errors",
    "corrections",
  ] as const;
  const tabFromUrl = searchParams.get("tab");
  const initialTab =
    tabFromUrl && VALID_TABS.includes(tabFromUrl as (typeof VALID_TABS)[number])
      ? tabFromUrl
      : null;
  const [activeNavItem, setActiveNavItemState] = useState<string>(
    initialTab ?? "grid"
  );
  const [liveRequisitionData, setLiveRequisitionData] =
    useState<LiveRequisitionData | null>(null);
  const setActiveNavItem = useCallback(
    (tab: string) => {
      setActiveNavItemState(tab);
      if (tab !== "requisition") {
        setLiveRequisitionData(null);
      }
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          next.set("tab", tab);
          return next;
        },
        { replace: true }
      );
    },
    [setSearchParams]
  );
  const [reviewExpanded, setReviewExpanded] = useState(true);
  const initialNavSet = useRef(!!initialTab);

  // Case images state
  const [imageUploadModalOpened, setImageUploadModalOpened] = useState(false);
  const [cameraCaptureModalOpened, setCameraCaptureModalOpened] =
    useState(false);
  const [caseImages, setCaseImages] = useState<CaseImage[]>([]);
  const [caseImagesLoading, setCaseImagesLoading] = useState(false);
  const [imageDownloadUrls, setImageDownloadUrls] = useState<
    Record<string, string>
  >({});
  const persistImageCaption = useCallback(
    async (imageId: string, caption: string | undefined) => {
      if (!caseId) return;
      await caseImagesService.updateImage(caseId, imageId, { caption });
    },
    [caseId]
  );
  const captionUpdateQueue = useMemo(
    () => new CaseImageCaptionUpdateQueue(persistImageCaption),
    [persistImageCaption]
  );
  const handleImageCaptionChange = useCallback(
    (imageId: string, caption: string) => {
      setCaseImages((images) =>
        images.map((image) =>
          image.id === imageId ? { ...image, caption } : image
        )
      );
      captionUpdateQueue.enqueue(imageId, caption);
    },
    [captionUpdateQueue]
  );

  const loadCaseImages = useCallback(async () => {
    if (!caseId) return;
    try {
      setCaseImagesLoading(true);
      const data = await caseImagesService.getImages(caseId);
      setCaseImages(data);
    } catch {
      // Silently fail - images are not critical
    } finally {
      setCaseImagesLoading(false);
    }
  }, [caseId]);

  const reportImageQueryClient = useQueryClient();
  const handleReportImagesChanged = useCallback(async () => {
    await loadCaseImages();
    if (!caseId) return;
    await Promise.all([
      reportImageQueryClient.invalidateQueries({
        queryKey: ["case-images-report", caseId],
      }),
      reportImageQueryClient.invalidateQueries({
        queryKey: ["case", caseId, "addendum-draft"],
      }),
    ]);
  }, [caseId, loadCaseImages, reportImageQueryClient]);

  // Fetch download URLs for side panel thumbnails
  const loadImageDownloadUrls = useCallback(async (imgs: CaseImage[]) => {
    const urls: Record<string, string> = {};
    await Promise.all(
      imgs.map(async (img) => {
        try {
          const result = await caseImagesService.getDownloadUrl(
            img.imageUrl,
            img.fileName
          );
          urls[img.id] = result.url;
        } catch {
          // Skip failed URLs
        }
      })
    );
    setImageDownloadUrls(urls);
  }, []);

  useEffect(() => {
    loadCaseImages();
  }, [loadCaseImages]);

  useEffect(() => {
    if (caseImages.length > 0) {
      loadImageDownloadUrls(caseImages);
    }
  }, [caseImages, loadImageDownloadUrls]);

  // ===== Case Documents state =====
  const [uploadedDocFiles, setUploadedDocFiles] = useState<UploadedFile[]>([]);
  const [savedCaseFiles, setSavedCaseFiles] = useState<CaseFile[]>([]);
  const documentCaseFiles = useMemo(
    () => savedCaseFiles.filter((file) => !isImageMimeType(file.mimeType)),
    [savedCaseFiles]
  );
  const imageCaseFiles = useMemo(
    () => savedCaseFiles.filter((file) => isImageMimeType(file.mimeType)),
    [savedCaseFiles]
  );
  const [caseFilesLoading, setCaseFilesLoading] = useState(false);
  const [docDownloadUrls, setDocDownloadUrls] = useState<
    Record<string, string>
  >({});
  const persistFileCaption = useCallback(
    async (fileId: string, caption: string | undefined) => {
      if (!caseId) return;
      await casesService.updateFile(caseId, fileId, { label: caption || null });
    },
    [caseId]
  );
  const fileCaptionUpdateQueue = useMemo(
    () => new CaseImageCaptionUpdateQueue(persistFileCaption),
    [persistFileCaption]
  );
  const handleFileCaptionChange = useCallback(
    (fileId: string, caption: string) => {
      setSavedCaseFiles((files) =>
        files.map((file) =>
          file.id === fileId ? { ...file, label: caption } : file
        )
      );
      fileCaptionUpdateQueue.enqueue(fileId, caption);
    },
    [fileCaptionUpdateQueue]
  );

  const loadCaseFiles = useCallback(async () => {
    if (!caseId) return;
    try {
      setCaseFilesLoading(true);
      const data = await casesService.getFiles(caseId);
      setSavedCaseFiles(data);
    } catch {
      // Silently fail
    } finally {
      setCaseFilesLoading(false);
    }
  }, [caseId]);

  // Fetch presigned download URLs for saved documents
  const loadDocDownloadUrls = useCallback(async (files: CaseFile[]) => {
    const urls: Record<string, string> = {};
    await Promise.all(
      files.map(async (file) => {
        try {
          const result = await caseImagesService.getDownloadUrl(
            file.url,
            file.name
          );
          urls[file.id] = result.url;
        } catch {
          // Skip failed URLs
        }
      })
    );
    setDocDownloadUrls(urls);
  }, []);

  useEffect(() => {
    loadCaseFiles();
  }, [loadCaseFiles]);

  useEffect(() => {
    if (savedCaseFiles.length > 0) {
      loadDocDownloadUrls(savedCaseFiles);
    }
  }, [savedCaseFiles, loadDocDownloadUrls]);

  const reportImages = caseImages.filter((img) => img.includeInReport);
  const reportFiles = savedCaseFiles.filter((f) => f.includeInReport);
  const hasReportAttachments =
    reportImages.length > 0 || reportFiles.length > 0;
  const nonReportImages = caseImages.filter((img) => !img.includeInReport);
  const nonReportFiles = savedCaseFiles.filter((f) => !f.includeInReport);
  const hasNonReportAttachments =
    nonReportImages.length > 0 || nonReportFiles.length > 0;
  const [addAttachmentOpen, setAddAttachmentOpen] = useState(false);

  const handleToggleImageInReport = useCallback(
    async (imageId: string, include: boolean) => {
      if (!caseId) return;
      try {
        await captionUpdateQueue.flush(imageId);
        await caseImagesService.updateImage(caseId, imageId, {
          includeInReport: include,
        });
        await loadCaseImages();
      } catch {
        notifications.show({
          title: "Error",
          message: "Failed to update image",
          color: "red",
        });
      }
    },
    [captionUpdateQueue, caseId, loadCaseImages]
  );

  const handleToggleFileInReport = useCallback(
    async (fileId: string) => {
      if (!caseId) return;
      try {
        await fileCaptionUpdateQueue.flush(fileId);
        await casesService.toggleFileIncludeInReport(caseId, fileId);
        await loadCaseFiles();
      } catch {
        notifications.show({
          title: "Error",
          message: "Failed to update file",
          color: "red",
        });
      }
    },
    [caseId, fileCaptionUpdateQueue, loadCaseFiles]
  );

  // Auto-save documents when files are uploaded
  const handleDocFilesChange = useCallback(
    async (files: UploadedFile[]) => {
      setUploadedDocFiles(files);

      // Find newly uploaded files (have s3Key and file object)
      const newDocs = files.filter(
        (f): f is UploadedFile & { file: File } => !!f.s3Key && !!f.file
      );
      if (newDocs.length > 0 && caseId) {
        try {
          await casesService.saveDocuments(
            caseId,
            newDocs.map((f) => ({
              s3Key: f.s3Key,
              fileName: f.file.name,
              mimeType: f.file.type,
              size: f.file.size,
            })),
            "pathology_review"
          );
          loadCaseFiles();
          setUploadedDocFiles([]);
        } catch {
          notifications.show({
            title: "Warning",
            message: "Files uploaded but failed to save to case. Try again.",
            color: "yellow",
          });
        }
      }
    },
    [caseId, loadCaseFiles]
  );

  const [deleteDocFileId, setDeleteDocFileId] = useState<string | null>(null);

  const handleConfirmDeleteCaseFile = useCallback(async () => {
    if (!caseId || !deleteDocFileId) return;
    try {
      await casesService.deleteFile(caseId, deleteDocFileId);
      setSavedCaseFiles((prev) => prev.filter((f) => f.id !== deleteDocFileId));
      setDeleteDocFileId(null);
      notifications.show({
        title: "Deleted",
        message: "Document removed",
        color: "green",
      });
    } catch {
      notifications.show({
        title: "Error",
        message: "Failed to delete document",
        color: "red",
      });
    }
  }, [caseId, deleteDocFileId]);

  const handleDownloadCaseFile = useCallback(async (file: CaseFile) => {
    try {
      // Always fetch a fresh presigned URL to avoid expired URL errors
      const result = await caseImagesService.getDownloadUrl(
        file.url,
        file.name
      );
      await downloadFileFromUrl(result.url, file.name);
    } catch {
      notifications.show({
        title: "Error",
        message: "Failed to download document",
        color: "red",
      });
    }
  }, []);

  const handlePreviewCaseFile = useCallback(async (file: CaseFile) => {
    const childWindow = window.open(
      "",
      file.name,
      "width=600,height=500,resizable=yes,scrollbars=yes"
    );
    try {
      const result = await caseImagesService.getDownloadUrl(
        file.url,
        file.name
      );
      if (childWindow) {
        childWindow.location.href = result.url;
      }
    } catch {
      if (childWindow) childWindow.close();
      notifications.show({
        title: "Error",
        message: "Failed to open document preview",
        color: "red",
      });
    }
  }, []);

  const handleDownloadCaseImage = useCallback(async (img: CaseImage) => {
    try {
      if (!img.fileName) throw new Error("The image name is unavailable.");
      const result = await caseImagesService.getDownloadUrl(
        img.imageUrl,
        img.fileName
      );
      await downloadFileFromUrl(result.url, img.fileName);
    } catch {
      notifications.show({
        title: "Error",
        message: "Failed to download image",
        color: "red",
      });
    }
  }, []);

  // Image editing state (modal-based)
  const [editImageModalOpened, setEditImageModalOpened] = useState(false);
  const [editingImageData, setEditingImageData] = useState<CaseImage | null>(
    null
  );
  const [editImageCaption, setEditImageCaption] = useState("");
  const [editImageDescription, setEditImageDescription] = useState("");
  const [editImageMagnification, setEditImageMagnification] = useState("");
  const [editImageIncludeInReport, setEditImageIncludeInReport] =
    useState(true);
  const [deleteImageId, setDeleteImageId] = useState<string | null>(null);
  const [replaceFile, setReplaceFile] = useState<File | null>(null);
  const [replacePreview, setReplacePreview] = useState<string | null>(null);
  const [replacingImage, setReplacingImage] = useState(false);
  const [replaceProgress, setReplaceProgress] = useState(0);

  const handleOpenImageEdit = (img: CaseImage) => {
    setEditingImageData(img);
    setEditImageCaption(img.caption || "");
    setEditImageDescription(img.description || "");
    setEditImageMagnification(img.magnification || "");
    setEditImageIncludeInReport(img.includeInReport);
    setEditImageModalOpened(true);
  };

  const handleCloseImageEdit = () => {
    setEditImageModalOpened(false);
    setEditingImageData(null);
    setEditImageCaption("");
    setEditImageDescription("");
    setEditImageMagnification("");
    setEditImageIncludeInReport(true);
    setReplaceFile(null);
    setReplacePreview(null);
    setReplacingImage(false);
    setReplaceProgress(0);
  };

  const handleReplaceFileSelect = (file: File | null) => {
    if (!file) return;
    const acceptedTypes = [
      "image/jpeg",
      "image/png",
      "image/tiff",
      "image/jpg",
    ];
    if (!acceptedTypes.includes(file.type)) {
      notifications.show({
        title: "Invalid file type",
        message: "Please upload a JPG, PNG, or TIFF image",
        color: "red",
      });
      return;
    }
    if (file.size > 50 * 1024 * 1024) {
      notifications.show({
        title: "File too large",
        message: "Image must be less than 50 MB",
        color: "red",
      });
      return;
    }
    setReplaceFile(file);
    const reader = new FileReader();
    reader.onloadend = () => setReplacePreview(reader.result as string);
    reader.readAsDataURL(file);
  };

  const handleSaveImageEdit = async () => {
    if (!caseId || !editingImageData) return;
    try {
      setReplacingImage(!!replaceFile);
      setReplaceProgress(10);

      let newImageUrl: string | undefined;
      let newFileSize: number | undefined;
      let newMimeType: string | undefined;

      if (replaceFile) {
        setReplaceProgress(20);
        const { url, key } = await caseImagesService.getPresignedUrl(
          replaceFile.name,
          replaceFile.type
        );
        setReplaceProgress(50);
        await caseImagesService.uploadToS3(url, replaceFile);
        setReplaceProgress(70);
        newImageUrl = key;
        newFileSize = replaceFile.size;
        newMimeType = replaceFile.type;
      }

      await caseImagesService.updateImage(caseId, editingImageData.id, {
        caption: editImageCaption.trim() || undefined,
        description: editImageDescription.trim() || undefined,
        magnification: editImageMagnification.trim() || undefined,
        includeInReport: editImageIncludeInReport,
        ...(newImageUrl && {
          imageUrl: newImageUrl,
          fileSize: newFileSize,
          mimeType: newMimeType,
        }),
      });
      setReplaceProgress(100);
      notifications.show({
        title: "Success",
        message: replaceFile ? "Image replaced and updated" : "Image updated",
        color: "green",
      });
      handleCloseImageEdit();
      loadCaseImages();
    } catch {
      notifications.show({
        title: "Error",
        message: "Failed to update image",
        color: "red",
      });
    } finally {
      setReplacingImage(false);
      setReplaceProgress(0);
    }
  };

  const handleDeleteImage = async () => {
    if (!caseId || !deleteImageId) return;
    try {
      await caseImagesService.deleteImage(caseId, deleteImageId);
      notifications.show({
        title: "Success",
        message: "Image deleted",
        color: "green",
      });
      setDeleteImageId(null);
      loadCaseImages();
    } catch {
      notifications.show({
        title: "Error",
        message: "Failed to delete image",
        color: "red",
      });
    }
  };

  // Track if we've already started review to prevent infinite loops
  const hasStartedReview = useRef(false);
  // Track if we've initialized expanded specimens (to prevent reset on refetch)
  const hasInitializedExpanded = useRef(false);
  // Ref to imperative handle for RequisitionCenter
  const requisitionRef = useRef<RequisitionCenterHandle>(null);

  // Reset flags when navigating to a different case
  useEffect(() => {
    hasStartedReview.current = false;
    hasInitializedExpanded.current = false;
  }, [caseId]);

  // Queries
  const {
    data: signedCaseData,
    isLoading: caseLoading,
    error: caseError,
    refetch: refetchCase,
  } = useCaseForPathologistReview(caseId);
  const amendmentEditing = useAmendmentDiagnosticDraft(
    caseId || "",
    Boolean(signedCaseData?.isFinalized)
  );
  const amendmentDraft = amendmentEditing.draft;
  const { savePatch: saveAmendmentPatch, create: createAmendmentDraft } =
    amendmentEditing;
  const [amendmentDiagnosticsPaused, setAmendmentDiagnosticsPaused] =
    useState(false);
  const caseData = useMemo(
    () => overlayAmendmentCase(signedCaseData, amendmentDraft),
    [signedCaseData, amendmentDraft]
  );

  const openAmendmentEditor = useCallback(async () => {
    try {
      await createAmendmentDraft();
    } catch {
      notifications.show({
        title: "Amendment unavailable",
        message: "The amendment draft could not be opened. Please try again.",
        color: "red",
      });
    }
  }, [createAmendmentDraft]);

  const legacyReportProjection = useLegacyImportedCase(
    caseId || "",
    caseData?.isLegacyImported === true
  );
  const canAmendReports = useHasPermission("sign_off.amend");
  const addendumEligibilityQuery = useAddendumEligibility(
    caseId || "",
    canAmendReports
  );
  const { refetch: refetchAddendumEligibility } = addendumEligibilityQuery;
  const canCreateFromExternalBaseline = useMemo(
    () => addendumEligibilityQuery.data?.canCreateFromExternalBaseline === true,
    [addendumEligibilityQuery.data?.canCreateFromExternalBaseline]
  );
  const preSignOffCastleReportFile = useMemo(() => {
    const eligibility = addendumEligibilityQuery.data;
    if (
      caseData?.isFinalized ||
      eligibility?.canCreateFromExternalBaseline ||
      !eligibility?.baselineId ||
      !eligibility.downloadUrl
    ) {
      return undefined;
    }
    return {
      file: {
        id: eligibility.baselineId,
        name: "Castle Biosciences Report.pdf",
        mimeType: "application/pdf",
        label: "Castle Biosciences Report",
      },
      url: eligibility.downloadUrl,
    };
  }, [addendumEligibilityQuery.data, caseData?.isFinalized]);
  const canCreateAddendum = useMemo(
    () => Boolean(caseData?.isFinalized || canCreateFromExternalBaseline),
    [canCreateFromExternalBaseline, caseData?.isFinalized]
  );
  const isAddendumEligibilityUnavailable =
    canAmendReports &&
    addendumEligibilityQuery.isError &&
    !caseData?.isFinalized;
  const handleRetryAddendumEligibility = useCallback(() => {
    void refetchAddendumEligibility();
  }, [refetchAddendumEligibility]);
  const addendumDraftQuery = useAddendumDraft(
    caseId || "",
    Boolean(caseId && canCreateAddendum)
  );
  const [stalePathologistConflict, setStalePathologistConflict] = useState<{
    caseId?: string;
    assignment: AssignmentState;
  }>();
  const stalePathologistAssignment =
    stalePathologistConflict && stalePathologistConflict.caseId === caseId
      ? stalePathologistConflict.assignment
      : undefined;

  const handleStalePathologistAssignment = useCallback(
    (error: unknown): boolean => {
      const currentAssignment = getStalePathologistAssignment(error);
      if (!currentAssignment) return false;

      setStalePathologistConflict({ caseId, assignment: currentAssignment });
      void refetchCase();
      return true;
    },
    [caseId, refetchCase]
  );

  const requestStainSpecimens = useMemo(
    () =>
      (caseData?.specimens || []).map((specimen: any) => ({
        id: specimen.id,
        number: specimen.number,
        source: specimen.source,
        blocks: specimen.blocks,
      })),
    [caseData?.specimens]
  );

  // Case is read-only if blocked OR finalized (signed off)
  const isReadOnly =
    caseData?.isBlocked ||
    caseData?.isFinalized ||
    canCreateFromExternalBaseline ||
    isAddendumEligibilityUnavailable;
  const isDiagnosticReadOnly = useMemo(
    () =>
      Boolean(
        amendmentDiagnosticsPaused ||
          caseData?.isBlocked ||
          (!amendmentDraft && isReadOnly)
      ),
    [
      amendmentDiagnosticsPaused,
      amendmentDraft,
      caseData?.isBlocked,
      isReadOnly,
    ]
  );
  const { hasSignedReportForAddendumSection, canAddPhotos } = useMemo(
    () =>
      getReportPhotoEligibility({
        isLegacyImported: caseData?.isLegacyImported,
        isFinalized: caseData?.isFinalized,
        signedOffAt: caseData?.signedOffAt,
        isBlocked: caseData?.isBlocked,
        isReadOnly,
        canCreateFromExternalBaseline,
        isAddendumEligibilityUnavailable,
        legacyProjection: legacyReportProjection.data,
      }),
    [
      caseData?.isLegacyImported,
      caseData?.isFinalized,
      caseData?.signedOffAt,
      caseData?.isBlocked,
      isReadOnly,
      canCreateFromExternalBaseline,
      isAddendumEligibilityUnavailable,
      legacyReportProjection.data,
    ]
  );

  // Adding / removing specimens is permission-gated, independent of the
  // diagnosis read-only state — pathologists may amend specimens after sign-off.
  const canModifyStructure = useHasPermission("pathologist_review.write");
  // Removing a specimen is narrower still — administrators and medical
  // directors only. Withholding the callback hides the row's remove control.
  const canDeleteSpecimen = useHasPermission(
    "pathologist_review.write",
    "pathologist_review.specimen_delete"
  );
  const canAddSpecimen = false;
  const archivedSpecimens = caseData?.archivedSpecimens ?? [];
  const archiveSpecimenMutation = useArchiveSpecimen();
  const addSpecimenMutation = useAddSpecimen();
  const recoverSpecimenMutation = useRecoverSpecimen();
  const [specimenToRemove, setSpecimenToRemove] = useState<{
    id: string;
    number: string;
  } | null>(null);
  const [
    addSpecimenOpened,
    { open: openAddSpecimen, close: closeAddSpecimen },
  ] = useDisclosure(false);
  const [
    recoverSpecimensOpened,
    { open: openRecoverSpecimens, close: closeRecoverSpecimens },
  ] = useDisclosure(false);

  const handleRequestRemoveSpecimen = useCallback(
    (specimenId: string, specimenNumber: string) => {
      setSpecimenToRemove({ id: specimenId, number: specimenNumber });
    },
    []
  );

  const handleCancelRemoveSpecimen = useCallback(() => {
    setSpecimenToRemove(null);
  }, []);

  const handleConfirmRemoveSpecimen = useCallback(() => {
    if (!caseId || !specimenToRemove) return;
    archiveSpecimenMutation.mutate(
      { caseId, specimenId: specimenToRemove.id },
      {
        onSuccess: () => {
          notifications.show({
            color: "green",
            title: "Specimen removed",
            message: `Specimen ${specimenToRemove.number} was removed from the case.`,
          });
          setSpecimenToRemove(null);
        },
        onError: () => {
          notifications.show({
            color: "red",
            title: "Couldn't remove specimen",
            message:
              "Something went wrong removing the specimen. Please try again.",
          });
        },
      }
    );
  }, [archiveSpecimenMutation, caseId, specimenToRemove]);

  // Send Report
  const {
    handleClick: handleSendReportClick,
    isSending: isSendingReport,
    modalOpened: sendReportModalOpened,
    closeModal: closeSendReportModal,
  } = useSendReport(caseId);

  const {
    state: reportRecallState,
    remainingSeconds: reportRecallRemainingSeconds,
    countdownPhase: reportRecallCountdownPhase,
    opened: reportRecallModalOpened,
    result: reportRecallResult,
    error: reportRecallError,
    stateError: reportRecallStateError,
    refetch: refetchReportRecallState,
    isLoading: isReportRecallLoading,
    isSubmitting: isReportRecallSubmitting,
    isReverting: isReportReverting,
    isReconciling: isReportRecallReconciling,
    openModal: openReportRecallModal,
    closeModal: closeReportRecallModal,
    submitRecall: submitReportRecall,
    checkNow: checkReportRecallNow,
    revertSignoff: revertReportSignoff,
  } = useReportDeliveryRecall(
    caseId,
    !!caseData?.isFinalized && !caseData?.isBlocked
  );

  const hasPostRecallServerState = useMemo(
    () =>
      !!reportRecallState &&
      !!reportRecallResult &&
      Date.parse(reportRecallState.serverTime) >=
        Date.parse(reportRecallResult.serverTime),
    [reportRecallResult, reportRecallState]
  );

  const displayedReportRecallState = useMemo(() => {
    if (!reportRecallState || !reportRecallResult || hasPostRecallServerState) {
      return reportRecallState;
    }

    if (
      reportRecallResult.outcome === "recalled" ||
      reportRecallResult.outcome === "already_recalled"
    ) {
      return {
        ...reportRecallState,
        aggregateState: "recalled" as const,
        canRecall: false,
      };
    }
    if (reportRecallResult.outcome === "partial") {
      return {
        ...reportRecallState,
        aggregateState: "partial_recall" as const,
        canRecall: false,
      };
    }
    return {
      ...reportRecallState,
      aggregateState: "mixed" as const,
      canRecall: false,
    };
  }, [hasPostRecallServerState, reportRecallResult, reportRecallState]);

  const canRecallReport = useMemo(
    () =>
      reportRecallState?.canRecall === true &&
      (!reportRecallResult || hasPostRecallServerState),
    [hasPostRecallServerState, reportRecallResult, reportRecallState?.canRecall]
  );
  const hasInProgressReportDelivery = useMemo(
    () =>
      displayedReportRecallState?.aggregateState === "queued" ||
      displayedReportRecallState?.aggregateState === "sending" ||
      displayedReportRecallState?.deliveries.some(
        (delivery) =>
          delivery.status === "pending" || delivery.status === "sending"
      ) === true,
    [
      displayedReportRecallState?.aggregateState,
      displayedReportRecallState?.deliveries,
    ]
  );
  const headerReportDeliveryState = useMemo(() => {
    const state = displayedReportRecallState?.aggregateState;
    return state === "recalled" || state === "delivered" ? state : null;
  }, [displayedReportRecallState?.aggregateState]);
  const isCurrentReportFullyRecalled = useMemo(
    () => headerReportDeliveryState === "recalled",
    [headerReportDeliveryState]
  );
  const showReportDeliveryStateBadge = useMemo(
    () =>
      !!displayedReportRecallState &&
      !canRecallReport &&
      displayedReportRecallState.aggregateState !== "none" &&
      displayedReportRecallState.aggregateState !== "queued" &&
      !headerReportDeliveryState,
    [canRecallReport, displayedReportRecallState, headerReportDeliveryState]
  );
  const hasReportDeliveryHistory = useMemo(
    () => !!displayedReportRecallState?.recallHistory.length,
    [displayedReportRecallState?.recallHistory.length]
  );
  const isReportRecallChecking = useMemo(
    () =>
      isReportRecallLoading ||
      reportRecallCountdownPhase === "checking" ||
      reportRecallCountdownPhase === "due_now",
    [isReportRecallLoading, reportRecallCountdownPhase]
  );
  const isReportRecallOutcomeUnknown = useMemo(
    () => reportRecallError?.kind === "unknown",
    [reportRecallError?.kind]
  );
  const handleRefreshReportRecallState = useCallback(() => {
    void refetchReportRecallState();
  }, [refetchReportRecallState]);
  const handleRevertSignoff = useCallback(() => {
    void revertReportSignoff();
  }, [revertReportSignoff]);
  const [
    reportDeliveryHistoryOpened,
    { open: openReportDeliveryHistory, close: closeReportDeliveryHistory },
  ] = useDisclosure(false);

  // Lock query
  const { data: lockData } = useCaseLock(caseId);

  // The API applies the current user's queue access scope before returning cases.
  const { data: queueCases = [] } = usePathologistReviewQueue();

  // Pending additional stains
  const { data: pendingStains = [] } = usePendingStains(caseId);

  // Pending correction requests (for nav badge)
  const { data: pendingCorrectionsData } = useCorrectionRequests(
    caseId,
    "pending"
  );
  const pendingCorrectionsCount =
    pendingCorrectionsData?.correctionRequests?.length ?? 0;

  // Pending errors count (for nav badge)
  const { data: caseErrorsForBadge = [] } = useQuery({
    queryKey: ["caseErrors", caseId],
    queryFn: () =>
      api.get<Array<{ id: string; status: string }>>(
        `/workflows/errors/case/${caseId}`
      ),
    enabled: !!caseId,
  });
  const pendingErrorsCount = caseErrorsForBadge.filter(
    (e) => e.status === "pending"
  ).length;

  // Active send-outs for external opinion button state
  const { activeSendOuts, hasActiveSendOuts } = useActiveSendOuts(caseId);

  // Prior cases for same patient (matched by MRN or Name+DOB on the server)
  const { data: priorCasesData, isLoading: priorCasesLoading } =
    usePriorCases(caseId);
  const priorCases = useMemo(
    () => priorCasesData?.data ?? [],
    [priorCasesData?.data]
  );
  const finalizedPriorCases = useMemo(
    () => priorCases.filter((pc) => pc.isFinalized),
    [priorCases]
  );
  const currentOtherCases = useMemo(
    () => priorCases.filter((pc) => !pc.isFinalized),
    [priorCases]
  );

  // Internal discussions (for flag modal and sign-out disable logic)
  const { data: caseDiscussions = [] } = useCaseDiscussions(caseId || "");
  const activeDiscussion = caseDiscussions.find(
    (d) => d.status === DiscussionStatus.PENDING
  );
  const hasActiveDiscussion = !!activeDiscussion;
  const pendingDiscussionsCount = caseDiscussions.filter(
    (d) => d.status === DiscussionStatus.PENDING
  ).length;
  const resolvedDiscussionsCount = caseDiscussions.filter(
    (d) => d.status === DiscussionStatus.RESOLVED
  ).length;

  // Mutations
  const startReviewMutation = useStartPathologistReview();
  const releaseLockMutation = useReleaseLock();
  const extendLockMutation = useExtendLock();
  const saveDraftMutation = useSaveDiagnosisDraft();
  const mutateDraftAsync = saveDraftMutation.mutateAsync;
  const performDraftSave = useCallback(
    (payload: Parameters<typeof mutateDraftAsync>[0]) =>
      mutateDraftAsync(payload),
    [mutateDraftAsync]
  );
  const coordinateCanonicalDraftSave =
    useDraftSaveCoordinator(performDraftSave);
  const coordinateDraftSave = useCallback(
    (fingerprint: string, payload: Parameters<typeof mutateDraftAsync>[0]) =>
      amendmentDraft
        ? saveAmendmentPatch(amendmentDraft.id, {
            diagnoses: payload.diagnoses,
            grossing: payload.grossing,
            caseLevelData: payload.caseLevelData,
          })
        : coordinateCanonicalDraftSave(fingerprint, payload),
    [amendmentDraft, coordinateCanonicalDraftSave, saveAmendmentPatch]
  );

  const openCaseByBarcode = useOpenCaseByBarcode();

  const handleNavigateToCase = useCallback(
    (id: string) =>
      navigate(`/pathologist-review/${id}`, { state: location.state }),
    [location.state, navigate]
  );

  // Global barcode scanner — intercepts rapid keystrokes from a physical scanner
  useBarcodeScanner(
    useCallback(
      (barcode: string) => {
        notifications.show({
          title: "Barcode Scanned",
          message: `Opening case for barcode: ${barcode}`,
          color: "blue",
          autoClose: 3000,
        });
        openCaseByBarcode.mutate(
          { barcode, intent: "pathologist_review" },
          {
            onSuccess: (caseResult: any) => {
              if (caseResult?.id && caseResult.id !== caseId) {
                handleNavigateToCase(caseResult.id);
              }
            },
            onError: () => {
              notifications.show({
                title: "Barcode Error",
                message: `No case found for barcode: ${barcode}`,
                color: "red",
                autoClose: 4000,
              });
            },
          }
        );
      },
      [caseId, handleNavigateToCase, openCaseByBarcode]
    )
  );

  // Fetch diagnosis macros for auto-fill
  const { data: diagnosisMacros = [] } = useDiagnosisMacros();

  // Fetch diagnosis categories from API
  const { data: diagnosisCategories = [] } = useDiagnosisCategories();

  // Fetch gross codes for grossing center
  const { data: grossCodes = [] } = useGrossCodes();

  const { data: icdCodeReferences = [] } = useIcdCodes();
  const icdCodeLabelMap = useMemo(() => {
    const map = new Map<string, string>();
    icdCodeReferences.forEach((option) => {
      map.set(option.value, option.label);
      map.set(option.code, option.label);
    });
    return map;
  }, [icdCodeReferences]);
  const icdCodeOptions = useMemo(
    () =>
      icdCodeReferences.map((option) => ({
        value: option.code,
        label: option.label,
      })),
    [icdCodeReferences]
  );

  // Fetch subsites by source for the report tab variable chips
  const { data: sourcesWithSubsites } = useSourcesWithSubsites();
  const sourceOptions = useMemo(
    () => sourcesWithSubsites?.sources ?? [],
    [sourcesWithSubsites?.sources]
  );
  const subsiteOptionsBySource = useMemo(
    () => sourcesWithSubsites?.subsitesBySource ?? {},
    [sourcesWithSubsites?.subsitesBySource]
  );

  // Build category options for dropdown (use exact names from database)
  const categoryOptions = diagnosisCategories
    .filter((cat) => cat.isActive)
    .map((cat) => ({
      value: cat.name, // Use name as value (backend looks up by name)
      label: cat.name,
    }));

  // Diagnosis data state for all specimens
  const [diagnosisDataMap, setDiagnosisDataMap] = useState<
    Map<string, SpecimenDiagnosisData>
  >(new Map());

  // Case-level frozen interpretation (single rich-text field on the case)
  const [caseFrozenInterpretation, setCaseFrozenInterpretation] = useState("");
  const caseFrozenInitializedRef = useRef(false);

  // Fetch per-case pathologist comments for document view and preview
  const { data: canonicalPathologistComments = [] } = usePathologistComments(
    caseId ?? undefined
  );

  // Grossing data state for all specimens
  const [grossingDataMap, setGrossingDataMap] = useState<
    Map<string, SpecimenGrossingData>
  >(new Map());
  const pathologistComments = useMemo(
    () =>
      amendmentDraft?.diagnosticData?.pathologistComments ||
      canonicalPathologistComments,
    [amendmentDraft, canonicalPathologistComments]
  );
  const handleAddAmendmentComment = useCallback(
    (commentText: string) => {
      if (!amendmentDraft)
        return Promise.reject(new Error("Amendment is unavailable"));
      return saveAmendmentPatch(amendmentDraft.id, (data) => ({
        pathologistComments: [...data.pathologistComments, { commentText }],
      }));
    },
    [amendmentDraft, saveAmendmentPatch]
  );
  const handleDeleteAmendmentComment = useCallback(
    (id: string) => {
      if (!amendmentDraft)
        return Promise.reject(new Error("Amendment is unavailable"));
      return saveAmendmentPatch(amendmentDraft.id, (data) => ({
        pathologistComments: data.pathologistComments.filter(
          (comment) => comment.id !== id
        ),
      }));
    },
    [amendmentDraft, saveAmendmentPatch]
  );
  const amendmentCommentContext = useMemo(
    () =>
      amendmentDraft
        ? {
            comments: pathologistComments,
            addComment: handleAddAmendmentComment,
            deleteComment: handleDeleteAmendmentComment,
          }
        : undefined,
    [
      amendmentDraft,
      handleAddAmendmentComment,
      handleDeleteAmendmentComment,
      pathologistComments,
    ]
  );
  const grossingInitializedRef = useRef(false);
  const liveReportSpecimens = useMemo(
    () =>
      projectLiveGrossingSpecimens({
        specimens: caseData?.specimens ?? [],
        grossingDataMap,
        sources: sourceOptions,
        subsitesBySource: subsiteOptionsBySource,
      }).map((specimen: any) => ({
        id: specimen.id,
        number: specimen.number,
        sourceId: specimen.sourceId,
        sourceName: specimen.sourceName || "Unknown",
        collectionMethod: specimen.collectionMethod,
        findings: specimen.findings,
        material: specimen.material,
        organMapTemplateId:
          specimen.source?.organMapTemplateId ||
          specimen.source?.organMapTemplate?.id ||
          null,
        organMapTemplateName: specimen.source?.organMapTemplate?.name || null,
        organMapImageUrl: specimen.source?.organMapTemplate?.imageUrl || null,
        organMapWidth: specimen.source?.organMapTemplate?.width || null,
        organMapHeight: specimen.source?.organMapTemplate?.height || null,
        subsiteName: specimen.subsiteName,
        subsitePositionX: specimen.subsite?.positionX ?? null,
        subsitePositionY: specimen.subsite?.positionY ?? null,
        blockCount: specimen.blocks?.length ?? null,
      })),
    [
      caseData?.specimens,
      grossingDataMap,
      sourceOptions,
      subsiteOptionsBySource,
    ]
  );

  // Document view: clinical history and pathologist comment state (inline editing)
  const [documentClinicalHistory, setDocumentClinicalHistory] = useState("");
  const [documentIndications, setDocumentIndications] = useState("");
  const [documentImpressions, setDocumentImpressions] = useState("");
  const [documentPathologistComment, setDocumentPathologistComment] =
    useState("");
  const documentClinicalHistoryInitialized = useRef(false);
  const derivedIcdCodes = useMemo(() => {
    const sorted = [...(caseData?.specimens || [])].sort((a: any, b: any) =>
      (a.number || "").localeCompare(b.number || "")
    );
    return sorted
      .flatMap((s: any) => {
        const diagData = diagnosisDataMap.get(s.id);
        if (diagData?.icdCodes?.length) return diagData.icdCodes;
        return diagData?.icdCode ? [diagData.icdCode] : [];
      })
      .filter((c): c is string => !!c);
  }, [caseData?.specimens, diagnosisDataMap]);

  const derivedCptCodes = useMemo(() => {
    const sorted = [...(caseData?.specimens || [])].sort((a: any, b: any) =>
      (a.number || "").localeCompare(b.number || "")
    );
    return sorted
      .map((s: any) => {
        const diagnosisData = diagnosisDataMap.get(s.id);
        const backendCpt = s.diagnosis?.cptCode || null;
        const localCpt = diagnosisData?.cptCode ?? null;

        // If the local code matches the backend (synced/saved), prioritize the
        // backend-computed display code (which includes billing modifiers).
        // Otherwise, fall back to the local optimistic state.
        if (localCpt === backendCpt && localCpt !== null) {
          return s.diagnosis?.cptDisplayCode ?? backendCpt;
        }
        return diagnosisData?.cptDisplayCode ?? localCpt;
      })
      .filter((c): c is string => !!c);
  }, [caseData?.specimens, diagnosisDataMap]);
  const [selectedTemplateId, setSelectedTemplateId] = useState<string>("");
  const { data: activeTemplates = [] } = useActiveReportTemplates();
  useEffect(() => {
    if (selectedTemplateId || activeTemplates.length === 0) return;
    const defaultTpl = activeTemplates.find((t: any) => t.isDefault);
    setSelectedTemplateId(defaultTpl?.id || activeTemplates[0]?.id || "");
  }, [activeTemplates, selectedTemplateId]);

  // Auto-save state
  const [autoSaveStatus, setAutoSaveStatus] = useState<
    "idle" | "saving" | "saved" | "error"
  >("idle");

  const persistAmendmentFieldChange = useCallback(
    (
      patch: import("../../hooks/useAmendmentDiagnosticDraft").AmendmentDiagnosticPatch
    ) => {
      if (!amendmentDraft || isDiagnosticReadOnly) return;
      setAutoSaveStatus("saving");
      void saveAmendmentPatch(amendmentDraft.id, patch).then(
        () => setAutoSaveStatus("saved"),
        () => {
          setAutoSaveStatus("error");
          notifications.show({
            title: "Draft not saved",
            message:
              "Your amendment changes could not be saved. Keep this case open and try saving again.",
            color: "red",
          });
        }
      );
    },
    [amendmentDraft, isDiagnosticReadOnly, saveAmendmentPatch]
  );
  const handleClinicalHistoryChange = useCallback(
    (value: string) => {
      setDocumentClinicalHistory(value);
      persistAmendmentFieldChange({
        caseLevelData: { clinicalHistoryNotes: value },
      });
    },
    [persistAmendmentFieldChange]
  );
  const handleIndicationsChange = useCallback(
    (value: string) => {
      setDocumentIndications(value);
      persistAmendmentFieldChange({ caseLevelData: { indications: value } });
    },
    [persistAmendmentFieldChange]
  );
  const handleImpressionsChange = useCallback(
    (value: string) => {
      setDocumentImpressions(value);
      persistAmendmentFieldChange({ caseLevelData: { impressions: value } });
    },
    [persistAmendmentFieldChange]
  );
  const handleFrozenInterpretationChange = useCallback(
    (value: string) => {
      setCaseFrozenInterpretation(value);
      persistAmendmentFieldChange({
        caseLevelData: { frozenInterpretation: value },
      });
    },
    [persistAmendmentFieldChange]
  );
  const handleDocumentCommentChange = useCallback(
    (value: string) => {
      setDocumentPathologistComment(value);
      persistAmendmentFieldChange({
        pathologistComments: value ? [{ commentText: value }] : [],
      });
    },
    [persistAmendmentFieldChange]
  );

  const liveCaseData = useMemo(() => {
    if (!caseData || !liveRequisitionData) return caseData;
    const lr = liveRequisitionData;
    return {
      ...caseData,
      patient: {
        ...caseData.patient,
        firstName: lr.patient.firstName ?? caseData.patient?.firstName,
        lastName: lr.patient.lastName ?? caseData.patient?.lastName,
        dateOfBirth: lr.patient.dateOfBirth ?? caseData.patient?.dateOfBirth,
        sex: lr.patient.sex ?? caseData.patient?.sex,
        medicalRecordNumber:
          lr.patient.medicalRecordNumber ??
          caseData.patient?.medicalRecordNumber,
      },
      physician: lr.physicianName
        ? { ...caseData.physician, firstName: "", lastName: lr.physicianName }
        : caseData.physician,
      physicianId: lr.physicianId ?? caseData.physicianId,
      dateReceived: lr.dateReceived ?? caseData.dateReceived,
      dateCollected: lr.dateCollected ?? caseData.dateCollected,
      sendingPracticeId: lr.sendingPracticeId ?? caseData.sendingPracticeId,
      surgeryCenterLabId: lr.surgeryCenterLabId ?? caseData.surgeryCenterLabId,
      surgeryCenterLab: lr.surgeryCenterName
        ? { ...caseData.surgeryCenterLab, name: lr.surgeryCenterName }
        : caseData.surgeryCenterLab,
      clinicalHistoryNotes:
        lr.clinicalHistoryNotes ?? caseData.clinicalHistoryNotes,
      indications: lr.indications ?? caseData.indications,
      impressions: lr.impressions ?? caseData.impressions,
    };
  }, [caseData, liveRequisitionData]);
  const labToday = useLabTodayDateOnly();
  const livePatientDateOfBirth = useMemo(
    () => parseDateOnly(liveCaseData?.patient?.dateOfBirth),
    [liveCaseData?.patient?.dateOfBirth]
  );
  const livePatientAge = useMemo(
    () =>
      livePatientDateOfBirth
        ? calculateAgeFromDateOnly(livePatientDateOfBirth, labToday)
        : null,
    [labToday, livePatientDateOfBirth]
  );

  const isInitialLoadRef = useRef(true);
  const lastSavedDataRef = useRef<string>("");
  const diagnosisInitializedRef = useRef(false);

  // Debounce diagnosis data changes for auto-save (2 second delay)
  const [debouncedDiagnosisData] = useDebouncedValue(diagnosisDataMap, 2000);
  const [debouncedGrossingData] = useDebouncedValue(grossingDataMap, 2000);

  // Debounce document-only fields for auto-save
  const [debouncedClinicalHistory] = useDebouncedValue(
    documentClinicalHistory,
    2000
  );
  const [debouncedIndications] = useDebouncedValue(documentIndications, 2000);
  const [debouncedImpressions] = useDebouncedValue(documentImpressions, 2000);
  const [debouncedPathologistComment] = useDebouncedValue(
    documentPathologistComment,
    2000
  );

  // Auto-save effect - saves in background when data changes
  useEffect(() => {
    // Skip auto-save on initial load or if no case
    if (amendmentDraft || isInitialLoadRef.current || !caseId || !caseData) {
      return;
    }

    // Skip if case is read-only (blocked or finalized)
    if (isDiagnosticReadOnly || stalePathologistAssignment) {
      return;
    }

    // Skip if already saving
    if (saveDraftMutation.isPending) {
      return;
    }

    // Convert both maps to string for comparison
    const currentDataString = JSON.stringify({
      d: Array.from(debouncedDiagnosisData.entries()),
      g: Array.from(debouncedGrossingData.entries()),
    });

    // Skip if data hasn't actually changed
    if (currentDataString === lastSavedDataRef.current) {
      return;
    }

    // Skip if no data to save
    if (debouncedDiagnosisData.size === 0 && debouncedGrossingData.size === 0) {
      return;
    }

    // Perform auto-save
    const diagnoses = Array.from(debouncedDiagnosisData.values()).map((d) => ({
      specimenId: d.specimenId,
      macroCode: d.macroCode || null,
      diagnosisText: d.diagnosisText ?? "",
      microscopicDescription: d.microscopicDescription ?? "",
      location: d.location ?? "",
      category: d.category ?? "",
      cptCode: d.cptCode ?? "",
      icdCode: d.icdCode,
      icdCodes: d.icdCodes,
      color: d.color ?? "",
      sizeCm: d.sizeCm ?? "",
      sizeMm: d.sizeMm ?? "",
      consistency: d.consistency ?? "",
    }));

    const grossing = buildGrossingDraftEntries(debouncedGrossingData);

    // Update ref immediately to prevent duplicate saves
    lastSavedDataRef.current = currentDataString;
    setAutoSaveStatus("saving");

    const draftPayload = buildPathologistReviewDraftPayload({
      diagnoses,
      grossing,
      specimens: caseData.specimens,
      isPartiallyFinalized: Boolean(caseData.isPartiallyFinalized),
      frozenInterpretation: caseFrozenInterpretation,
    });
    const saveFingerprint = buildDraftFingerprint(
      caseId,
      debouncedDiagnosisData,
      debouncedGrossingData,
      caseFrozenInterpretation
    );
    void coordinateDraftSave(saveFingerprint, {
      caseId,
      ...draftPayload,
    }).then(
      () => {
        setAutoSaveStatus("saved");
        setTimeout(() => setAutoSaveStatus("idle"), 3000);
      },
      (error) => {
        handleStalePathologistAssignment(error);
        lastSavedDataRef.current = "";
        setAutoSaveStatus("error");
        setTimeout(() => setAutoSaveStatus("idle"), 5000);
      }
    );
  }, [
    amendmentDraft,
    debouncedDiagnosisData,
    debouncedGrossingData,
    caseData,
    caseFrozenInterpretation,
    caseId,
    isDiagnosticReadOnly,
    coordinateDraftSave,
    handleStalePathologistAssignment,
    saveDraftMutation.isPending,
    stalePathologistAssignment,
  ]);

  const diagnosticContextRef = useRef<string>();
  const diagnosticContextId = `${caseId}:${amendmentDraft?.id || "signed"}`;
  if (diagnosticContextRef.current !== diagnosticContextId) {
    diagnosticContextRef.current = diagnosticContextId;
    diagnosisInitializedRef.current = false;
    grossingInitializedRef.current = false;
    caseFrozenInitializedRef.current = false;
    documentClinicalHistoryInitialized.current = false;
    isInitialLoadRef.current = true;
    lastSavedDataRef.current = "";
  }

  // Initialize diagnosis data when case data loads
  // The LOCATION dropdown now uses the raw subsite name as its value,
  // so we can pre-populate directly from specimen.subsite.name.
  const mapSubsiteToLocation = (
    subsiteName: string | undefined
  ): string | null => {
    return subsiteName?.trim() || null;
  };

  useEffect(() => {
    if (diagnosisInitializedRef.current) return;
    if (caseData?.specimens) {
      diagnosisInitializedRef.current = true;
      const newMap = new Map<string, SpecimenDiagnosisData>();
      caseData.specimens.forEach((specimen: any) => {
        // Check if there's existing diagnosis data from backend
        const existingDiagnosis = specimen.diagnosis || {};

        // Backend now returns formatted diagnosis with proper fields
        // Fallback to legacy JSON parsing for backward compatibility
        let legacyFields: any = {};
        try {
          if (existingDiagnosis.comments) {
            legacyFields = JSON.parse(existingDiagnosis.comments);
          }
        } catch {
          // Ignore parse errors
        }

        // Pre-populate location from multiple sources (priority order):
        // 1. Saved location from diagnosis (new column)
        // 2. Legacy location from comments JSON
        // 3. Subsite from accessioning
        const savedLocation =
          existingDiagnosis.location || legacyFields.location || null;
        const subsiteLocation = mapSubsiteToLocation(specimen.subsite?.name);
        const location =
          amendmentDraft?.id ||
          legacyFields.amendmentDiagnosticSchemaVersion === 1
            ? existingDiagnosis.location ?? null
            : savedLocation || subsiteLocation;

        // The backend normalizes category names (e.g. "Benign" -> "benign",
        // "Low-grade Dysplasia" -> "low_grade_dysplasia"). The Select component
        // uses the raw DB name as its value, so we match back by comparing
        // the normalized form of each category option name.
        const rawCategory =
          existingDiagnosis.category || legacyFields.category || null;
        const denormalizedCategory = rawCategory
          ? diagnosisCategories.find(
              (c: any) =>
                c.name.toLowerCase().replace(/-/g, "_").replace(/\s+/g, "_") ===
                rawCategory
                  .toLowerCase()
                  .replace(/-/g, "_")
                  .replace(/\s+/g, "_")
            )?.name ?? rawCategory
          : null;

        // Use new fields from backend, falling back to legacy JSON for compatibility
        newMap.set(specimen.id, {
          specimenId: specimen.id,
          macroCode:
            existingDiagnosis.macroCode || legacyFields.macroCode || "",
          diagnosisText: existingDiagnosis.diagnosisText || "",
          microscopicDescription:
            existingDiagnosis.microscopicDescription || "",
          location,
          category: denormalizedCategory,
          cptCode: existingDiagnosis.cptCode || legacyFields.cptCode || null,
          cptDisplayCode:
            existingDiagnosis.cptDisplayCode ||
            existingDiagnosis.cptCode ||
            legacyFields.cptCode ||
            null,
          icdCode: existingDiagnosis.icdCode || legacyFields.icdCode || null,
          icdCodes: existingDiagnosis.icdCodes?.length
            ? existingDiagnosis.icdCodes
            : existingDiagnosis.icdCode || legacyFields.icdCode
            ? [existingDiagnosis.icdCode || legacyFields.icdCode]
            : [],
          color: existingDiagnosis.color || legacyFields.color || null,
          sizeCm: existingDiagnosis.sizeCm || legacyFields.sizeCm || null,
          sizeMm: existingDiagnosis.sizeMm || legacyFields.sizeMm || null,
          consistency:
            existingDiagnosis.consistency || legacyFields.consistency || null,
        });
      });
      setDiagnosisDataMap(newMap);

      // Store initial data state and mark initial load complete
      // Grossing data may not be initialized yet, so use empty array as placeholder
      lastSavedDataRef.current = JSON.stringify({
        d: Array.from(newMap.entries()),
        g: [],
      });
      // Use timeout to ensure debounced value is also set before enabling auto-save
      setTimeout(() => {
        isInitialLoadRef.current = false;
      }, 2500); // Slightly longer than debounce delay
    }
  }, [amendmentDraft?.id, caseData?.specimens, diagnosisCategories]);

  // Initialize case-level frozen interpretation from caseData
  useEffect(() => {
    if (caseFrozenInitializedRef.current) return;
    if (!caseData) return;
    caseFrozenInitializedRef.current = true;
    setCaseFrozenInterpretation(caseData.frozenInterpretation || "");
  }, [caseData]);

  // Update diagnosis data for a specimen
  const pendingImmediateSaveRef = useRef(false);

  const updateDiagnosisData = useCallback(
    (specimenId: string, data: Partial<SpecimenDiagnosisData>) => {
      const { cptDisplayCode, ...values } = data;
      persistAmendmentFieldChange({ diagnoses: [{ specimenId, ...values }] });
      setDiagnosisDataMap((prev) => {
        const newMap = new Map(prev);
        const existing = newMap.get(specimenId) || {
          specimenId,
          macroCode: "",
          diagnosisText: "",
          microscopicDescription: "",
          location: null,
          category: null,
          cptCode: null,
          cptDisplayCode: null,
          icdCode: null,
          icdCodes: [],
          color: null,
          sizeCm: null,
          sizeMm: null,
          consistency: null,
        };

        // Detect macro code change — triggers immediate save for auto-linked stains
        if (
          !amendmentDraft &&
          data.macroCode !== undefined &&
          data.macroCode !== existing.macroCode
        ) {
          pendingImmediateSaveRef.current = true;
        }

        const nextData = { ...existing, ...data };
        if (data.cptCode !== undefined && data.cptDisplayCode === undefined) {
          nextData.cptDisplayCode = data.cptCode;
        }
        newMap.set(specimenId, nextData);
        return newMap;
      });
    },
    [amendmentDraft, persistAmendmentFieldChange]
  );

  // When a macro code changes, fire an immediate save (bypassing debounce)
  // so auto-linked stains appear in the UI without the 2-second delay.
  useEffect(() => {
    if (
      amendmentDraft ||
      isInitialLoadRef.current ||
      !pendingImmediateSaveRef.current ||
      !caseId ||
      isDiagnosticReadOnly ||
      stalePathologistAssignment
    )
      return;

    // Small delay to let the state setter flush
    const timer = setTimeout(() => {
      pendingImmediateSaveRef.current = false;
      const diagnoses = Array.from(diagnosisDataMap.values()).map((d) => ({
        specimenId: d.specimenId,
        macroCode: d.macroCode || null,
        diagnosisText: d.diagnosisText ?? "",
        microscopicDescription: d.microscopicDescription ?? "",
        location: d.location ?? "",
        category: d.category ?? "",
        cptCode: d.cptCode ?? "",
        icdCode: d.icdCode,
        icdCodes: d.icdCodes,
        color: d.color ?? "",
        sizeCm: d.sizeCm ?? "",
        sizeMm: d.sizeMm ?? "",
        consistency: d.consistency ?? "",
      }));

      const grossing = buildGrossingDraftEntries(grossingDataMap);

      lastSavedDataRef.current = JSON.stringify({
        d: Array.from(debouncedDiagnosisData.entries()),
        g: Array.from(debouncedGrossingData.entries()),
      });
      setAutoSaveStatus("saving");

      const draftPayload = buildPathologistReviewDraftPayload({
        diagnoses,
        grossing,
        specimens: caseData?.specimens || [],
        isPartiallyFinalized: Boolean(caseData?.isPartiallyFinalized),
        frozenInterpretation: caseFrozenInterpretation,
      });
      const fingerprint = buildDraftFingerprint(
        caseId,
        diagnosisDataMap,
        grossingDataMap,
        caseFrozenInterpretation
      );
      void coordinateDraftSave(fingerprint, {
        caseId,
        ...draftPayload,
      }).then(
        () => {
          setAutoSaveStatus("saved");
          setTimeout(() => setAutoSaveStatus("idle"), 3000);
        },
        (error) => {
          handleStalePathologistAssignment(error);
          lastSavedDataRef.current = "";
          setAutoSaveStatus("error");
          setTimeout(() => setAutoSaveStatus("idle"), 5000);
        }
      );
    }, 50);

    return () => clearTimeout(timer);
  }, [
    amendmentDraft,
    caseData?.isPartiallyFinalized,
    caseData?.specimens,
    caseFrozenInterpretation,
    caseId,
    coordinateDraftSave,
    debouncedDiagnosisData,
    debouncedGrossingData,
    diagnosisDataMap,
    grossingDataMap,
    handleStalePathologistAssignment,
    isDiagnosticReadOnly,
    stalePathologistAssignment,
  ]);

  // Initialize grossing data when case + gross codes load
  useEffect(() => {
    if (grossingInitializedRef.current) return;
    if (!caseData?.specimens?.length || !grossCodes.length) return;
    grossingInitializedRef.current = true;
    const newMap = new Map<string, SpecimenGrossingData>();
    caseData.specimens.forEach((specimen: any) => {
      const rawGrossCodeId = amendmentDraft?.id
        ? specimen.internalGrossCodeId ?? ""
        : specimen.internalGrossCodeId ||
          specimen.accessioningGrossCodeId ||
          "";
      const matchedGrossCode = rawGrossCodeId
        ? grossCodes.find((gc: any) => gc.value === rawGrossCodeId)
        : undefined;
      const grossCodeId = matchedGrossCode ? rawGrossCodeId : "";
      const grossCodeLabel = matchedGrossCode?.label || "";
      newMap.set(specimen.id, {
        specimenId: specimen.id,
        grossCode: grossCodeLabel,
        grossCodeId,
        grossDescription: specimen.rawGrossDescription ?? specimen.gross ?? "",
        variableValues: specimen.variableValues,
        fragmentCount: specimen.fragmentCount ?? null,
        fragmentCountQualifier: specimen.fragmentCountQualifier ?? null,
        sourceId: specimen.sourceId ?? null,
        subsiteId: specimen.subsiteId ?? null,
        subsiteName: specimen.subsite?.name ?? null,
        findings: specimen.findings ?? null,
        collectionMethod: specimen.collectionMethod ?? null,
        material: specimen.material ?? null,
      });
    });
    setGrossingDataMap(newMap);
  }, [amendmentDraft?.id, caseData?.specimens, grossCodes]);

  // Initialize clinical history from caseData for document view inline editing
  useEffect(() => {
    if (documentClinicalHistoryInitialized.current) return;
    if (!caseData) return;
    documentClinicalHistoryInitialized.current = true;
    setDocumentClinicalHistory(caseData.clinicalHistoryNotes || "");
    setDocumentIndications(caseData.indications || "");
    setDocumentImpressions(caseData.impressions || "");
  }, [caseData]);

  // Sync requisition clinical history edits to the document view
  useEffect(() => {
    if (amendmentDraft) return;
    if (liveRequisitionData?.clinicalHistoryNotes !== undefined) {
      setDocumentClinicalHistory(liveRequisitionData.clinicalHistoryNotes);
    }
    if (liveRequisitionData?.indications !== undefined) {
      setDocumentIndications(liveRequisitionData.indications);
    }
    if (liveRequisitionData?.impressions !== undefined) {
      setDocumentImpressions(liveRequisitionData.impressions);
    }
  }, [
    amendmentDraft,
    liveRequisitionData?.clinicalHistoryNotes,
    liveRequisitionData?.indications,
    liveRequisitionData?.impressions,
  ]);

  // Auto-save document-only fields (clinical history, indications, impressions)
  useEffect(() => {
    if (amendmentDraft) return;
    if (!caseId || !caseData || isDiagnosticReadOnly) return;
    if (!documentClinicalHistoryInitialized.current) return;

    const updates: Record<string, string> = {};
    if (debouncedClinicalHistory !== (caseData.clinicalHistoryNotes || "")) {
      updates.clinicalHistoryNotes = debouncedClinicalHistory;
    }
    if (debouncedIndications !== (caseData.indications || "")) {
      updates.indications = debouncedIndications;
    }
    if (debouncedImpressions !== (caseData.impressions || "")) {
      updates.impressions = debouncedImpressions;
    }
    if (Object.keys(updates).length > 0) {
      documentViewService
        .updateClinicalHistory(caseId, updates)
        .then(() => refetchCase())
        .catch(() => {
          // Silently fail — main auto-save error handling handles visibility
        });
    }
  }, [
    amendmentDraft,
    refetchCase,
    debouncedClinicalHistory,
    debouncedIndications,
    debouncedImpressions,
    caseId,
    caseData,
    isDiagnosticReadOnly,
  ]);

  useEffect(() => {
    if (amendmentDraft) return;
    if (!caseId || !caseData || isDiagnosticReadOnly) return;
    if (!debouncedPathologistComment) return;

    documentViewService
      .savePathologistComment(caseId, debouncedPathologistComment)
      .catch(() => {
        // Silently fail
      });
  }, [
    amendmentDraft,
    debouncedPathologistComment,
    caseId,
    caseData,
    isDiagnosticReadOnly,
  ]);

  // Update grossing data for a specimen
  const updateGrossingData = useCallback(
    (specimenId: string, data: Partial<SpecimenGrossingData>) => {
      const existingGross = grossingDataMap.get(specimenId) || {
        specimenId,
        grossCode: "",
        grossCodeId: "",
        grossDescription: "",
      };
      const diagnosis = diagnosisDataMap.get(specimenId);
      const location = diagnosisLocationForGrossingSubsiteChange(
        diagnosis?.location ?? null,
        data
      );
      persistAmendmentFieldChange({
        grossing: [buildGrossingDraftEntry({ ...existingGross, ...data })],
        ...(data.subsiteId !== undefined
          ? { diagnoses: [{ specimenId, location }] }
          : {}),
      });
      setGrossingDataMap((prev) => {
        const newMap = new Map(prev);
        const existing = newMap.get(specimenId) || {
          specimenId,
          grossCode: "",
          grossCodeId: "",
          grossDescription: "",
        };
        newMap.set(specimenId, { ...existing, ...data });
        return newMap;
      });
      if (data.subsiteId !== undefined) {
        setDiagnosisDataMap((prev) => {
          const existing = prev.get(specimenId);
          if (!existing) return prev;
          const location = diagnosisLocationForGrossingSubsiteChange(
            existing.location,
            data
          );
          if (location === existing.location) return prev;
          const next = new Map(prev);
          next.set(specimenId, { ...existing, location });
          return next;
        });
      }
    },
    [diagnosisDataMap, grossingDataMap, persistAmendmentFieldChange]
  );

  // Copy diagnosis from one specimen to another
  // Per C-811 spec 6.2: Location is NOT copied - pathologist sets it manually for each part

  // Validation state
  const [validationErrors, setValidationErrors] = useState<SignOutIssue[]>([]);
  const [validationWarnings, setValidationWarnings] = useState<
    SignOutWarning[]
  >([]);
  const warningPresentation = useMemo(
    () => buildSignOutWarningPresentation([], validationWarnings),
    [validationWarnings]
  );
  const [
    validationModalOpened,
    { open: openValidationModal, close: closeValidationModal },
  ] = useDisclosure(false);
  const { data: controlSummary } = useControlSummary();
  const [
    warningModalOpened,
    { open: openWarningModal, close: closeWarningModal },
  ] = useDisclosure(false);

  const [historicalReportCase, setHistoricalReportCase] = useState<{
    id: string;
    caseNumber: string;
  } | null>(null);

  const handleViewHistoricalReport = useCallback(
    (id: string, caseNumber: string) => {
      setHistoricalReportCase({ id, caseNumber });
    },
    []
  );

  const handleCloseHistoricalReport = useCallback(() => {
    setHistoricalReportCase(null);
  }, []);

  // Sign-off modals state
  const [
    previewModalOpened,
    { open: openPreviewModal, close: closePreviewModal },
  ] = useDisclosure(false);
  const [authModalOpened, { open: openAuthModal, close: closeAuthModal }] =
    useDisclosure(false);
  const [
    successModalOpened,
    { open: openSuccessModal, close: closeSuccessModal },
  ] = useDisclosure(false);
  const [
    amendmentEditorOpened,
    { open: openAmendmentModal, close: closeAmendmentEditor },
  ] = useDisclosure(false);
  const [
    addendumEditorOpened,
    { open: openAddendumEditor, close: closeAddendumEditor },
  ] = useDisclosure(false);
  const [
    correctionAmendmentModalOpened,
    {
      open: openCorrectionAmendmentModal,
      close: closeCorrectionAmendmentModal,
    },
  ] = useDisclosure(false);
  const openTypedAmendment = useCallback(
    (type: "addendum" | "amended" | "corrected") => {
      if (type === "addendum") {
        openAddendumEditor();
        return;
      }
      if (type === "amended") {
        openAmendmentEditor();
        return;
      }
      openCorrectionAmendmentModal();
    },
    [openAddendumEditor, openAmendmentEditor, openCorrectionAmendmentModal]
  );
  const handleAddendumSigned = useCallback(() => {
    refetchCase?.();
    void loadCaseFiles();
    void handleReportImagesChanged();
  }, [handleReportImagesChanged, loadCaseFiles, refetchCase]);
  const handleCorrectionClosed = useCallback(() => {
    closeCorrectionAmendmentModal();
    refetchCase?.();
    void loadCaseFiles();
  }, [closeCorrectionAmendmentModal, loadCaseFiles, refetchCase]);
  const handleOpenFinalizedReport = useCallback(async () => {
    if (addendumDraftQuery.isError) {
      notifications.show({
        title: "Report status unavailable",
        message:
          "The addendum draft status could not be checked. Retrying now so no draft is hidden.",
        color: "red",
      });
      const result = await addendumDraftQuery.refetch();
      if (result.isSuccess) openPreviewModal();
      return;
    }
    if (addendumDraftQuery.isLoading) return;
    openPreviewModal();
  }, [addendumDraftQuery, openPreviewModal]);
  const handleOpenAddendum = useCallback(() => {
    openTypedAmendment("addendum");
  }, [openTypedAmendment]);
  const handleOpenAmendment = useCallback(() => {
    openTypedAmendment("amended");
  }, [openTypedAmendment]);
  const handleOpenCorrection = useCallback(() => {
    openTypedAmendment("corrected");
  }, [openTypedAmendment]);

  // Page-level macro search (F3 / macro search bar in grid view)
  const [
    gridMacroSearchOpened,
    { open: openGridMacroSearch, close: closeGridMacroSearch },
  ] = useDisclosure(false);
  const [
    historyModalOpened,
    { open: openHistoryModal, close: closeHistoryModal },
  ] = useDisclosure(false);
  const [
    requestStainModalOpened,
    { open: openRequestStainModal, close: closeRequestStainModal },
  ] = useDisclosure(false);
  const [
    malignancyModalOpened,
    { open: openMalignancyModal, close: closeMalignancyModal },
  ] = useDisclosure(false);
  const [
    externalOpinionModalOpened,
    { open: openExternalOpinionModal, close: closeExternalOpinionModal },
  ] = useDisclosure(false);
  const [
    duplicateConfirmOpened,
    { open: openDuplicateConfirm, close: closeDuplicateConfirm },
  ] = useDisclosure(false);
  const [
    externalOpinionDuplicateConfirmed,
    setExternalOpinionDuplicateConfirmed,
  ] = useState(false);
  const [
    flagDiscussionModalOpened,
    { open: openFlagDiscussionModal, close: closeFlagDiscussionModal },
  ] = useDisclosure(false);
  const [preselectedSpecimenId, setPreselectedSpecimenId] = useState<
    string | undefined
  >(undefined);

  const handleRequestStains = useCallback(() => {
    setPreselectedSpecimenId(undefined);
    openRequestStainModal();
  }, [openRequestStainModal]);

  const handleRequestStainsForSpecimen = useCallback(
    (specimenId: string) => {
      setPreselectedSpecimenId(specimenId);
      openRequestStainModal();
    },
    [openRequestStainModal]
  );

  const handleOpenExternalOpinionModal = useCallback(() => {
    setExternalOpinionDuplicateConfirmed(false);
    openExternalOpinionModal();
  }, [openExternalOpinionModal]);

  const handleOpenConfirmedDuplicateExternalOpinion = useCallback(() => {
    setExternalOpinionDuplicateConfirmed(true);
    closeDuplicateConfirm();
    openExternalOpinionModal();
  }, [closeDuplicateConfirm, openExternalOpinionModal]);

  const handleCloseExternalOpinionModal = useCallback(() => {
    setExternalOpinionDuplicateConfirmed(false);
    closeExternalOpinionModal();
  }, [closeExternalOpinionModal]);

  const handleExternalOpinionSuccess = useCallback(() => {
    refetchCase();
  }, [refetchCase]);

  const handleAddExternalInstitution = useCallback(() => {
    window.open("/admin/external-institutions", "_blank");
  }, []);

  // Malignancy notification data
  const [malignancyData, setMalignancyData] = useState<{
    malignancies: Array<{
      specimenNumber: string;
      specimenLabel: string;
      diagnosisText: string;
      categoryName: string;
    }>;
    notification: any;
    attempts: any[];
    physician: {
      id: string;
      firstName: string;
      lastName: string;
      phone?: string;
      fax?: string;
    } | null;
  } | null>(null);

  // Discussion Required modal state (per C-813 Section 7.2)
  const [
    discussionRequiredModalOpened,
    { open: openDiscussionRequiredModal, close: closeDiscussionRequiredModal },
  ] = useDisclosure(false);
  const [requiredDiscussionInfo, setRequiredDiscussionInfo] =
    useState<RequiredDiscussionInfo | null>(null);

  // Pending Discussions modal state (non-blocking override)
  const [
    pendingDiscussionsModalOpened,
    { open: openPendingDiscussionsModal, close: closePendingDiscussionsModal },
  ] = useDisclosure(false);
  const [pendingDiscussionsOverrideCount, setPendingDiscussionsOverrideCount] =
    useState(0);

  // Tracks whether the user has already overridden discussion checks in the
  // current sign-out attempt so the Warning modal preserves the flag.
  const [discussionChecksSkipped, setDiscussionChecksSkipped] = useState(false);

  // Review Preferences Modal
  const [
    reviewPrefsOpened,
    { open: openReviewPrefs, close: closeReviewPrefs },
  ] = useDisclosure(false);
  const { data: savedReviewPrefs } = useUserPreference(
    REVIEW_SCREEN_PREFERENCE_KEY
  );
  const { mutate: saveReviewPrefs, isPending: isSavingReviewPrefs } =
    useSaveUserPreference();

  const reviewPreferences = useMemo<ReviewScreenPreferences>(() => {
    const validWidgetIds = new Set(ALL_REVIEW_WIDGETS.map((w) => w.id));
    const rawPrefs = {
      ...DEFAULT_REVIEW_PREFERENCES,
      ...(savedReviewPrefs as Partial<ReviewScreenPreferences> | null),
    };
    return {
      ...rawPrefs,
      widgetOrder: rawPrefs.widgetOrder.filter((id) => validWidgetIds.has(id)),
    };
  }, [savedReviewPrefs]);

  const handleSaveReviewPreferences = (prefs: ReviewScreenPreferences) => {
    saveReviewPrefs(
      { key: REVIEW_SCREEN_PREFERENCE_KEY, value: prefs },
      { onSuccess: closeReviewPrefs }
    );
  };

  // Set initial nav item from editing mode preference (runs once after prefs load)
  useEffect(() => {
    if (savedReviewPrefs && !initialNavSet.current) {
      initialNavSet.current = true;
      const mode = (savedReviewPrefs as ReviewScreenPreferences).editingMode;
      setActiveNavItem(mode === "document" ? "report" : "grid");
    }
  }, [savedReviewPrefs, setActiveNavItem]);

  // Sign-off session state - persist in localStorage for cross-case sessions
  const getStoredSession = () => {
    try {
      const stored = localStorage.getItem("signOffSession");
      if (stored) {
        const { sessionId, expiresAt } = JSON.parse(stored);
        const expiry = new Date(expiresAt);
        // Only return if session is still valid
        if (expiry > new Date()) {
          return { sessionId, expiresAt: expiry };
        }
        // Clear expired session
        localStorage.removeItem("signOffSession");
      }
    } catch {
      localStorage.removeItem("signOffSession");
    }
    return { sessionId: null, expiresAt: null };
  };

  const storedSession = getStoredSession();
  const [signOffSessionId, setSignOffSessionIdState] = useState<string | null>(
    storedSession.sessionId
  );
  const [signOffSessionExpiry, setSignOffSessionExpiryState] =
    useState<Date | null>(storedSession.expiresAt);

  // Wrapper to persist session to localStorage
  const setSignOffSession = useCallback(
    (sessionId: string | null, expiresAt: Date | null) => {
      setSignOffSessionIdState(sessionId);
      setSignOffSessionExpiryState(expiresAt);
      if (sessionId && expiresAt) {
        localStorage.setItem(
          "signOffSession",
          JSON.stringify({ sessionId, expiresAt: expiresAt.toISOString() })
        );
      } else {
        localStorage.removeItem("signOffSession");
      }
    },
    []
  );
  const handleAddendumSessionUpdated = useCallback(
    (sessionId: string, expiresAt?: string) => {
      setSignOffSession(
        sessionId,
        expiresAt
          ? new Date(expiresAt)
          : new Date(Date.now() + 4 * 60 * 60 * 1000)
      );
    },
    [setSignOffSession]
  );

  const [signOffError, setSignOffError] = useState<string | null>(null);
  const [signOffLoading, setSignOffLoading] = useState(false);
  // Template the pathologist picked in the preview modal. Undefined when no
  // explicit pick — the backend renderer's auto-select handles that case.
  const signOffTemplateIdRef = useRef<string | undefined>(undefined);
  // Track pending sign-off options for when auth modal opens mid-flow
  const [pendingSignOffOptions, setPendingSignOffOptions] = useState<{
    skipWarnings?: boolean;
    specimenIds?: string[];
    skipDiscussionChecks?: boolean;
    createRequiredDiscussions?: boolean;
    previewEvidence?: signOffService.ReportPreviewEvidence;
  } | null>(null);
  const [warningSignOffOptions, setWarningSignOffOptions] =
    useState<typeof pendingSignOffOptions>(null);
  const [reflexProposals, setReflexProposals] = useState<ReflexPanelProposal[]>(
    []
  );
  const [tissueCypherProposals, setTissueCypherProposals] = useState<
    TissueCypherSignOutProposal[]
  >([]);
  const [reflexModalOpened, setReflexModalOpened] = useState(false);
  const [reflexSubmitting, setReflexSubmitting] = useState(false);
  const reflexPreflightCompletedRef = useRef(false);
  const reviewedReflexProposalKeysRef = useRef<string[] | undefined>(undefined);
  const reviewedTissueCypherProposalKeysRef = useRef<string[] | undefined>(
    undefined
  );
  const [signOffResult, setSignOffResult] = useState<{
    caseNumber: string;
    signedBy: string;
    signedAt: string;
    deliveryQueue: signOffService.SignOffResult["deliveryQueue"];
    nextCaseId: string | null;
    isPartialSignOff?: boolean;
    signedSpecimenCount?: number;
    pendingSpecimenCount?: number;
  } | null>(null);

  // Check if sign-off session is valid
  // Always check localStorage directly to handle cross-navigation scenarios
  const isSignOffSessionValid = (): boolean => {
    // First check React state
    if (
      signOffSessionId &&
      signOffSessionExpiry &&
      new Date() < signOffSessionExpiry
    ) {
      return true;
    }
    // Also check localStorage directly (handles component remount scenarios)
    try {
      const stored = localStorage.getItem("signOffSession");
      if (stored) {
        const { sessionId, expiresAt } = JSON.parse(stored);
        const expiry = new Date(expiresAt);
        if (expiry > new Date() && sessionId) {
          // Sync state with localStorage
          setSignOffSessionIdState(sessionId);
          setSignOffSessionExpiryState(expiry);
          return true;
        }
      }
    } catch {
      // Ignore parsing errors
    }
    return false;
  };

  // Validate all specimens before sign-out
  const validateForSignOut = (): {
    isValid: boolean;
    errors: Array<{
      specimenNumber: string;
      field: string;
      message: string;
      severity: "error" | "warning";
    }>;
    warnings: Array<{ specimenNumber: string; field: string; message: string }>;
  } => {
    const errors: Array<{
      specimenNumber: string;
      field: string;
      message: string;
      severity: "error" | "warning";
    }> = [];
    const warnings: Array<{
      specimenNumber: string;
      field: string;
      message: string;
    }> = [];

    const specimenList = caseData?.specimens || [];
    const anySpecimenHasIcdCode = hasWholeCaseIcdCode(
      specimenList.map((specimen: any) => {
        const diagData = diagnosisDataMap.get(specimen.id);
        return diagData?.icdCodes?.length
          ? diagData.icdCodes[0]
          : diagData?.icdCode;
      })
    );
    specimenList.forEach((specimen: any) => {
      const diagData = diagnosisDataMap.get(specimen.id);
      const sourceName = specimen.source?.name;
      const collectionMethod = specimen.collectionMethod;
      let specimenLabel = `${specimen.number}`;
      if (sourceName) specimenLabel += ` - ${sourceName}`;
      if (collectionMethod) specimenLabel += ` (${collectionMethod})`;

      // Blocking errors
      if (isEmptyHtml(diagData?.diagnosisText)) {
        errors.push({
          specimenNumber: specimenLabel,
          field: "Diagnosis",
          message: "Diagnosis text is required",
          severity: "error",
        });
      }

      // Non-blocking warnings (shown in modal but don't prevent sign-out)
      if (!diagData?.category) {
        errors.push({
          specimenNumber: specimenLabel,
          field: "Category",
          message: "No category selected",
          severity: "warning",
        });
      }

      if (!diagData?.cptCode) {
        errors.push({
          specimenNumber: specimenLabel,
          field: "CPT Code",
          message: "No CPT code selected",
          severity: "warning",
        });
      }

      if (
        !anySpecimenHasIcdCode &&
        !diagData?.icdCodes?.length &&
        !diagData?.icdCode
      ) {
        errors.push({
          specimenNumber: specimenLabel,
          field: "ICD Code",
          message: "No ICD code selected",
          severity: "warning",
        });
      }

      // --- Unresolved template variable checks (BLOCKING) ---
      // These appear on the printed report and are a patient safety issue.
      const grossData = grossingDataMap.get(specimen.id);

      const fieldsToCheck: Array<{
        label: string;
        html: string | null | undefined;
      }> = [
        { label: "Diagnosis", html: diagData?.diagnosisText },
        {
          label: "Microscopic Description",
          html: diagData?.microscopicDescription,
        },
        {
          label: "Gross Description",
          html: grossData
            ? resolveGrossVariables(
                grossData.grossDescription,
                grossData.variableValues
              )
            : null,
        },
      ];

      fieldsToCheck.forEach(({ label, html }) => {
        const unresolved = getUnresolvedVariables(html);
        if (unresolved.length > 0) {
          errors.push({
            specimenNumber: specimenLabel,
            field: label,
            message: `Unresolved template variable(s): ${unresolved.join(
              ", "
            )}`,
            severity: "error",
          });
        }
      });

      // Note: Malignant category notification check is handled by the backend
      // as a blocking error with full notification workflow (CancerNotificationService)
    });

    // Case-level text fields that appear on the report
    const caseLevelFields: Array<{
      label: string;
      html: string | null | undefined;
    }> = [
      { label: "Clinical History", html: documentClinicalHistory },
      { label: "Indications", html: documentIndications },
      { label: "Impressions", html: documentImpressions },
      { label: "Pathologist Comment", html: documentPathologistComment },
      { label: "Frozen Interpretation", html: caseFrozenInterpretation },
    ];

    caseLevelFields.forEach(({ label, html }) => {
      const unresolved = getUnresolvedVariables(html);
      if (unresolved.length > 0) {
        errors.push({
          specimenNumber: "Case",
          field: label,
          message: `Unresolved template variable(s): ${unresolved.join(", ")}`,
          severity: "error",
        });
      }
    });

    // Check for pending additional stains (case-level warning)
    if (pendingStains && pendingStains.length > 0) {
      warnings.push({
        specimenNumber: "Case",
        field: "Pending Stains",
        message: `${pendingStains.length} pending stain request(s) not yet completed`,
      });
    }

    const hasBlockingErrors = errors.some((e) => e.severity === "error");
    return { isValid: !hasBlockingErrors, errors, warnings };
  };

  // Get specimen IDs without pending stains (for partial sign-off)
  const getSpecimensWithoutPendingStains = (): string[] => {
    if (!caseData?.specimens || !pendingStains) return [];

    // Get specimen IDs that have pending stains
    const specimensWithPendingStains = new Set(
      pendingStains.map((s) => s.specimen.id)
    );

    // Return specimens that don't have pending stains
    return caseData.specimens
      .filter((s: any) => !specimensWithPendingStains.has(s.id))
      .map((s: any) => s.id);
  };

  // Check if partial sign-off is available (some specimens can be signed)
  const canDoPartialSignOff = (): boolean => {
    const availableSpecimens = getSpecimensWithoutPendingStains();
    return (
      pendingStains &&
      pendingStains.length > 0 &&
      availableSpecimens.length > 0 &&
      availableSpecimens.length < (caseData?.specimens?.length || 0)
    );
  };

  // Get session ID from state or localStorage
  const getSessionId = (): string | undefined => {
    if (signOffSessionId) return signOffSessionId;
    try {
      const stored = localStorage.getItem("signOffSession");
      if (stored) {
        const { sessionId } = JSON.parse(stored);
        return sessionId || undefined;
      }
    } catch {
      // Ignore
    }
    return undefined;
  };

  const saveDraftAsync = useCallback(
    async (retainedSpecimenIds?: string[]) => {
      if (!caseId) return;
      if (amendmentDraft)
        return saveAmendmentPatch(amendmentDraft.id, {
          diagnoses: Array.from(diagnosisDataMap.values()).map(
            ({ cptDisplayCode, ...row }) => row
          ),
          grossing: buildGrossingDraftEntries(grossingDataMap),
          caseLevelData: {
            frozenInterpretation: caseFrozenInterpretation,
            clinicalHistoryNotes: documentClinicalHistory,
            indications: documentIndications,
            impressions: documentImpressions,
          },
        });
      const diagnoses = Array.from(diagnosisDataMap.values()).map((d) => ({
        specimenId: d.specimenId,
        macroCode: d.macroCode || null,
        diagnosisText: d.diagnosisText ?? "",
        microscopicDescription: d.microscopicDescription ?? "",
        location: d.location ?? "",
        category: d.category ?? "",
        cptCode: d.cptCode ?? "",
        icdCode: d.icdCode,
        icdCodes: d.icdCodes,
        color: d.color ?? "",
        sizeCm: d.sizeCm ?? "",
        sizeMm: d.sizeMm ?? "",
        consistency: d.consistency ?? "",
      }));

      const grossing = buildGrossingDraftEntries(grossingDataMap);
      const draftPayload = buildPathologistReviewDraftPayload({
        diagnoses,
        grossing,
        specimens: caseData?.specimens || [],
        retainedSpecimenIds,
        isPartiallyFinalized: Boolean(caseData?.isPartiallyFinalized),
        frozenInterpretation: caseFrozenInterpretation,
      });
      const fingerprint = buildDraftFingerprint(
        caseId,
        diagnosisDataMap,
        grossingDataMap,
        caseFrozenInterpretation,
        retainedSpecimenIds
      );
      setAutoSaveStatus("saving");
      try {
        const result = await coordinateDraftSave(fingerprint, {
          caseId,
          ...draftPayload,
        });
        lastSavedDataRef.current = JSON.stringify({
          d: Array.from(debouncedDiagnosisData.entries()),
          g: Array.from(debouncedGrossingData.entries()),
        });
        setAutoSaveStatus("saved");
        return result;
      } catch (error) {
        setAutoSaveStatus("error");
        handleStalePathologistAssignment(error);
        throw error;
      }
    },
    [
      amendmentDraft,
      saveAmendmentPatch,
      documentClinicalHistory,
      documentIndications,
      documentImpressions,
      caseData?.isPartiallyFinalized,
      caseData?.specimens,
      caseFrozenInterpretation,
      caseId,
      coordinateDraftSave,
      debouncedDiagnosisData,
      debouncedGrossingData,
      diagnosisDataMap,
      grossingDataMap,
      handleStalePathologistAssignment,
    ]
  );

  // Actually perform the sign-out (called after validation/confirmation)
  // specimenIds: if provided, only sign off these specimens (partial sign-off)
  // suppressErrorToast: if true, errors are set in state but not shown as a toast
  //   (used when doSignOut is called programmatically, e.g. after malignancy notification)
  const doSignOut = async (
    password?: string,
    skipWarnings = false,
    specimenIds?: string[],
    suppressErrorToast = false,
    skipDiscussionChecks = false,
    createRequiredDiscussions = false,
    previewEvidence?: signOffService.ReportPreviewEvidence
  ) => {
    closeWarningModal();
    setWarningSignOffOptions(null);

    if (!caseId) return;

    if (!reflexPreflightCompletedRef.current) {
      try {
        await saveDraftAsync(specimenIds);
        const [proposals, tissueCypher] = await Promise.all([
          reflexPanelsService.getProposals(caseId),
          reflexPanelsService.getTissueCypherProposals(caseId),
        ]);
        if (proposals.length > 0 || tissueCypher.length > 0) {
          setPendingSignOffOptions({
            skipWarnings,
            specimenIds,
            skipDiscussionChecks,
            createRequiredDiscussions,
            previewEvidence,
          });
          setReflexProposals(proposals);
          setTissueCypherProposals(tissueCypher);
          setReflexModalOpened(true);
          return;
        }
        reviewedReflexProposalKeysRef.current = [];
        reviewedTissueCypherProposalKeysRef.current = [];
        reflexPreflightCompletedRef.current = true;
      } catch (error) {
        notifications.show({
          title: "Reflex check unavailable",
          message:
            getUserFriendlyErrorMessage(error) ||
            "The case could not be checked for reflex panels. Try sign-out again.",
          color: "red",
        });
        return;
      }
    }

    if (reportReviewStatus?.reviewRequired !== false && !previewEvidence) {
      setPendingSignOffOptions({
        skipWarnings,
        specimenIds,
        skipDiscussionChecks,
        createRequiredDiscussions,
        previewEvidence,
      });
      await Promise.all([
        captionUpdateQueue.flush(),
        fileCaptionUpdateQueue.flush(),
      ]);
      await saveDraftAsync(specimenIds);
      openPreviewModal();
      return;
    }

    // If no valid session and no password provided, show auth modal
    if (!isSignOffSessionValid() && !password) {
      // Save the options so they're used after authentication
      setPendingSignOffOptions({
        skipWarnings,
        specimenIds,
        skipDiscussionChecks,
        createRequiredDiscussions,
        previewEvidence,
      });
      openAuthModal();
      return;
    }

    setSignOffLoading(true);
    setSignOffError(null);

    try {
      if (!previewEvidence) {
        await Promise.all([
          captionUpdateQueue.flush(),
          fileCaptionUpdateQueue.flush(),
        ]);
        // Evidence was issued only after this persistence boundary. Saving
        // again after acknowledgement would advance the canonical revision
        // and make the report the pathologist just reviewed stale.
        await saveDraftAsync(specimenIds);
      }

      const reportData =
        previewEvidence?.reportData ??
        (await documentViewService.fetchInitialPreviewProjection(
          caseId,
          signOffTemplateIdRef.current
        ));

      const result = await signOffService.signOutCase(caseId, {
        password,
        sessionId: getSessionId(),
        skipWarnings,
        specimenIds, // For partial sign-off
        skipDiscussionChecks,
        createRequiredDiscussions,
        templateId: signOffTemplateIdRef.current,
        previewEvidence,
        reportData,
        reviewedReflexProposalKeys: reviewedReflexProposalKeysRef.current,
        reviewedTissueCypherProposalKeys:
          reviewedTissueCypherProposalKeysRef.current,
      });

      // Save session info if present (even if sign-off failed)
      // This ensures auth session persists for subsequent cases
      if (result.sessionId) {
        const expiresAt = result.sessionExpiresAt
          ? new Date(result.sessionExpiresAt)
          : new Date(Date.now() + 4 * 60 * 60 * 1000); // Default 4 hours
        setSignOffSession(result.sessionId, expiresAt);
      }

      if (!result.success) {
        if (
          result.reviewErrorCode === "REPORT_REVIEW_REQUIRED" ||
          result.reviewErrorCode === "REPORT_REVIEW_STALE"
        ) {
          setSignOffLoading(false);
          openPreviewModal();
          return;
        }
        // Check if authentication is required or password was wrong
        if ((result as any).requiresPassword) {
          setSignOffLoading(false);
          // If password was provided, it means the password was wrong
          // (otherwise requiresPassword just means session expired)
          if (password) {
            setSignOffError(
              "The password you entered is incorrect. Please try again."
            );
          }
          openAuthModal();
          return;
        }

        // Close auth modal first before showing any error modals
        closeAuthModal();

        // Check if there are blocking errors
        if (result.blockingErrors && result.blockingErrors.length > 0) {
          setSignOffLoading(false);

          // Check if any blocking error is for malignancy notification
          const hasMalignancyError = result.blockingErrors.some((e: string) =>
            e.toLowerCase().includes("malignancy notification")
          );

          if (hasMalignancyError && caseId) {
            // Fetch malignancy notification data and show the modal
            try {
              const notificationData =
                await cancerNotificationService.getCancerNotification(caseId);
              setMalignancyData({
                malignancies: notificationData.malignancies,
                notification: notificationData.notification,
                attempts: notificationData.notification?.attempts || [],
                physician: caseData?.physician
                  ? {
                      id: caseData.physician.id,
                      firstName: caseData.physician.firstName || "",
                      lastName: caseData.physician.lastName || "",
                      phone: caseData.physician.phone,
                      fax: caseData.physician.fax,
                    }
                  : null,
              });
              openMalignancyModal();
            } catch (err) {
              // If we can't fetch notification data, show the regular validation modal
              setValidationErrors(
                result.blockingErrors.map((e: string) => ({
                  ...parseBackendSignOffIssue(e),
                  field: "",
                  severity: "error" as const,
                }))
              );
              openValidationModal();
            }
            return;
          }

          // Check if any blocking error is discussion-related (C-813 Section 7)
          const hasDiscussionRequiredError = result.blockingErrors.some(
            (e: string) =>
              e.toLowerCase().includes("discussion required") &&
              e.toLowerCase().includes("diagnosis criteria")
          );
          const hasUnresolvedDiscussionError = result.blockingErrors.some(
            (e: string) =>
              e.toLowerCase().includes("unresolved") &&
              e.toLowerCase().includes("discussion")
          );

          if (hasDiscussionRequiredError && result.requiredDiscussions) {
            // Required discussions missing — show modal with create + override options
            setRequiredDiscussionInfo(result.requiredDiscussions);
            openDiscussionRequiredModal();

            const nonDiscussionErrors = result.blockingErrors.filter(
              (e: string) => {
                const lower = e.toLowerCase();
                const isDiscussionErr =
                  (lower.includes("discussion required") &&
                    lower.includes("diagnosis criteria")) ||
                  (lower.includes("unresolved") &&
                    lower.includes("discussion"));
                return !isDiscussionErr;
              }
            );
            if (nonDiscussionErrors.length > 0) {
              setValidationErrors(
                nonDiscussionErrors.map((e: string) => ({
                  ...parseBackendSignOffIssue(e),
                  field: "",
                  severity: "error" as const,
                }))
              );
            }
            return;
          }

          if (hasUnresolvedDiscussionError) {
            // Pending discussions exist — show non-blocking override modal
            const pendingCount =
              result.requiredDiscussions?.existingDiscussions?.filter(
                (d: { isPending: boolean }) => d.isPending
              ).length ??
              parseInt(
                result.blockingErrors
                  .find(
                    (e: string) =>
                      e.toLowerCase().includes("unresolved") &&
                      e.toLowerCase().includes("discussion")
                  )
                  ?.match(/\d+/)?.[0] ?? "1",
                10
              );
            setPendingDiscussionsOverrideCount(pendingCount);
            openPendingDiscussionsModal();
            return;
          }

          setValidationErrors(
            result.blockingErrors.map((e: string) => ({
              ...parseBackendSignOffIssue(e),
              field: "",
              severity: "error" as const,
            }))
          );
          openValidationModal();
          return;
        }

        // Check if there are warnings to confirm
        if (result.warnings && result.warnings.length > 0 && !skipWarnings) {
          setSignOffLoading(false);
          setWarningSignOffOptions({
            specimenIds,
            skipDiscussionChecks,
            createRequiredDiscussions,
            previewEvidence,
          });
          setValidationWarnings(
            result.warnings.map(normalizeBackendSignOutWarning)
          );
          openWarningModal();
          return;
        }

        throw new Error(result.message || "Sign-out failed");
      }

      closeAuthModal();
      setDiscussionChecksSkipped(false);

      // Calculate next case ID before queue refetch changes the data
      const availableCasesForNav = queueCases.filter((c: any) => !c.isBlocked);
      const currentIdx = availableCasesForNav.findIndex(
        (c: any) => c.id === caseId
      );
      const nextCaseForNav =
        currentIdx >= 0 && currentIdx < availableCasesForNav.length - 1
          ? availableCasesForNav[currentIdx + 1]
          : null;

      signOffTemplateIdRef.current = undefined; // Clear so next case starts fresh

      setSignOffResult({
        caseNumber: caseData?.caseNumber || "",
        signedBy: result.signedBy?.name || "",
        signedAt: result.signedAt || new Date().toISOString(),
        deliveryQueue: result.deliveryQueue,
        nextCaseId: nextCaseForNav?.id || null,
        isPartialSignOff: result.isPartialSignOff,
        signedSpecimenCount: result.signedSpecimenIds?.length,
        pendingSpecimenCount: result.pendingSpecimenIds?.length,
      });

      // Refetch case data
      refetchCase();
      void loadCaseFiles();

      // Post sign-out navigation driven by user preference
      const postAction = reviewPreferences.postSignOutAction;
      if (postAction === "next-case") {
        if (nextCaseForNav) {
          navigate(`/pathologist-review/${nextCaseForNav.id}`, {
            state: location.state,
          });
        } else {
          handleReturnToQueue();
        }
      } else if (postAction === "return-to-queue") {
        handleReturnToQueue();
      } else {
        // "scan-modal" — open the success modal with barcode input
        openSuccessModal();
      }
    } catch (error: any) {
      handleStalePathologistAssignment(error);
      // Close auth modal first so error is visible
      closeAuthModal();

      const friendlyMessage = getUserFriendlyErrorMessage(error);
      setSignOffError(friendlyMessage);
      // Only show the error toast when sign-off was user-initiated.
      // When called programmatically (e.g. after malignancy notification) the
      // error is surfaced via signOffError state instead of a toast, preventing
      // a false "unexpected error" flash if any secondary step fails transiently.
      if (!suppressErrorToast) {
        notifications.show({
          title: "Sign Out Failed",
          message: friendlyMessage,
          color: "red",
        });
      }
    } finally {
      setSignOffLoading(false);
    }
  };

  // Handle authentication and sign-out
  const handleAuthenticateAndSignOut = async (password: string) => {
    // Use pending options if available (from warning modal flow)
    const skipWarnings = pendingSignOffOptions?.skipWarnings || false;
    const specimenIds = pendingSignOffOptions?.specimenIds;
    const skipDiscussionChecks =
      pendingSignOffOptions?.skipDiscussionChecks || false;
    const createRequiredDiscussions =
      pendingSignOffOptions?.createRequiredDiscussions || false;
    const previewEvidence = pendingSignOffOptions?.previewEvidence;
    setPendingSignOffOptions(null); // Clear after use
    await doSignOut(
      password,
      skipWarnings,
      specimenIds,
      false,
      skipDiscussionChecks,
      createRequiredDiscussions,
      previewEvidence
    );
  };

  // Clear preselected category when Flag Discussion modal closes
  const handleFlagDiscussionModalClose = () => {
    closeFlagDiscussionModal();
  };

  // Malignancy notification handlers
  const refreshMalignancyData = async () => {
    if (!caseId) return;
    try {
      const notificationData =
        await cancerNotificationService.getCancerNotification(caseId);
      setMalignancyData((prev) =>
        prev
          ? {
              ...prev,
              notification: notificationData.notification,
              attempts: notificationData.notification?.attempts || [],
            }
          : null
      );
    } catch {
      // Silently handle malignancy refresh errors
    }
  };

  const handleInitiateCall = async (phoneNumber?: string) => {
    if (!caseId) return { success: false, error: "No case ID" };
    try {
      const result = await cancerNotificationService.initiateCall(
        caseId,
        phoneNumber
      );
      await refreshMalignancyData();
      return result;
    } catch (err: any) {
      return { success: false, error: err.message };
    }
  };

  const handleSendSms = async (phoneNumber?: string, message?: string) => {
    if (!caseId) return { success: false, error: "No case ID" };
    try {
      const result = await cancerNotificationService.sendSms(
        caseId,
        phoneNumber,
        message
      );
      await refreshMalignancyData();
      return result;
    } catch (err: any) {
      return { success: false, error: err.message };
    }
  };

  const handleSendFax = async (faxNumber?: string) => {
    if (!caseId) return { success: false, error: "No case ID" };
    try {
      const result = await cancerNotificationService.sendFax(caseId, faxNumber);
      await refreshMalignancyData();
      return result;
    } catch (err: any) {
      return { success: false, error: err.message };
    }
  };

  const handleDocumentOutcome = async (data: {
    outcome: string;
    spokeWith?: string;
    notes?: string;
    notificationMethod: string;
  }) => {
    if (!caseId) return { success: false, error: "No case ID" };
    try {
      await cancerNotificationService.documentOutcome(caseId, {
        outcome: data.outcome as any,
        spokeWith: data.spokeWith,
        notes: data.notes,
        notificationMethod: data.notificationMethod as any,
      });
      closeMalignancyModal();
      // Now proceed with sign-out since notification is documented.
      // Pass suppressErrorToast=true so that any transient failure during
      // this programmatic sign-off attempt does not flash a misleading error
      // toast – the confirmation modal will appear on success as normal.
      doSignOut(undefined, false, undefined, true);
      return { success: true };
    } catch (err: any) {
      return { success: false, error: err.message };
    }
  };

  const handleGetRecordingUrl = async (attemptId: string) => {
    if (!caseId) return null;
    try {
      const result = await cancerNotificationService.getRecordingUrl(
        caseId,
        attemptId
      );
      return result.url;
    } catch {
      return null;
    }
  };

  // Sign out handler
  const handleSignOut = async () => {
    if (amendmentDraft) {
      try {
        setAmendmentDiagnosticsPaused(true);
        await saveDraftAsync();
        openAmendmentModal();
      } catch {
        setAmendmentDiagnosticsPaused(false);
        notifications.show({
          title: "Draft not saved",
          message: "Save your diagnostic changes before continuing.",
          color: "red",
        });
      }
      return;
    }

    reflexPreflightCompletedRef.current = false;
    reviewedReflexProposalKeysRef.current = undefined;
    reviewedTissueCypherProposalKeysRef.current = undefined;
    // First do client-side validation
    const { errors, warnings } = validateForSignOut();
    const blockingErrors = errors.filter((error) => error.severity === "error");
    const nonBlockingWarnings = [
      ...errors
        .filter((error) => error.severity === "warning")
        .map(toSignOutWarning),
      ...warnings.map(toSignOutWarning),
    ];

    if (blockingErrors.length > 0) {
      setValidationErrors([
        ...blockingErrors,
        ...nonBlockingWarnings.map((warning) => ({
          specimenNumber: warning.specimenNumber,
          field: warning.field,
          message: warning.message,
          severity: "warning" as const,
        })),
      ]);
      openValidationModal();
      return;
    }

    if (nonBlockingWarnings.length > 0) {
      setValidationWarnings(nonBlockingWarnings);
      openWarningModal();
      return;
    }

    // No errors or warnings - proceed with sign-out
    doSignOut();
  };

  const doSignOutRef = useRef(doSignOut);
  doSignOutRef.current = doSignOut;
  const handleConfirmSignOutWarning = useCallback(() => {
    const options = warningSignOffOptions;
    setWarningSignOffOptions(null);
    void doSignOutRef.current(
      undefined,
      true,
      options?.specimenIds,
      false,
      options?.skipDiscussionChecks || discussionChecksSkipped,
      options?.createRequiredDiscussions || false,
      options?.previewEvidence
    );
  }, [discussionChecksSkipped, warningSignOffOptions]);
  const handleCancelSignOutWarning = useCallback(() => {
    setWarningSignOffOptions(null);
    closeWarningModal();
  }, [closeWarningModal]);
  const handleCancelReflexDecision = useCallback(() => {
    setReflexModalOpened(false);
    setReflexProposals([]);
    setTissueCypherProposals([]);
    reflexPreflightCompletedRef.current = false;
    reviewedReflexProposalKeysRef.current = undefined;
    reviewedTissueCypherProposalKeysRef.current = undefined;
  }, []);
  const handleContinueAfterReflexDecision = useCallback(
    async (acceptedProposalKeys: string[]) => {
      if (!caseId) return;
      setReflexSubmitting(true);
      try {
        const accepted = reflexProposals.filter((proposal) =>
          acceptedProposalKeys.includes(proposal.proposalKey)
        );
        if (accepted.length > 0) {
          await reflexPanelsService.acceptProposals(
            caseId,
            accepted.map((proposal) => ({
              proposalKey: proposal.proposalKey,
              enrollmentId: proposal.enrollmentId,
              reflexRuleId: proposal.reflexRuleId,
              specimenId: proposal.specimenId,
            }))
          );
        }
        const acceptedTissueCypherKeys = tissueCypherProposals
          .filter((proposal) =>
            acceptedProposalKeys.includes(proposal.proposalKey)
          )
          .map((proposal) => proposal.proposalKey);
        if (acceptedTissueCypherKeys.length > 0) {
          await reflexPanelsService.acceptTissueCypherProposals(
            caseId,
            acceptedTissueCypherKeys
          );
        }
        const continuation = buildReflexSignOutContinuation(
          reflexProposals,
          acceptedProposalKeys,
          pendingSignOffOptions?.previewEvidence
        );
        reviewedReflexProposalKeysRef.current =
          continuation.reviewedProposalKeys;
        reviewedTissueCypherProposalKeysRef.current = tissueCypherProposals.map(
          (proposal) => proposal.proposalKey
        );
        reflexPreflightCompletedRef.current = true;
        setReflexModalOpened(false);
        setReflexProposals([]);
        setTissueCypherProposals([]);
        closePreviewModal();
        const options = pendingSignOffOptions;
        setPendingSignOffOptions(
          options
            ? { ...options, previewEvidence: continuation.previewEvidence }
            : null
        );
        await doSignOutRef.current(
          undefined,
          options?.skipWarnings || false,
          options?.specimenIds,
          false,
          options?.skipDiscussionChecks || false,
          options?.createRequiredDiscussions || false,
          continuation.previewEvidence
        );
      } catch (error) {
        notifications.show({
          title: "Reflex panel was not ordered",
          message:
            getUserFriendlyErrorMessage(error) ||
            "The selected reflex panel could not be ordered. Nothing was changed.",
          color: "red",
        });
      } finally {
        setReflexSubmitting(false);
      }
    },
    [
      caseId,
      closePreviewModal,
      pendingSignOffOptions,
      reflexProposals,
      tissueCypherProposals,
    ]
  );
  const handleReviewedReportSignOut = useCallback(
    async (previewEvidence?: signOffService.ReportPreviewEvidence) => {
      const options = pendingSignOffOptions;
      setPendingSignOffOptions(
        previewEvidence ? { ...options, previewEvidence } : options
      );
      closePreviewModal();
      await doSignOutRef.current(
        undefined,
        options?.skipWarnings || false,
        options?.specimenIds,
        false,
        options?.skipDiscussionChecks || false,
        options?.createRequiredDiscussions || false,
        previewEvidence
      );
    },
    [closePreviewModal, pendingSignOffOptions]
  );

  // Handle preview report
  const handlePreviewReport = useCallback(async () => {
    if (!caseId) return;
    try {
      await saveDraftAsync();
    } catch (error) {
      notifications.show({
        title: "Preview not opened",
        message:
          getUserFriendlyErrorMessage(error) ||
          "Your draft could not be saved. Check your connection, then try Preview Report again.",
        color: "red",
      });
      return;
    }
    try {
      await Promise.all([
        captionUpdateQueue.flush(),
        fileCaptionUpdateQueue.flush(),
      ]);
      openPreviewModal();
    } catch {
      notifications.show({
        title: "Caption not saved",
        message: "Check your connection, then open Preview Report again.",
        color: "red",
      });
    }
  }, [
    captionUpdateQueue,
    caseId,
    fileCaptionUpdateQueue,
    openPreviewModal,
    saveDraftAsync,
  ]);

  const handleContinueAmendment = useCallback(async () => {
    try {
      setAmendmentDiagnosticsPaused(true);
      if (amendmentDraft) await saveDraftAsync();
      openAmendmentModal();
    } catch {
      setAmendmentDiagnosticsPaused(false);
      notifications.show({
        title: "Draft not saved",
        message: "Save your diagnostic changes before continuing.",
        color: "red",
      });
    }
  }, [amendmentDraft, openAmendmentModal, saveDraftAsync]);

  const handleCloseAmendment = useCallback(() => {
    setAmendmentDiagnosticsPaused(false);
    closeAmendmentEditor();
  }, [closeAmendmentEditor]);

  // Save draft handler
  const handleSaveDraft = useCallback(async () => {
    if (!caseId) return;
    try {
      const data = await saveDraftAsync();
      setTimeout(() => setAutoSaveStatus("idle"), 3000);
      notifications.show({
        title: "Draft Saved",
        message: `Saved draft for ${data?.savedCount ?? 0} specimen(s)`,
        color: "green",
      });
    } catch (error) {
      if (handleStalePathologistAssignment(error)) return;
      notifications.show({
        title: "Save Failed",
        message:
          getUserFriendlyErrorMessage(error) ||
          "Unable to save your changes. Please try again.",
        color: "red",
      });
    }
  }, [caseId, handleStalePathologistAssignment, saveDraftAsync]);

  // Check if locked by another user
  const isLockedByOther =
    lockData && lockData.lockedById && lockData.lockedById !== userId;

  const canForceRelease = useHasPermission("case_locks.force_release");
  const forceReleaseMutation = useForceReleaseLock();

  const handleForceUnlock = useCallback(() => {
    if (
      !window.confirm(
        `Force-unlock this case from ${
          lockData?.lockedByName || "another user"
        }? They will lose their lock immediately.`
      )
    )
      return;
    forceReleaseMutation.mutate(caseId!, {
      onSuccess: () => {
        notifications.show({
          title: "Lock Released",
          message: "The case has been unlocked.",
          color: "green",
        });
      },
      onError: (err: Error) => {
        notifications.show({
          title: "Error",
          message: err.message || "Failed to release lock",
          color: "red",
        });
      },
    });
  }, [caseId, lockData, forceReleaseMutation]);

  // Auto-start review on mount (only if not locked by another user and not blocked/finalized)
  useEffect(() => {
    if (hasStartedReview.current) return;

    const allowedForReview = [
      "Pending Pathologist Review",
      "Pathologist Review",
    ];
    const currentStatusName = caseData?.currentStatus?.name;

    if (
      caseId &&
      userId &&
      caseData &&
      !isDiagnosticReadOnly &&
      !lockData &&
      !isLockedByOther &&
      currentStatusName &&
      allowedForReview.includes(currentStatusName)
    ) {
      hasStartedReview.current = true;
      startReviewMutation.mutate(caseId, {
        onError: (error: any) => {
          if (handleStalePathologistAssignment(error)) return;
          hasStartedReview.current = false;
          notifications.show({
            title: "Unable to Start Review",
            message:
              getUserFriendlyErrorMessage(error) ||
              "Unable to start the review. Please try again.",
            color: "red",
          });
          handleReturnToQueue();
        },
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [caseId, userId, caseData, lockData, isLockedByOther]);

  // Extend lock every 5 minutes
  useEffect(() => {
    if (!caseId || !userId || !lockData || isLockedByOther) return;

    const interval = setInterval(() => {
      extendLockMutation.mutate(caseId);
    }, 2 * 60 * 1000);

    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [caseId, userId, lockData, isLockedByOther]);

  // Page-level keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Don't trigger shortcuts when typing in inputs (except for specific shortcuts)
      const target = e.target as HTMLElement;
      const isInputField =
        target.tagName === "INPUT" ||
        target.tagName === "TEXTAREA" ||
        target.isContentEditable;

      // Ctrl+S or Cmd+S - Save draft (works even in input fields)
      if ((e.ctrlKey || e.metaKey) && e.key === "s") {
        e.preventDefault();
        if (caseData && !isDiagnosticReadOnly) {
          handleSaveDraft();
        }
        return;
      }

      // Ctrl+O or Cmd+O - Preview report (works even in input fields)
      if ((e.ctrlKey || e.metaKey) && e.key === "o") {
        e.preventDefault();
        if (caseData && !isDiagnosticReadOnly) {
          handlePreviewReport();
        }
        return;
      }

      // F2 - Sign out case (works even in input fields)
      if (e.key === "F2") {
        e.preventDefault();
        if (caseData && !isDiagnosticReadOnly) {
          handleSignOut();
        }
        return;
      }

      // F3 - Open macro search (works even in input fields)
      if (e.key === "F3") {
        e.preventDefault();
        openGridMacroSearch();
        return;
      }

      // Ctrl+Enter - Save and sign out (works even in input fields)
      if ((e.ctrlKey || e.metaKey) && e.key === "Enter") {
        e.preventDefault();
        if (caseData && !isDiagnosticReadOnly) {
          // handleSignOut now saves draft internally before sign-off
          handleSignOut();
        }
        return;
      }

      // Don't process other shortcuts if in input field
      if (isInputField) return;

      // Get sorted specimen IDs for navigation
      const specimenIds: string[] =
        caseData?.specimens
          ?.slice()
          .sort((a: any, b: any) =>
            (a.number || "").localeCompare(b.number || "")
          )
          .map((s: any) => s.id as string) || [];

      // Helper to get current index
      const getCurrentIndex = (): number => {
        const expandedArray = Array.from(expandedSpecimens);
        if (expandedArray.length === 0) return -1;
        return specimenIds.indexOf(expandedArray[expandedArray.length - 1]);
      };

      // ↑ Arrow Up - Navigate to previous specimen
      if (e.key === "ArrowUp") {
        if (specimenIds.length === 0) return;
        e.preventDefault();
        const currentIndex = getCurrentIndex();
        const prevIndex: number =
          currentIndex > 0 ? currentIndex - 1 : specimenIds.length - 1;
        setExpandedSpecimens(new Set([specimenIds[prevIndex]]));
        // Scroll the specimen into view
        setTimeout(() => {
          const element = document.getElementById(
            `specimen-${specimenIds[prevIndex]}`
          );
          if (element) {
            element.scrollIntoView({ behavior: "smooth", block: "center" });
          }
        }, 50);
        return;
      }

      // ↓ Arrow Down - Navigate to next specimen
      if (e.key === "ArrowDown") {
        if (specimenIds.length === 0) return;
        e.preventDefault();
        const currentIndex = getCurrentIndex();
        const nextIndex: number =
          currentIndex < specimenIds.length - 1 ? currentIndex + 1 : 0;
        setExpandedSpecimens(new Set([specimenIds[nextIndex]]));
        // Scroll the specimen into view
        setTimeout(() => {
          const element = document.getElementById(
            `specimen-${specimenIds[nextIndex]}`
          );
          if (element) {
            element.scrollIntoView({ behavior: "smooth", block: "center" });
          }
        }, 50);
        return;
      }

      // Tab/Shift+Tab - Navigate between specimens (when not in input)
      if (e.key === "Tab" && !e.ctrlKey && !e.metaKey) {
        if (specimenIds.length === 0) return;
        const currentIndex = getCurrentIndex();

        if (e.shiftKey) {
          // Navigate to previous specimen
          const prevIndex: number =
            currentIndex > 0 ? currentIndex - 1 : specimenIds.length - 1;
          setExpandedSpecimens(new Set([specimenIds[prevIndex]]));
        } else {
          // Navigate to next specimen
          const nextIndex: number =
            currentIndex < specimenIds.length - 1 ? currentIndex + 1 : 0;
          setExpandedSpecimens(new Set([specimenIds[nextIndex]]));
        }
        e.preventDefault();
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [caseData, expandedSpecimens, diagnosisDataMap]);

  // Auto-expand first specimen only (on initial load)
  useEffect(() => {
    if (!caseData?.specimens) return;

    // Only initialize once to prevent resetting user's selection on refetch
    if (hasInitializedExpanded.current) return;
    hasInitializedExpanded.current = true;

    const sortedSpecimens = [...caseData.specimens].sort((a: any, b: any) =>
      (a.number || "").localeCompare(b.number || "")
    );
    // Only select the first specimen initially
    const firstSpecimen = sortedSpecimens[0];
    if (firstSpecimen) {
      setExpandedSpecimens(new Set([firstSpecimen.id]));
    }
  }, [caseData]);

  // Release lock on unmount
  useEffect(() => {
    return () => {
      if (caseId) {
        releaseLockMutation.mutate(caseId);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [caseId]);

  const handleLeave = async () => {
    if (amendmentDraft) {
      try {
        await saveDraftAsync();
      } catch {
        notifications.show({
          title: "Draft not saved",
          message:
            "Your changes could not be saved. Please try again before leaving.",
          color: "red",
        });
        return;
      }
      if (caseId) releaseLockMutation.mutate(caseId);
      handleReturnToQueue();
      return;
    }
    if (caseId && caseData && !isDiagnosticReadOnly) {
      // Fire immediate background save for diagnosis/grossing data
      const diagnoses = Array.from(diagnosisDataMap.values()).map((d) => ({
        specimenId: d.specimenId,
        macroCode: d.macroCode || null,
        diagnosisText: d.diagnosisText ?? "",
        microscopicDescription: d.microscopicDescription ?? "",
        location: d.location ?? "",
        category: d.category ?? "",
        cptCode: d.cptCode ?? "",
        icdCode: d.icdCode,
        icdCodes: d.icdCodes,
        color: d.color ?? "",
        sizeCm: d.sizeCm ?? "",
        sizeMm: d.sizeMm ?? "",
        consistency: d.consistency ?? "",
      }));
      const grossing = buildGrossingDraftEntries(grossingDataMap);
      if (diagnoses.length > 0 || grossing.length > 0) {
        const draftPayload = buildPathologistReviewDraftPayload({
          diagnoses,
          grossing,
          specimens: caseData.specimens || [],
          isPartiallyFinalized: Boolean(caseData.isPartiallyFinalized),
          frozenInterpretation: caseFrozenInterpretation,
        });
        saveDraftMutation.mutate({ caseId, ...draftPayload });
      }

      // Fire immediate background save for document fields
      if (documentClinicalHistoryInitialized.current) {
        const updates: Record<string, string> = {};
        if (documentClinicalHistory !== (caseData.clinicalHistoryNotes || "")) {
          updates.clinicalHistoryNotes = documentClinicalHistory;
        }
        if (documentIndications !== (caseData.indications || "")) {
          updates.indications = documentIndications;
        }
        if (documentImpressions !== (caseData.impressions || "")) {
          updates.impressions = documentImpressions;
        }
        if (Object.keys(updates).length > 0) {
          documentViewService
            .updateClinicalHistory(caseId, updates)
            .catch(() => {
              // Silently fail — navigating away regardless
            });
        }
      }
    }

    if (caseId) {
      releaseLockMutation.mutate(caseId);
    }
    handleReturnToQueue();
  };

  // Loading state
  if (caseLoading) {
    return (
      <Box p="xl">
        <Group justify="center" p="xl">
          <Loader />
          <Text>Loading case...</Text>
        </Group>
      </Box>
    );
  }

  // Error state
  if (caseError || !caseData) {
    const errorMessage = getUserFriendlyErrorMessage(caseError);
    const isBusinessRule = errorMessage.includes(
      "does not require pathologist review"
    );

    return (
      <Box p="xl">
        <Alert
          icon={
            isBusinessRule ? (
              <IconAlertTriangle size={16} />
            ) : (
              <IconAlertCircle size={16} />
            )
          }
          color={isBusinessRule ? "orange" : "red"}
          title={isBusinessRule ? "Review Not Required" : "Error"}
        >
          {errorMessage}
        </Alert>
        <Button
          mt="md"
          onClick={handleReturnToQueue}
          leftSection={<IconArrowLeft size={16} />}
        >
          Back to Queue
        </Button>
      </Box>
    );
  }

  // Locked by another user state
  if (isLockedByOther) {
    return (
      <Box p="xl">
        <Alert
          icon={<IconAlertCircle size={16} />}
          color="orange"
          title="Case Locked"
        >
          This case is currently being reviewed by{" "}
          <strong>{lockData?.lockedByName || "another user"}</strong>. Please
          try again later or choose a different case from the queue.
        </Alert>
        <Group mt="md">
          <Button
            onClick={handleReturnToQueue}
            leftSection={<IconArrowLeft size={16} />}
          >
            Back to Queue
          </Button>
          {canForceRelease && (
            <Button
              color="red"
              variant="light"
              leftSection={<IconLockOpen size={16} />}
              onClick={handleForceUnlock}
              loading={forceReleaseMutation.isPending}
            >
              Force Unlock
            </Button>
          )}
        </Group>
      </Box>
    );
  }

  // Sort specimens alphabetically
  const specimens = [...(caseData.specimens || [])].sort((a: any, b: any) =>
    (a.number || "").localeCompare(b.number || "")
  );

  return (
    <>
      <style>{`
        .hide-scrollbar {
          scrollbar-width: none;
          -ms-overflow-style: none;
        }
        .hide-scrollbar::-webkit-scrollbar {
          display: none;
        }
      `}</style>

      {/* Header Bar */}
      <Box
        style={{
          borderBottom: "1px solid var(--mantine-color-gray-3)",
          backgroundColor: "white",
        }}
        py={6}
        px="md"
      >
        <Group justify="space-between" gap="xs" wrap="nowrap">
          {/* Left: Priority badges */}
          <Group gap="xs" wrap="nowrap" style={{ flexShrink: 0 }}>
            {stalePathologistAssignment && (
              <StalePathologistAssignmentIndicator
                assignment={stalePathologistAssignment}
              />
            )}
            {caseData.isHighPriority && (
              <Tooltip
                label={
                  caseData.highPriorityReason ||
                  "High priority — flagged for attention"
                }
                multiline
                maw={300}
              >
                <Badge size="sm" color="red" variant="filled" radius="sm">
                  HIGH PRIORITY
                </Badge>
              </Tooltip>
            )}
            {caseData.isRush && (
              <Tooltip
                label={
                  caseData.rushReason || "Rush case — expedited processing"
                }
                multiline
                maw={300}
              >
                <Badge size="sm" color="orange" variant="filled" radius="sm">
                  RUSH
                </Badge>
              </Tooltip>
            )}
            <CaseStatusBadge
              statusName={caseData.currentStatus?.name || "Unknown"}
              isBlocked={caseData.isBlocked}
            />
            {headerReportDeliveryState && (
              <ReportDeliveryStateBadge state={headerReportDeliveryState} />
            )}
            {caseId && userId && (
              <AssignmentHistoryTransferContext
                caseId={caseId}
                pathologistId={userId}
                recordedWorkGuidance="Recorded case work saved before this reassignment remains available. Changes that were not saved before the transfer stay only in the prior pathologist's session."
              />
            )}
            {hasActiveSendOuts && (
              <Tooltip
                label={activeSendOuts
                  .map(
                    (so) =>
                      `${so.externalInstitution?.name || "Unknown"} — ${
                        so.status === "pending_approval"
                          ? "Pending Approval"
                          : so.status === "pending_shipping"
                          ? "Pending Shipping"
                          : so.status === "shipped"
                          ? "Shipped"
                          : so.status === "received_partially"
                          ? "Partially Received"
                          : so.status
                      }`
                  )
                  .join("\n")}
                multiline
                maw={400}
              >
                <Badge
                  size="sm"
                  color="violet"
                  variant="filled"
                  radius="sm"
                  leftSection={<IconExternalLink size={10} />}
                >
                  {activeSendOuts.length === 1
                    ? `EXT. OPINION — ${
                        activeSendOuts[0].externalInstitution?.shortName ||
                        activeSendOuts[0].externalInstitution?.name ||
                        "Pending"
                      }`
                    : `${activeSendOuts.length} EXT. OPINIONS PENDING`}
                </Badge>
              </Tooltip>
            )}
          </Group>

          {/* Center: Case identity — the most prominent element on the page */}
          <Group
            gap="xs"
            justify="center"
            wrap="nowrap"
            style={{ flex: 1, minWidth: 0, overflow: "hidden" }}
          >
            <Text
              size="xl"
              fw={800}
              c="yellow.7"
              style={{
                lineHeight: 1.1,
                letterSpacing: "-0.01em",
                whiteSpace: "nowrap",
              }}
            >
              {liveCaseData.caseNumber || "—"}
            </Text>
            {liveCaseData.patient && (
              <>
                <Text size="xl" c="dimmed" style={{ lineHeight: 1.1 }}>
                  •
                </Text>
                <Text
                  size="xl"
                  fw={700}
                  style={{ lineHeight: 1.1, whiteSpace: "nowrap" }}
                >
                  {[
                    liveCaseData.patient.lastName,
                    liveCaseData.patient.firstName,
                  ]
                    .filter(Boolean)
                    .join(", ")}
                </Text>
                {liveCaseData.patient.dateOfBirth && (
                  <>
                    <Text size="xl" c="dimmed" style={{ lineHeight: 1.1 }}>
                      •
                    </Text>
                    <Text
                      size="xl"
                      c="dimmed"
                      style={{ lineHeight: 1.1, whiteSpace: "nowrap" }}
                    >
                      DOB:{" "}
                      {formatDateOnlyForDisplay(
                        requireDateOnlyString(
                          liveCaseData.patient.dateOfBirth,
                          "Patient date of birth"
                        )
                      )}
                      {livePatientAge !== null ? ` (${livePatientAge}Y)` : ""}
                    </Text>
                  </>
                )}
              </>
            )}
          </Group>

          {/* Right: Actions + Preferences */}
          <Group gap={6} wrap="nowrap" style={{ flexShrink: 0 }}>
            <Button
              size="xs"
              variant={controlSummary?.pendingResultCount ? "light" : "subtle"}
              color={controlSummary?.pendingResultCount ? "orange" : "gray"}
              leftSection={<IconFlask size={13} />}
              onClick={() =>
                navigate("/distribution/control-slides", {
                  state: { from: `/pathologist-review/${caseId}` },
                })
              }
            >
              Control Slides
              {(controlSummary?.pendingResultCount ?? 0) > 0 && (
                <Badge size="xs" color="orange" variant="filled" ml={4}>
                  {controlSummary!.pendingResultCount}
                </Badge>
              )}
            </Button>

            <Button
              size="xs"
              variant="light"
              color="grape"
              leftSection={<IconMessageCircle size={13} />}
              onClick={
                hasActiveDiscussion
                  ? () =>
                      navigate(
                        activeDiscussion?.id
                          ? `/discussion-queue/${caseId}/${activeDiscussion.id}`
                          : `/discussion-queue/${caseId}`
                      )
                  : openFlagDiscussionModal
              }
            >
              {hasActiveDiscussion ? "Go to Discussion" : "Flag for Discussion"}
            </Button>

            <Button
              size="xs"
              variant={hasActiveSendOuts ? "filled" : "light"}
              color={hasActiveSendOuts ? "violet" : "orange"}
              leftSection={<IconExternalLink size={13} />}
              onClick={
                hasActiveSendOuts
                  ? openDuplicateConfirm
                  : openExternalOpinionModal
              }
            >
              {hasActiveSendOuts
                ? `External Opinion (${activeSendOuts.length})`
                : "External Opinion"}
            </Button>

            <Tooltip label="Preferences">
              <ActionIcon
                size="sm"
                variant="subtle"
                color="gray"
                onClick={openReviewPrefs}
              >
                <IconSettings size={15} />
              </ActionIcon>
            </Tooltip>
          </Group>
        </Group>
      </Box>

      <Grid
        gutter="md"
        style={{
          margin: "0 auto",
          maxWidth: "2200px",
          height: "calc(100vh - 112px)",
          overflow: "hidden",
        }}
      >
        {/* LEFT COLUMN: Navigation */}
        <Grid.Col
          span={{ base: 12, lg: "content" }}
          style={{ width: 210, minWidth: 210, maxWidth: 210 }}
        >
          <ScrollArea
            style={{
              height: "calc(100vh - 112px)",
              borderRight: "1px solid var(--mantine-color-gray-3)",
              width: "100%",
            }}
          >
            <Stack gap={0} py="xs">
              {/* Results — expandable */}
              <NavLink
                label={
                  <Group justify="space-between" wrap="nowrap" gap="xs">
                    <Text
                      size="sm"
                      fw={
                        activeNavItem === "grid" || activeNavItem === "report"
                          ? 600
                          : 400
                      }
                    >
                      Results
                    </Text>
                    {(activeNavItem === "grid" ||
                      activeNavItem === "report") && (
                      <Box
                        style={{
                          width: 7,
                          height: 7,
                          borderRadius: "50%",
                          backgroundColor: "var(--mantine-color-blue-6)",
                          flexShrink: 0,
                        }}
                      />
                    )}
                  </Group>
                }
                leftSection={<IconLayoutGrid size={16} />}
                rightSection={
                  reviewExpanded ? (
                    <IconChevronUp
                      size={14}
                      color="var(--mantine-color-dimmed)"
                    />
                  ) : (
                    <IconChevronDown
                      size={14}
                      color="var(--mantine-color-dimmed)"
                    />
                  )
                }
                active={activeNavItem === "grid" || activeNavItem === "report"}
                onClick={() => {
                  setReviewExpanded((e) => !e);
                  setActiveNavItem(
                    reviewPreferences.editingMode === "document"
                      ? "report"
                      : "grid"
                  );
                }}
                style={{ borderRadius: 0, whiteSpace: "nowrap" }}
              />
              <Collapse in={reviewExpanded}>
                {reviewPreferences.editingMode === "document" ? (
                  <>
                    <NavLink
                      label="Report"
                      pl="xl"
                      active={activeNavItem === "report"}
                      onClick={() => setActiveNavItem("report")}
                      style={{ borderRadius: 0, whiteSpace: "nowrap" }}
                      styles={{
                        label: {
                          fontSize: "var(--mantine-font-size-xs)",
                        },
                      }}
                    />
                    <NavLink
                      label="Table"
                      pl="xl"
                      active={activeNavItem === "grid"}
                      onClick={() => setActiveNavItem("grid")}
                      style={{ borderRadius: 0, whiteSpace: "nowrap" }}
                      styles={{
                        label: {
                          fontSize: "var(--mantine-font-size-xs)",
                        },
                      }}
                    />
                  </>
                ) : (
                  <>
                    <NavLink
                      label="Table"
                      pl="xl"
                      active={activeNavItem === "grid"}
                      onClick={() => setActiveNavItem("grid")}
                      style={{ borderRadius: 0, whiteSpace: "nowrap" }}
                      styles={{
                        label: {
                          fontSize: "var(--mantine-font-size-xs)",
                        },
                      }}
                    />
                    <NavLink
                      label="Report"
                      pl="xl"
                      active={activeNavItem === "report"}
                      onClick={() => setActiveNavItem("report")}
                      style={{ borderRadius: 0, whiteSpace: "nowrap" }}
                      styles={{
                        label: {
                          fontSize: "var(--mantine-font-size-xs)",
                        },
                      }}
                    />
                  </>
                )}
              </Collapse>

              {/* Requisition */}
              <NavLink
                label="Requisition"
                leftSection={<IconAddressBook size={16} />}
                active={activeNavItem === "requisition"}
                onClick={() => setActiveNavItem("requisition")}
                style={{ borderRadius: 0, whiteSpace: "nowrap" }}
              />

              {/* Grossing */}
              <NavLink
                label="Grossing"
                leftSection={<IconScissors size={16} />}
                active={activeNavItem === "grossing"}
                onClick={() => setActiveNavItem("grossing")}
                style={{ borderRadius: 0, whiteSpace: "nowrap" }}
              />

              {/* Stains — badge shows pending stains count */}
              <NavLink
                label="Stains"
                leftSection={<IconFlask size={16} />}
                rightSection={
                  pendingStains.length > 0 ? (
                    <Badge size="xs" color="orange" variant="filled">
                      {pendingStains.length}
                    </Badge>
                  ) : undefined
                }
                active={activeNavItem === "stains"}
                onClick={() => setActiveNavItem("stains")}
                style={{ borderRadius: 0, whiteSpace: "nowrap" }}
              />

              {/* Discussions — badges show pending + resolved counts */}
              <NavLink
                label="Discussions"
                leftSection={<IconMessageCircle size={16} />}
                rightSection={
                  pendingDiscussionsCount > 0 ||
                  resolvedDiscussionsCount > 0 ? (
                    <Group gap={4}>
                      {pendingDiscussionsCount > 0 && (
                        <Badge size="xs" color="yellow" variant="filled" circle>
                          {pendingDiscussionsCount}
                        </Badge>
                      )}
                      {resolvedDiscussionsCount > 0 && (
                        <Badge size="xs" color="green" variant="filled" circle>
                          {resolvedDiscussionsCount}
                        </Badge>
                      )}
                    </Group>
                  ) : undefined
                }
                active={activeNavItem === "discussions"}
                onClick={() => setActiveNavItem("discussions")}
                style={{ borderRadius: 0, whiteSpace: "nowrap" }}
              />

              {/* External Opinion */}
              <NavLink
                label="External Opinion"
                leftSection={<IconExternalLink size={16} />}
                rightSection={
                  hasActiveSendOuts ? (
                    <Badge size="xs" color="violet" variant="filled">
                      {activeSendOuts.length}
                    </Badge>
                  ) : undefined
                }
                active={activeNavItem === "external-opinion"}
                onClick={() => setActiveNavItem("external-opinion")}
                style={{ borderRadius: 0, whiteSpace: "nowrap" }}
              />

              {/* Documents */}
              <NavLink
                label="Documents"
                leftSection={<IconFileText size={16} />}
                rightSection={
                  savedCaseFiles.length + caseImages.length > 0 ? (
                    <Badge size="xs" color="gray" variant="light">
                      {savedCaseFiles.length + caseImages.length}
                    </Badge>
                  ) : undefined
                }
                active={activeNavItem === "documents"}
                onClick={() => setActiveNavItem("documents")}
                style={{ borderRadius: 0, whiteSpace: "nowrap" }}
              />

              <Divider my="xs" mx="md" />

              {/* Notes */}
              <NavLink
                label="Notes"
                leftSection={<IconNotes size={16} />}
                active={activeNavItem === "notes"}
                onClick={() => setActiveNavItem("notes")}
                style={{ borderRadius: 0, whiteSpace: "nowrap" }}
              />

              {/* Comments */}
              <NavLink
                label="Comments"
                leftSection={<IconMessage size={16} />}
                active={activeNavItem === "comments"}
                onClick={() => setActiveNavItem("comments")}
                style={{ borderRadius: 0, whiteSpace: "nowrap" }}
              />

              {/* Errors */}
              <NavLink
                label="Errors"
                leftSection={<IconCircleOff size={16} />}
                rightSection={
                  pendingErrorsCount > 0 ? (
                    <Badge size="xs" color="red" variant="filled">
                      {pendingErrorsCount}
                    </Badge>
                  ) : undefined
                }
                active={activeNavItem === "errors"}
                onClick={() => setActiveNavItem("errors")}
                style={{ borderRadius: 0, whiteSpace: "nowrap" }}
              />

              {/* Corrections */}
              <NavLink
                label="Corrections"
                leftSection={<IconReplace size={16} />}
                rightSection={
                  pendingCorrectionsCount > 0 ? (
                    <Badge size="xs" color="orange" variant="filled">
                      {pendingCorrectionsCount}
                    </Badge>
                  ) : undefined
                }
                active={activeNavItem === "corrections"}
                onClick={() => setActiveNavItem("corrections")}
                style={{ borderRadius: 0, whiteSpace: "nowrap" }}
              />

              {/* Case Queue */}
              {reviewPreferences.showQueueInSidebar && (
                <>
                  <Divider my="xs" mx="md" />
                  <Box px="md" pb="xs">
                    <Text size="xs" fw={500} c="dimmed" mb={6}>
                      Case Queue
                    </Text>
                    <CaseQueueWidget
                      currentCaseId={caseId || ""}
                      queueCases={queueCases}
                    />
                  </Box>
                </>
              )}
            </Stack>
          </ScrollArea>
        </Grid.Col>

        {/* MIDDLE COLUMN: Diagnosis Entry + Fixed Footer */}
        <Grid.Col
          span={{ base: 12, lg: "auto" }}
          style={{ flex: 1, minWidth: 0, overflow: "hidden" }}
        >
          <Box
            style={{
              height: "calc(100vh - 112px)",
              display: "flex",
              flexDirection: "column",
            }}
          >
            {/* Signed Off Case Alert — visible on all tabs */}
            {caseData.isFinalized &&
              !amendmentDraft &&
              !caseData.isBlocked &&
              headerReportDeliveryState !== "delivered" && (
                <Box mx="xs" mt="md">
                  <SignedOffDeliveryAlert
                    state={displayedReportRecallState}
                    checking={isReportRecallChecking}
                    unknownOutcome={isReportRecallOutcomeUnknown}
                    stateUnavailable={!!reportRecallStateError}
                    onRefreshState={handleRefreshReportRecallState}
                    deliveryDetailsAction={
                      hasReportDeliveryHistory &&
                      displayedReportRecallState &&
                      !headerReportDeliveryState ? (
                        <ReportDeliveryStateBadge
                          state={displayedReportRecallState.aggregateState}
                          onClick={openReportDeliveryHistory}
                          actionLabel="View report delivery and recall history"
                        />
                      ) : undefined
                    }
                  />
                </Box>
              )}

            {/* Blocked Case Alert — visible on all tabs except errors */}
            {caseData.isBlocked && activeNavItem !== "errors" && (
              <Alert
                icon={<IconAlertTriangle size={16} />}
                color="red"
                title="Case Blocked"
                mx="xs"
                mt="md"
                style={{ width: "75%", maxWidth: 500 }}
              >
                <Text size="sm">
                  This case has been blocked due to a reported error. Diagnosis
                  entry is disabled until the error is resolved.
                </Text>
              </Alert>
            )}

            {/* Scrollable Content Area — switches based on active nav item */}
            {activeNavItem === "grid" ? (
              <Box
                style={{ flex: 1, overflowY: "auto", paddingBottom: "16px" }}
                px="xs"
                py="md"
              >
                <Stack gap="md">
                  {/* Diagnosis Entry Grid */}
                  <DiagnosisEntryGrid
                    specimens={specimens}
                    diagnosisDataMap={diagnosisDataMap}
                    onDiagnosisChange={updateDiagnosisData}
                    onOpenMacroSearch={() => {
                      openGridMacroSearch();
                    }}
                    onOpenMacroSearchBar={openGridMacroSearch}
                    diagnosisMacros={diagnosisMacros}
                    categoryOptions={categoryOptions}
                    icdCodeOptions={icdCodeOptions}
                    isReadOnly={isDiagnosticReadOnly}
                    canRequestAdditionalStains={
                      !amendmentDraft && !caseData.isBlocked
                    }
                    canModifyStructure={!amendmentDraft && canModifyStructure}
                    canAddSpecimen={canAddSpecimen}
                    onArchiveSpecimen={
                      !amendmentDraft && canDeleteSpecimen
                        ? handleRequestRemoveSpecimen
                        : undefined
                    }
                    onAddSpecimen={openAddSpecimen}
                    onRecoverSpecimens={openRecoverSpecimens}
                    archivedCount={archivedSpecimens.length}
                    onRequestStains={handleRequestStainsForSpecimen}
                    caseInfo={{
                      caseNumber: caseData.caseNumber ?? null,
                      patientName: caseData.patient
                        ? [
                            caseData.patient.firstName,
                            caseData.patient.lastName,
                          ]
                            .filter(Boolean)
                            .join(" ") || null
                        : null,
                      dateCollected: caseData.dateCollected
                        ? (
                            parseDateString(caseData.dateCollected as string) ||
                            new Date()
                          ).toLocaleDateString()
                        : null,
                    }}
                  />

                  {/* Case Level Section — Pathologist Comments + Case-Level Frozen Interpretation */}
                  <CaseLevelSection
                    caseId={caseId || null}
                    frozenInterpretation={caseFrozenInterpretation}
                    onFrozenInterpretationChange={
                      handleFrozenInterpretationChange
                    }
                    amendmentCommentContext={amendmentCommentContext}
                    isReadOnly={isDiagnosticReadOnly}
                  />

                  {/* Signed report controls remain available after Castle receipt reopens review. */}
                  {hasSignedReportForAddendumSection && (
                    <AddendumSection
                      caseId={caseId || null}
                      isFinalized={hasSignedReportForAddendumSection}
                      requireAddendumFirst={!caseData.isFinalized}
                      reportVersion={caseData.currentReportVersion}
                      onAmendmentCreated={handleAddendumSigned}
                      onAddAddendum={openAddendumEditor}
                      onAddAmendment={openAmendmentEditor}
                      onContinueAmendment={handleContinueAmendment}
                      caseData={caseData}
                    />
                  )}
                </Stack>
              </Box>
            ) : activeNavItem === "notes" ? (
              <Box style={{ flex: 1, overflowY: "auto" }} p="md">
                <Box style={{ maxWidth: 800, margin: "0 auto" }}>
                  <NotesCenter caseId={caseId || null} />
                </Box>
              </Box>
            ) : activeNavItem === "comments" ? (
              <Box style={{ flex: 1, overflowY: "auto" }} p="md">
                <Box style={{ maxWidth: 800, margin: "0 auto" }}>
                  <CommentsCenter caseId={caseId || null} />
                </Box>
              </Box>
            ) : activeNavItem === "errors" ? (
              <Box style={{ flex: 1, overflowY: "auto" }} p="md">
                <Box style={{ maxWidth: 800, margin: "0 auto" }}>
                  <ErrorsCenter caseId={caseId || null} />
                </Box>
              </Box>
            ) : activeNavItem === "stains" ? (
              <Box
                style={{
                  flex: 1,
                  overflowY: "auto",
                  overflowX: "auto",
                  minWidth: 0,
                }}
                p="md"
              >
                <StainsCenter
                  caseId={caseId || null}
                  caseData={caseData}
                  onRequestStains={handleRequestStains}
                  isReadOnly={isReadOnly}
                />
              </Box>
            ) : activeNavItem === "discussions" ? (
              <Box style={{ flex: 1, overflowY: "auto" }} p="md">
                <Box style={{ maxWidth: 800, margin: "0 auto" }}>
                  <DiscussionsCenter
                    caseId={caseId || null}
                    caseNumber={caseData?.caseNumber}
                  />
                </Box>
              </Box>
            ) : activeNavItem === "external-opinion" ? (
              <Box style={{ flex: 1, overflowY: "auto" }} p="md">
                <Box style={{ maxWidth: 800, margin: "0 auto" }}>
                  <ExternalOpinionCenter
                    caseId={caseId || null}
                    onRequestExternalOpinion={
                      hasActiveSendOuts
                        ? openDuplicateConfirm
                        : handleOpenExternalOpinionModal
                    }
                    isReadOnly={isReadOnly}
                  />
                </Box>
              </Box>
            ) : activeNavItem === "grossing" ? (
              <Box style={{ flex: 1, overflowY: "auto" }} p="md">
                <GrossingCenter
                  caseData={caseData}
                  isReadOnly={isDiagnosticReadOnly}
                  grossingDataMap={grossingDataMap}
                  onGrossingChange={updateGrossingData}
                  grossCodes={grossCodes}
                  canModifyStructure={!amendmentDraft && canModifyStructure}
                  canAddSpecimen={canAddSpecimen}
                  onArchiveSpecimen={
                    !amendmentDraft && canDeleteSpecimen
                      ? handleRequestRemoveSpecimen
                      : undefined
                  }
                  onAddSpecimen={openAddSpecimen}
                  onRecoverSpecimens={openRecoverSpecimens}
                  archivedCount={archivedSpecimens.length}
                />
              </Box>
            ) : activeNavItem === "requisition" ? (
              <Box style={{ flex: 1, overflowY: "auto" }} p="md">
                <RequisitionCenter
                  ref={requisitionRef}
                  caseData={caseData}
                  isReadOnly={isReadOnly}
                  onSave={() => refetchCase()}
                  onAutoSaveStatusChange={setAutoSaveStatus}
                  onFormValuesChange={setLiveRequisitionData}
                />
              </Box>
            ) : activeNavItem === "documents" ? (
              <Box style={{ flex: 1, overflowY: "auto" }}>
                <DocumentsImagesCenter
                  caseId={caseId || null}
                  isReadOnly={isReadOnly}
                  isFinalized={hasSignedReportForAddendumSection}
                  allowSignedPhotoAddition={canAddPhotos}
                  savedCaseFiles={savedCaseFiles}
                  caseFilesLoading={caseFilesLoading}
                  uploadedDocFiles={uploadedDocFiles}
                  onDocFilesChange={handleDocFilesChange}
                  onFileDeleted={(fileId) =>
                    setSavedCaseFiles((prev) =>
                      prev.filter((f) => f.id !== fileId)
                    )
                  }
                  onFileIncludeInReportToggled={(fileId, value) =>
                    setSavedCaseFiles((prev) =>
                      prev.map((f) =>
                        f.id === fileId ? { ...f, includeInReport: value } : f
                      )
                    )
                  }
                  onFileIncludeInReportChange={handleToggleFileInReport}
                  onFileUpdated={(updated) =>
                    setSavedCaseFiles((prev) =>
                      prev.map((f) => (f.id === updated.id ? updated : f))
                    )
                  }
                  onFileCaptionChange={handleFileCaptionChange}
                  onImageCaptionChange={handleImageCaptionChange}
                  onImageIncludeInReportChange={handleToggleImageInReport}
                  caseImages={caseImages}
                  caseImagesLoading={caseImagesLoading}
                  imageDownloadUrls={imageDownloadUrls}
                  onDownloadFile={handleDownloadCaseFile}
                  onImagesReload={handleReportImagesChanged}
                  onOpenImageEdit={handleOpenImageEdit}
                  onDeleteImage={(imageId) => setDeleteImageId(imageId)}
                  onOpenCameraCapture={() => setCameraCaptureModalOpened(true)}
                  pathologistReviewServiceGetPresignedUrl={
                    pathologistReviewService.getPresignedUrl
                  }
                />
              </Box>
            ) : activeNavItem === "corrections" ? (
              <Box style={{ flex: 1, overflowY: "auto" }} p="md">
                <Box style={{ maxWidth: 800, margin: "0 auto" }}>
                  <CorrectionsCenter
                    caseId={caseId || ""}
                    caseData={caseData}
                  />
                </Box>
              </Box>
            ) : activeNavItem === "report" ? (
              <Box
                style={{
                  flex: 1,
                  overflowY: "auto",
                  position: "relative",
                  backgroundColor: "white",
                }}
              >
                <Group
                  justify="flex-end"
                  px="md"
                  py={6}
                  style={{
                    borderBottom: "1px solid var(--mantine-color-gray-3)",
                    position: "sticky",
                    top: 0,
                    zIndex: 10,
                    backgroundColor: "var(--mantine-color-body)",
                  }}
                >
                  {activeTemplates.length > 0 && (
                    <Group gap="xs">
                      <Text size="xs" fw={500} c="dimmed">
                        Template:
                      </Text>
                      <Select
                        size="xs"
                        data={activeTemplates.map((t: any) => ({
                          value: t.id,
                          label: t.name,
                        }))}
                        value={selectedTemplateId || null}
                        onChange={(val) => setSelectedTemplateId(val || "")}
                        styles={{ input: { fontSize: 12, minHeight: 28 } }}
                        w={220}
                      />
                    </Group>
                  )}
                  {/* Click-only trigger for the macro search modal. A read-only
                      input is still a tab stop, so it is removed from the tab
                      sequence — otherwise tabbing lands in a box that cannot be
                      typed into. F3 opens the same search from anywhere. */}
                  <TextInput
                    placeholder="Search macros..."
                    leftSection={<IconSearch size={14} />}
                    rightSection={<Kbd size="xs">F3</Kbd>}
                    readOnly
                    size="xs"
                    style={{ cursor: "pointer" }}
                    styles={{ input: { cursor: "pointer", fontSize: 12 } }}
                    w={200}
                    onClick={openGridMacroSearch}
                    tabIndex={-1}
                  />
                </Group>
                {caseData && (
                  <DocumentView
                    templateId={selectedTemplateId || undefined}
                    caseData={{
                      id: caseId || "",
                      caseNumber: liveCaseData.caseNumber || "",
                      clinicalHistoryNotes: liveCaseData.clinicalHistoryNotes,
                      indications: liveCaseData.indications,
                      impressions: liveCaseData.impressions,
                      isFinalized: liveCaseData.isFinalized || false,
                      signedOffAt: liveCaseData.signedOffAt,
                      dateReceived: liveCaseData.dateReceived,
                      dateCollected: liveCaseData.dateCollected,
                      physicianId:
                        liveCaseData.physicianId || liveCaseData.physician?.id,
                      sendingPracticeId: liveCaseData.sendingPracticeId,
                      surgeryCenterLabId: liveCaseData.surgeryCenterLabId,
                      patient: liveCaseData.patient
                        ? {
                            firstName: liveCaseData.patient.firstName,
                            lastName: liveCaseData.patient.lastName,
                            dateOfBirth: liveCaseData.patient.dateOfBirth,
                            medicalRecordNumber:
                              liveCaseData.patient.medicalRecordNumber,
                          }
                        : undefined,
                      physician: liveCaseData.physician
                        ? {
                            firstName: liveCaseData.physician.firstName,
                            lastName: liveCaseData.physician.lastName,
                            credentials: liveCaseData.physician.credentials,
                          }
                        : undefined,
                      signedOffBy: liveCaseData.signedOffBy
                        ? {
                            fullName: liveCaseData.signedOffBy.fullName,
                            credentials: liveCaseData.signedOffBy.credentials,
                          }
                        : undefined,
                      procedure:
                        liveCaseData.procedure?.name ||
                        liveCaseData.procedureName ||
                        undefined,
                    }}
                    specimens={liveReportSpecimens}
                    diagnosisDataMap={
                      new Map(
                        Array.from(diagnosisDataMap.entries()).map(
                          ([id, d]) => [
                            id,
                            {
                              specimenId: d.specimenId,
                              macroCode: d.macroCode || "",
                              diagnosisText: d.diagnosisText || "",
                              microscopicDescription:
                                d.microscopicDescription || "",
                              location: d.location,
                              category: d.category,
                              cptCode: d.cptCode,
                              icdCode: d.icdCode,
                              icdCodes: d.icdCodes,
                              color: d.color,
                              sizeCm: d.sizeCm,
                            },
                          ]
                        )
                      )
                    }
                    grossingDataMap={
                      new Map(
                        Array.from(grossingDataMap.entries()).map(([id, g]) => [
                          id,
                          {
                            specimenId: g.specimenId,
                            grossCode: g.grossCode || "",
                            grossCodeId: g.grossCodeId || "",
                            grossDescription: g.grossDescription || "",
                            fragmentCount: g.fragmentCount,
                            fragmentCountQualifier: g.fragmentCountQualifier,
                            variableValues: g.variableValues,
                          },
                        ])
                      )
                    }
                    clinicalHistoryNotes={documentClinicalHistory}
                    indications={documentIndications}
                    impressions={documentImpressions}
                    pathologistComment={documentPathologistComment}
                    pathologistComments={pathologistComments}
                    amendmentCommentContext={amendmentCommentContext}
                    frozenInterpretation={caseFrozenInterpretation}
                    isReadOnly={isDiagnosticReadOnly}
                    onClinicalHistoryChange={handleClinicalHistoryChange}
                    onIndicationsChange={handleIndicationsChange}
                    onImpressionsChange={handleImpressionsChange}
                    onCommentChange={handleDocumentCommentChange}
                    onDiagnosisChange={(specimenId, field, value) => {
                      updateDiagnosisData(specimenId, {
                        [field]: value,
                      } as any);
                    }}
                    onGrossChange={(specimenId, value) => {
                      updateGrossingData(specimenId, {
                        grossDescription: value,
                      });
                    }}
                    liveCptDisplay={derivedCptCodes}
                    liveIcdDisplay={derivedIcdCodes}
                    onCodesChange={(type, codes) => {
                      // ICD codes are edited per specimen (grid / widget): a
                      // flat report-level list cannot be mapped back once a
                      // specimen can carry several codes.
                      if (type !== "cpt") return;
                      const sortedSpecs = [...specimens].sort(
                        (a: any, b: any) =>
                          (a.number || "").localeCompare(b.number || "")
                      );
                      sortedSpecs.forEach((s: any, i: number) => {
                        updateDiagnosisData(s.id, {
                          cptCode: codes[i] || null,
                        });
                      });
                    }}
                    onHeaderSaved={() => refetchCase()}
                    macros={diagnosisMacros}
                    onMacroApplied={(specimenId, macro) => {
                      const macroIcdCodes = macro.icdCodes?.length
                        ? macro.icdCodes
                        : macro.icdCode
                        ? [macro.icdCode]
                        : [];
                      updateDiagnosisData(specimenId, {
                        macroCode: macro.code,
                        diagnosisText: macro.diagnosisText || "",
                        microscopicDescription:
                          macro.microscopicDescription || "",
                        category: macro.category || null,
                        cptCode: macro.cptCode || null,
                        icdCode: macroIcdCodes[0] || null,
                        icdCodes: macroIcdCodes,
                      } as any);
                    }}
                    grossCodes={grossCodes}
                    onGrossCodeApplied={(specimenId, data) => {
                      updateGrossingData(specimenId, {
                        grossCode: data.grossCode,
                        grossCodeId: data.grossCodeId,
                        grossDescription: data.grossDescription,
                        fragmentCount: data.fragmentCount,
                        fragmentCountQualifier: data.fragmentCountQualifier,
                      });
                    }}
                    onGrossVariableChange={(specimenId, varName, value) => {
                      const current = grossingDataMap.get(specimenId);
                      updateGrossingData(specimenId, {
                        variableValues: {
                          ...(current?.variableValues || {}),
                          [varName]: value,
                        },
                      });
                    }}
                    subsiteOptionsBySource={subsiteOptionsBySource}
                    diagnosisCategories={diagnosisCategories}
                    onAddAddendum={
                      canCreateAddendum ? handleOpenAddendum : undefined
                    }
                    onAddAmendment={
                      caseData.isFinalized ? handleOpenAmendment : undefined
                    }
                    onAddCorrection={
                      caseData.isFinalized ? handleOpenCorrection : undefined
                    }
                  />
                )}

                {caseData &&
                  (hasReportAttachments ||
                    (!isReadOnly && hasNonReportAttachments)) && (
                    <Box
                      pb="lg"
                      style={{
                        maxWidth: 800,
                        margin: "0 auto",
                        padding: "0 40px 24px",
                      }}
                    >
                      <Divider
                        my="md"
                        label="Attached Documents & Images"
                        labelPosition="center"
                        styles={{
                          label: {
                            fontSize: 11,
                            fontWeight: 600,
                            textTransform: "uppercase",
                            letterSpacing: 0.5,
                            color: "var(--mantine-color-gray-6)",
                          },
                        }}
                      />

                      {reportImages.map((img) => {
                        const url = imageDownloadUrls[img.id];
                        return (
                          <Box
                            key={img.id}
                            mb="md"
                            pos="relative"
                            style={{ textAlign: "center" }}
                          >
                            {!isReadOnly && (
                              <ActionIcon
                                size="md"
                                variant="filled"
                                color="red"
                                radius="xl"
                                pos="absolute"
                                top={8}
                                right={8}
                                style={{ zIndex: 2 }}
                                onClick={() =>
                                  handleToggleImageInReport(img.id, false)
                                }
                              >
                                <IconX size={14} />
                              </ActionIcon>
                            )}
                            {url ? (
                              <Image
                                src={url}
                                alt={
                                  img.caption || img.fileName || "Case image"
                                }
                                fit="contain"
                                style={{ maxHeight: 600 }}
                                radius="sm"
                              />
                            ) : (
                              <Center py="lg">
                                <Loader size="sm" />
                              </Center>
                            )}
                            {(img.caption || img.magnification) && (
                              <Text size="xs" c="dimmed" ta="center" mt={4}>
                                {[img.caption, img.magnification]
                                  .filter(Boolean)
                                  .join(" — ")}
                              </Text>
                            )}
                          </Box>
                        );
                      })}

                      {reportFiles.map((file) => {
                        const url = docDownloadUrls[file.id];
                        const isImage = file.mimeType?.startsWith("image/");
                        const isPdf = file.mimeType === "application/pdf";

                        return (
                          <Box key={file.id} mb="md" pos="relative">
                            {!isReadOnly && (
                              <ActionIcon
                                size="md"
                                variant="filled"
                                color="red"
                                radius="xl"
                                pos="absolute"
                                top={8}
                                right={8}
                                style={{ zIndex: 2 }}
                                onClick={() =>
                                  handleToggleFileInReport(file.id)
                                }
                              >
                                <IconX size={14} />
                              </ActionIcon>
                            )}
                            {isImage && url ? (
                              <Box style={{ textAlign: "center" }}>
                                <Image
                                  src={url}
                                  alt={file.name}
                                  fit="contain"
                                  style={{ maxHeight: 600 }}
                                  radius="sm"
                                />
                                <Text size="xs" c="dimmed" ta="center" mt={4}>
                                  {file.name}
                                </Text>
                              </Box>
                            ) : isPdf && url ? (
                              <PdfPagesRenderer url={url} label={file.name} />
                            ) : (
                              <Group
                                gap="xs"
                                py="sm"
                                px="md"
                                style={{
                                  border:
                                    "1px solid var(--mantine-color-gray-3)",
                                  borderRadius: 4,
                                }}
                              >
                                <IconFile
                                  size={18}
                                  color="var(--mantine-color-gray-5)"
                                />
                                <Text size="sm">
                                  {url ? (
                                    <a
                                      href={url}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                    >
                                      {file.name}
                                    </a>
                                  ) : (
                                    file.name
                                  )}
                                </Text>
                              </Group>
                            )}
                          </Box>
                        );
                      })}

                      {!isReadOnly && (
                        <Popover
                          opened={addAttachmentOpen}
                          onChange={setAddAttachmentOpen}
                          position="top"
                          width={360}
                          shadow="md"
                        >
                          <Popover.Target>
                            <Button
                              variant="light"
                              size="sm"
                              fullWidth
                              leftSection={<IconPlus size={16} />}
                              mt="sm"
                              onClick={() => setAddAttachmentOpen((o) => !o)}
                            >
                              Add Document or Image
                            </Button>
                          </Popover.Target>
                          <Popover.Dropdown>
                            <Stack gap="xs">
                              <Text size="sm" fw={600}>
                                Add to Report
                              </Text>
                              {!hasNonReportAttachments && (
                                <Text size="xs" c="dimmed">
                                  All images and documents are already included.
                                </Text>
                              )}
                              {nonReportImages.map((img) => (
                                <Group
                                  key={img.id}
                                  gap="xs"
                                  justify="space-between"
                                  wrap="nowrap"
                                >
                                  <Text size="xs" truncate style={{ flex: 1 }}>
                                    {img.fileName || "Case image"}
                                  </Text>
                                  <Button
                                    size="compact-xs"
                                    variant="light"
                                    onClick={async () => {
                                      await handleToggleImageInReport(
                                        img.id,
                                        true
                                      );
                                      setAddAttachmentOpen(false);
                                    }}
                                  >
                                    Add
                                  </Button>
                                </Group>
                              ))}
                              {nonReportFiles.map((file) => (
                                <Group
                                  key={file.id}
                                  gap="xs"
                                  justify="space-between"
                                  wrap="nowrap"
                                >
                                  <Text size="xs" truncate style={{ flex: 1 }}>
                                    {file.name}
                                  </Text>
                                  <Button
                                    size="compact-xs"
                                    variant="light"
                                    onClick={async () => {
                                      await handleToggleFileInReport(file.id);
                                      setAddAttachmentOpen(false);
                                    }}
                                  >
                                    Add
                                  </Button>
                                </Group>
                              ))}
                            </Stack>
                          </Popover.Dropdown>
                        </Popover>
                      )}
                    </Box>
                  )}
              </Box>
            ) : (
              <Box
                style={{
                  flex: 1,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <Stack align="center" gap="xs">
                  <Text size="sm" c="dimmed">
                    Select a section from the navigation
                  </Text>
                </Stack>
              </Box>
            )}

            {/* Fixed Footer */}
            <Box
              style={{
                borderTop: "1px solid var(--mantine-color-gray-3)",
                padding: "var(--mantine-spacing-md)",
                backgroundColor: "white",
              }}
            >
              <Stack gap={0}>
                {/* Keyboard shortcuts reference — small, above the action row */}
                <Text
                  size="xs"
                  c="dimmed"
                  ta="center"
                  mb="sm"
                  style={{ fontSize: 10 }}
                >
                  Tab/Enter = Macro • Ctrl+S = Save • Ctrl+O = Preview • F2 =
                  Sign Out • F3 = Macros
                </Text>

                {/* Action row: Back to Queue (left) + primary actions (right) */}
                <Group justify="space-between" wrap="wrap">
                  <Button
                    variant="default"
                    leftSection={<IconArrowLeft size={16} />}
                    onClick={handleLeave}
                  >
                    Back to Queue
                  </Button>

                  {caseData?.isBlocked ? (
                    <Button
                      variant="light"
                      leftSection={<IconEye size={16} />}
                      onClick={handlePreviewReport}
                    >
                      Preview Report
                    </Button>
                  ) : (
                    <Group gap="sm" wrap="wrap" justify="flex-end">
                      {/* Auto-save status indicator */}
                      {autoSaveStatus !== "idle" && (
                        <Group gap={4}>
                          {autoSaveStatus === "saving" && (
                            <>
                              <IconCloud
                                size={16}
                                color="var(--mantine-color-blue-5)"
                              />
                              <Text size="xs" c="blue">
                                Saving...
                              </Text>
                            </>
                          )}
                          {autoSaveStatus === "saved" && (
                            <>
                              <IconCloudCheck
                                size={16}
                                color="var(--mantine-color-green-6)"
                              />
                              <Text size="xs" c="green">
                                Auto-saved
                              </Text>
                            </>
                          )}
                          {autoSaveStatus === "error" && (
                            <>
                              <IconCloudOff
                                size={16}
                                color="var(--mantine-color-red-5)"
                              />
                              <Text size="xs" c="red">
                                Save failed
                              </Text>
                            </>
                          )}
                        </Group>
                      )}

                      {caseData?.isFinalized ? (
                        <>
                          {caseId && (
                            <AdditionalStainNoReportChangeAction
                              caseId={caseId}
                              projection={
                                caseData.additionalStainNoReportChange
                              }
                            />
                          )}
                          <Button
                            variant="default"
                            leftSection={<IconEye size={16} />}
                            onClick={handleOpenFinalizedReport}
                            loading={addendumDraftQuery.isLoading}
                          >
                            {addendumDraftQuery.data
                              ? "Preview Report"
                              : addendumDraftQuery.isError
                              ? "Retry Report"
                              : "View Report"}
                          </Button>
                          {reportRecallState?.canRevertSignoff === true && (
                            <Button
                              variant="light"
                              color="orange"
                              leftSection={<IconLockOpen size={16} />}
                              onClick={handleRevertSignoff}
                              loading={isReportReverting}
                            >
                              Revert signoff
                            </Button>
                          )}
                          <Button
                            variant="default"
                            leftSection={<IconFileText size={16} />}
                            onClick={openAmendmentEditor}
                          >
                            Create Amendment
                          </Button>
                          <Menu
                            position="top-end"
                            offset={8}
                            withinPortal
                            shadow="md"
                          >
                            <Menu.Target>
                              <Tooltip label="More actions">
                                <ActionIcon
                                  variant="default"
                                  size="lg"
                                  aria-label="More actions"
                                >
                                  <IconDots size={16} />
                                </ActionIcon>
                              </Tooltip>
                            </Menu.Target>
                            <Menu.Dropdown>
                              {hasInProgressReportDelivery && (
                                <>
                                  <Menu.Item
                                    leftSection={<IconSend size={14} />}
                                    onClick={handleSendReportClick}
                                    disabled={isSendingReport}
                                  >
                                    Send Report
                                  </Menu.Item>
                                  <Menu.Divider />
                                </>
                              )}
                              <Menu.Item
                                leftSection={<IconHistory size={14} />}
                                onClick={openHistoryModal}
                              >
                                View Report History
                              </Menu.Item>
                              {hasReportDeliveryHistory && (
                                <Menu.Item
                                  leftSection={<IconHistory size={14} />}
                                  onClick={openReportDeliveryHistory}
                                >
                                  View Delivery History
                                </Menu.Item>
                              )}
                            </Menu.Dropdown>
                          </Menu>
                          {canRecallReport && (
                            <RecallReportButton
                              remainingSeconds={reportRecallRemainingSeconds}
                              countdownPhase={reportRecallCountdownPhase}
                              methods={reportRecallState?.methods ?? []}
                              scheduledAt={
                                reportRecallState?.earliestPendingScheduledAt ??
                                null
                              }
                              recipientCount={
                                reportRecallState?.recipientCount ?? 0
                              }
                              onClick={openReportRecallModal}
                              loading={isReportRecallSubmitting}
                            />
                          )}
                          {canRecallReport &&
                            hasReportDeliveryHistory &&
                            displayedReportRecallState && (
                              <ReportDeliveryStateBadge
                                state={
                                  displayedReportRecallState.aggregateState
                                }
                                onClick={openReportDeliveryHistory}
                                actionLabel="View report delivery and recall history"
                              />
                            )}
                          {showReportDeliveryStateBadge &&
                            displayedReportRecallState && (
                              <ReportDeliveryStateBadge
                                state={
                                  displayedReportRecallState.aggregateState
                                }
                                onClick={
                                  hasReportDeliveryHistory
                                    ? openReportDeliveryHistory
                                    : undefined
                                }
                                actionLabel={
                                  hasReportDeliveryHistory
                                    ? "View report delivery and recall history"
                                    : undefined
                                }
                              />
                            )}
                          {!hasInProgressReportDelivery && (
                            <Button
                              color={
                                isCurrentReportFullyRecalled
                                  ? "orange"
                                  : "gcpBlue"
                              }
                              leftSection={<IconSend size={16} />}
                              onClick={handleSendReportClick}
                              loading={isSendingReport}
                            >
                              Send Report
                            </Button>
                          )}
                        </>
                      ) : isAddendumEligibilityUnavailable ? (
                        <>
                          <Text size="sm" c="red">
                            Report status could not be verified. Retry before
                            signing out.
                          </Text>
                          <Button
                            variant="default"
                            onClick={handleRetryAddendumEligibility}
                            loading={addendumEligibilityQuery.isFetching}
                          >
                            Retry
                          </Button>
                        </>
                      ) : canCreateFromExternalBaseline ? (
                        <>
                          <Button
                            component="a"
                            href={addendumEligibilityQuery.data?.downloadUrl}
                            target="_blank"
                            rel="noreferrer"
                            variant="default"
                            leftSection={<IconExternalLink size={16} />}
                            disabled={
                              !addendumEligibilityQuery.data?.downloadUrl
                            }
                          >
                            View Castle Report
                          </Button>
                          <Button
                            color="gcpBlue"
                            leftSection={<IconFileText size={16} />}
                            onClick={handleOpenAddendum}
                            loading={addendumDraftQuery.isLoading}
                          >
                            Create Addendum
                          </Button>
                        </>
                      ) : activeNavItem === "requisition" ? (
                        <>
                          <Button
                            variant="light"
                            leftSection={<IconEye size={16} />}
                            onClick={handlePreviewReport}
                          >
                            Preview Report
                          </Button>
                          <Button
                            onClick={() => requisitionRef.current?.save()}
                            loading={requisitionRef.current?.isSaving}
                          >
                            Save
                          </Button>
                        </>
                      ) : (
                        <>
                          <Button
                            variant="default"
                            onClick={handleSaveDraft}
                            loading={saveDraftMutation.isPending}
                            disabled={Boolean(stalePathologistAssignment)}
                          >
                            Save Draft
                          </Button>
                          <Button
                            variant="light"
                            leftSection={<IconEye size={16} />}
                            onClick={handlePreviewReport}
                          >
                            Preview Report
                          </Button>
                          <Tooltip
                            label="This case has a pending discussion"
                            disabled={!hasActiveDiscussion}
                          >
                            <Button
                              color="green"
                              onClick={handleSignOut}
                              loading={signOffLoading}
                              disabled={Boolean(stalePathologistAssignment)}
                            >
                              {reportReviewStatus?.actionLabel ??
                                "Preview & Sign Out"}
                            </Button>
                          </Tooltip>
                        </>
                      )}
                    </Group>
                  )}
                </Group>
              </Stack>
            </Box>
          </Box>
        </Grid.Col>

        {/* RIGHT COLUMN: Dynamic Widget Panel */}
        <Grid.Col
          span={{ base: 12, lg: 3 }}
          style={{ minWidth: 0, maxWidth: 320 }}
        >
          <Box
            style={{
              height: "calc(100vh - 112px)",
              overflowY: "auto",
              borderLeft: "1px solid var(--mantine-color-gray-3)",
            }}
          >
            <Stack gap={0} px={0} py="md">
              {/* Partial Sign-Off Indicator — always shown */}
              {caseData.isPartiallyFinalized && (
                <Alert
                  color="blue"
                  variant="light"
                  title="Partially Signed Off"
                  icon={<IconCheck size={18} />}
                >
                  Some specimens have been signed off. This case is awaiting
                  pending stains before final sign-off.
                </Alert>
              )}

              {/* Dynamic widgets driven by widgetOrder preference */}
              {reviewPreferences.widgetOrder.map((widgetId) => {
                switch (widgetId) {
                  case "client-requisition":
                    return (
                      <RightPanelWidget
                        key={widgetId}
                        title="Client / Requisition"
                        icon={
                          <IconAddressBook
                            size={16}
                            color="var(--mantine-color-blue-6)"
                          />
                        }
                      >
                        <CaseInfoWidget
                          caseId={caseId}
                          caseNumber={liveCaseData.caseNumber}
                          patient={{
                            firstName: liveCaseData.patient?.firstName,
                            lastName: liveCaseData.patient?.lastName,
                            dateOfBirth: liveCaseData.patient?.dateOfBirth,
                            sex: liveCaseData.patient?.sex,
                            mrn: liveCaseData.patient?.medicalRecordNumber,
                          }}
                          physicianName={
                            liveCaseData.physician
                              ? [
                                  liveCaseData.physician.lastName,
                                  liveCaseData.physician.firstName,
                                ]
                                  .filter(Boolean)
                                  .join(", ")
                              : undefined
                          }
                          dateCollected={liveCaseData.dateCollected}
                          dateReceived={liveCaseData.dateReceived}
                          dateReported={liveCaseData.signedOffAt}
                          surgicalCenterName={
                            liveCaseData.surgeryCenterLab?.name
                          }
                        />
                      </RightPanelWidget>
                    );

                  case "results":
                    return (
                      <RightPanelWidget
                        key={widgetId}
                        title="Historical Reports"
                        icon={
                          <IconHistory
                            size={16}
                            color="var(--mantine-color-grape-6)"
                          />
                        }
                      >
                        <HistoricalReportsWidget
                          cases={finalizedPriorCases}
                          isLoading={priorCasesLoading}
                          onViewReport={handleViewHistoricalReport}
                        />
                      </RightPanelWidget>
                    );

                  case "microscopic-description":
                    return (
                      <RightPanelWidget
                        key={widgetId}
                        title="Microscopic Description"
                        icon={
                          <IconMicroscope
                            size={16}
                            color="var(--mantine-color-teal-6)"
                          />
                        }
                      >
                        <MicroscopicDescriptionWidget
                          caseData={liveCaseData}
                          diagnosisDataMap={diagnosisDataMap}
                        />
                      </RightPanelWidget>
                    );

                  case "stains":
                    return (
                      <RightPanelWidget
                        key={widgetId}
                        title="Stains"
                        icon={
                          <IconFlask
                            size={16}
                            color="var(--mantine-color-teal-6)"
                          />
                        }
                        onOpen={() => setActiveNavItem("stains")}
                      >
                        <StainsWidget
                          caseId={caseId || null}
                          caseData={liveCaseData}
                          onRequestStains={handleRequestStains}
                          isReadOnly={isReadOnly}
                        />
                      </RightPanelWidget>
                    );

                  case "grossing":
                    return (
                      <RightPanelWidget
                        key={widgetId}
                        title="Grossing"
                        icon={
                          <IconScissors
                            size={16}
                            color="var(--mantine-color-orange-6)"
                          />
                        }
                        onOpen={() => setActiveNavItem("grossing")}
                      >
                        <GrossingWidget
                          caseData={liveCaseData}
                          grossingDataMap={grossingDataMap}
                        />
                      </RightPanelWidget>
                    );

                  case "documents-files-images":
                    return (
                      <RightPanelWidget
                        key={widgetId}
                        title={`Documents${
                          savedCaseFiles.length + caseImages.length > 0
                            ? ` (${savedCaseFiles.length + caseImages.length})`
                            : ""
                        }`}
                        icon={
                          <IconFileText
                            size={16}
                            color="var(--mantine-color-green-6)"
                          />
                        }
                      >
                        <Stack gap="xs">
                          {/* ── Documents ── */}
                          <Group gap={6} align="center">
                            <IconFileText
                              size={13}
                              color="var(--mantine-color-green-6)"
                            />
                            <Text size="xs" fw={600} c="dimmed" tt="uppercase">
                              Documents
                            </Text>
                            {documentCaseFiles.length > 0 && (
                              <Badge
                                size="xs"
                                variant="light"
                                color="green"
                                radius="sm"
                              >
                                {documentCaseFiles.length}
                              </Badge>
                            )}
                          </Group>
                          <DocumentUpload
                            files={uploadedDocFiles}
                            onFilesChange={handleDocFilesChange}
                            maxFiles={10}
                            getPresignedUrl={
                              pathologistReviewService.getPresignedUrl
                            }
                            hideFileList
                            showDropZone
                            dropZonePlaceholder="Drag and drop documents here, or click to select"
                            disabled={Boolean(caseData?.isBlocked)}
                          >
                            {caseFilesLoading ? (
                              <Text size="xs" c="dimmed">
                                Loading...
                              </Text>
                            ) : documentCaseFiles.length === 0 &&
                              uploadedDocFiles.filter(
                                (f) => !f.s3Key && !f.uploadError
                              ).length === 0 ? (
                              <Text size="xs" c="dimmed">
                                No documents attached
                              </Text>
                            ) : (
                              <Stack gap={4}>
                                {documentCaseFiles.map((file) => (
                                  <Card key={file.id} p="xs" withBorder>
                                    <Stack gap={6}>
                                      {/* Row 1: icon + name/badge + actions */}
                                      <Group
                                        gap="xs"
                                        wrap="nowrap"
                                        align="center"
                                      >
                                        <Box
                                          style={{
                                            width: 28,
                                            height: 28,
                                            borderRadius: 4,
                                            display: "flex",
                                            alignItems: "center",
                                            justifyContent: "center",
                                            flexShrink: 0,
                                            background:
                                              "var(--mantine-color-gray-1)",
                                          }}
                                        >
                                          <IconFileText
                                            size={14}
                                            color="var(--mantine-color-green-6)"
                                          />
                                        </Box>
                                        <Box style={{ flex: 1, minWidth: 0 }}>
                                          <Text
                                            size="xs"
                                            fw={500}
                                            truncate="end"
                                          >
                                            {file.name}
                                          </Text>
                                          {(() => {
                                            const source = getUploadSourceBadge(
                                              file.documentType
                                            );
                                            return (
                                              <Badge
                                                size="xs"
                                                variant="light"
                                                color={source.color}
                                                mt={2}
                                              >
                                                {source.label}
                                              </Badge>
                                            );
                                          })()}
                                        </Box>
                                        <Group
                                          gap={4}
                                          wrap="nowrap"
                                          style={{ flexShrink: 0 }}
                                        >
                                          <Tooltip label="Preview">
                                            <ActionIcon
                                              size="xs"
                                              variant="light"
                                              color="blue"
                                              onClick={() =>
                                                handlePreviewCaseFile(file)
                                              }
                                            >
                                              <IconEye size={12} />
                                            </ActionIcon>
                                          </Tooltip>
                                          {!isReadOnly &&
                                            file.documentType !==
                                              "accessioning" && (
                                              <Tooltip label="Delete">
                                                <ActionIcon
                                                  size="xs"
                                                  variant="light"
                                                  color="red"
                                                  onClick={() =>
                                                    setDeleteDocFileId(file.id)
                                                  }
                                                >
                                                  <IconTrash size={12} />
                                                </ActionIcon>
                                              </Tooltip>
                                            )}
                                        </Group>
                                      </Group>

                                      {isImageMimeType(file.mimeType) ? (
                                        <CaseFileReportControls
                                          fileId={file.id}
                                          caption={file.label || ""}
                                          includeInReport={file.includeInReport}
                                          readOnly={Boolean(
                                            file.finalizedInReport ||
                                              !canAddPhotos
                                          )}
                                          isFinalized={
                                            hasSignedReportForAddendumSection
                                          }
                                          finalizedInReport={
                                            file.finalizedInReport
                                          }
                                          includeReadOnly={Boolean(
                                            file.finalizedInReport ||
                                              !canAddPhotos
                                          )}
                                          onCaptionChange={
                                            handleFileCaptionChange
                                          }
                                          onIncludeInReportChange={
                                            handleToggleFileInReport
                                          }
                                        />
                                      ) : (
                                        <Stack gap={4}>
                                          <Checkbox
                                            size="xs"
                                            label="Add to report"
                                            checked={
                                              file.includeInReport ?? false
                                            }
                                            disabled={file.finalizedInReport}
                                            onChange={() =>
                                              handleToggleFileInReport(file.id)
                                            }
                                            styles={{
                                              label: {
                                                cursor: "pointer",
                                                fontSize: 11,
                                              },
                                            }}
                                          />
                                          <PendingReportAttachmentNotice
                                            isFinalized={
                                              hasSignedReportForAddendumSection
                                            }
                                            includeInReport={
                                              file.includeInReport ?? false
                                            }
                                            finalizedInReport={
                                              file.finalizedInReport
                                            }
                                          />
                                        </Stack>
                                      )}
                                    </Stack>
                                  </Card>
                                ))}
                                {uploadedDocFiles
                                  .filter((f) => !f.s3Key && !f.uploadError)
                                  .map((f) => {
                                    const name =
                                      f.file?.name ||
                                      f.fileName ||
                                      "Uploading...";
                                    return (
                                      <Card key={name} p="xs" withBorder>
                                        <Group
                                          gap="xs"
                                          wrap="nowrap"
                                          align="center"
                                        >
                                          <Box
                                            style={{
                                              width: 28,
                                              height: 28,
                                              borderRadius: 4,
                                              display: "flex",
                                              alignItems: "center",
                                              justifyContent: "center",
                                              flexShrink: 0,
                                              background:
                                                "var(--mantine-color-gray-1)",
                                            }}
                                          >
                                            <Loader size="xs" />
                                          </Box>
                                          <Box style={{ flex: 1, minWidth: 0 }}>
                                            <Text
                                              size="xs"
                                              fw={500}
                                              truncate="end"
                                            >
                                              {name}
                                            </Text>
                                            <Text size="xs" c="dimmed">
                                              Uploading...
                                            </Text>
                                          </Box>
                                        </Group>
                                      </Card>
                                    );
                                  })}
                              </Stack>
                            )}
                          </DocumentUpload>

                          <Divider my={4} />

                          {/* ── Images ── */}
                          <Group gap={6} align="center">
                            <IconPhoto
                              size={13}
                              color="var(--mantine-color-blue-6)"
                            />
                            <Text size="xs" fw={600} c="dimmed" tt="uppercase">
                              Images
                            </Text>
                            {caseImages.length + imageCaseFiles.length > 0 && (
                              <Badge
                                size="xs"
                                variant="light"
                                color="blue"
                                radius="sm"
                              >
                                {caseImages.length + imageCaseFiles.length}
                              </Badge>
                            )}
                          </Group>
                          {imageCaseFiles.length > 0 && (
                            <Stack gap={4}>
                              {imageCaseFiles.map((file) => (
                                <Card key={file.id} p="xs" withBorder>
                                  <Stack gap={6}>
                                    {/* Row 1: icon + name/badge + actions */}
                                    <Group
                                      gap="xs"
                                      wrap="nowrap"
                                      align="center"
                                    >
                                      <Box
                                        style={{
                                          width: 28,
                                          height: 28,
                                          borderRadius: 4,
                                          display: "flex",
                                          alignItems: "center",
                                          justifyContent: "center",
                                          flexShrink: 0,
                                          background:
                                            "var(--mantine-color-gray-1)",
                                        }}
                                      >
                                        <IconPhoto
                                          size={14}
                                          color="var(--mantine-color-blue-6)"
                                        />
                                      </Box>
                                      <Box style={{ flex: 1, minWidth: 0 }}>
                                        <Text size="xs" fw={500} truncate="end">
                                          {file.name}
                                        </Text>
                                        {(() => {
                                          const source = getUploadSourceBadge(
                                            file.documentType
                                          );
                                          return (
                                            <Badge
                                              size="xs"
                                              variant="light"
                                              color={source.color}
                                              mt={2}
                                            >
                                              {source.label}
                                            </Badge>
                                          );
                                        })()}
                                      </Box>
                                      <Group
                                        gap={4}
                                        wrap="nowrap"
                                        style={{ flexShrink: 0 }}
                                      >
                                        <Tooltip label="Preview">
                                          <ActionIcon
                                            size="xs"
                                            variant="light"
                                            color="blue"
                                            onClick={() =>
                                              handlePreviewCaseFile(file)
                                            }
                                          >
                                            <IconEye size={12} />
                                          </ActionIcon>
                                        </Tooltip>
                                        {!isReadOnly &&
                                          file.documentType !==
                                            "accessioning" && (
                                            <Tooltip label="Delete">
                                              <ActionIcon
                                                size="xs"
                                                variant="light"
                                                color="red"
                                                onClick={() =>
                                                  setDeleteDocFileId(file.id)
                                                }
                                              >
                                                <IconTrash size={12} />
                                              </ActionIcon>
                                            </Tooltip>
                                          )}
                                      </Group>
                                    </Group>

                                    {isImageMimeType(file.mimeType) ? (
                                      <CaseFileReportControls
                                        fileId={file.id}
                                        caption={file.label || ""}
                                        includeInReport={file.includeInReport}
                                        readOnly={Boolean(
                                          file.finalizedInReport ||
                                            !canAddPhotos
                                        )}
                                        isFinalized={
                                          hasSignedReportForAddendumSection
                                        }
                                        finalizedInReport={
                                          file.finalizedInReport
                                        }
                                        includeReadOnly={Boolean(
                                          file.finalizedInReport ||
                                            !canAddPhotos
                                        )}
                                        onCaptionChange={
                                          handleFileCaptionChange
                                        }
                                        onIncludeInReportChange={
                                          handleToggleFileInReport
                                        }
                                      />
                                    ) : (
                                      <Stack gap={4}>
                                        <Checkbox
                                          size="xs"
                                          label="Add to report"
                                          checked={
                                            file.includeInReport ?? false
                                          }
                                          disabled={file.finalizedInReport}
                                          onChange={() =>
                                            handleToggleFileInReport(file.id)
                                          }
                                          styles={{
                                            label: {
                                              cursor: "pointer",
                                              fontSize: 11,
                                            },
                                          }}
                                        />
                                        <PendingReportAttachmentNotice
                                          isFinalized={
                                            hasSignedReportForAddendumSection
                                          }
                                          includeInReport={
                                            file.includeInReport ?? false
                                          }
                                          finalizedInReport={
                                            file.finalizedInReport
                                          }
                                        />
                                      </Stack>
                                    )}
                                  </Stack>
                                </Card>
                              ))}
                            </Stack>
                          )}
                          {caseImagesLoading ? (
                            <Text size="xs" c="dimmed">
                              Loading...
                            </Text>
                          ) : caseImages.length === 0 &&
                            imageCaseFiles.length === 0 ? (
                            <Text size="xs" c="dimmed">
                              No images attached
                            </Text>
                          ) : (
                            <Stack gap={4}>
                              {caseImages.map((img, idx) => (
                                <Card key={img.id} p="xs" withBorder>
                                  <Stack gap={6}>
                                    {/* Thumbnail + caption row */}
                                    <Group gap="xs" wrap="nowrap">
                                      {imageDownloadUrls[img.id] ? (
                                        <Box
                                          style={{
                                            width: 36,
                                            height: 36,
                                            borderRadius: 4,
                                            overflow: "hidden",
                                            flexShrink: 0,
                                            border:
                                              "1px solid var(--mantine-color-gray-3)",
                                          }}
                                        >
                                          <img
                                            src={imageDownloadUrls[img.id]}
                                            alt={
                                              img.caption || `Image ${idx + 1}`
                                            }
                                            style={{
                                              width: "100%",
                                              height: "100%",
                                              objectFit: "cover",
                                            }}
                                          />
                                        </Box>
                                      ) : (
                                        <Box
                                          style={{
                                            width: 36,
                                            height: 36,
                                            borderRadius: 4,
                                            flexShrink: 0,
                                            backgroundColor:
                                              "var(--mantine-color-gray-1)",
                                            display: "flex",
                                            alignItems: "center",
                                            justifyContent: "center",
                                          }}
                                        >
                                          <IconPhoto
                                            size={14}
                                            color="var(--mantine-color-gray-5)"
                                          />
                                        </Box>
                                      )}
                                      <Box style={{ flex: 1, minWidth: 0 }}>
                                        <Text size="xs" fw={500} truncate>
                                          {img.fileName || `Image ${idx + 1}`}
                                        </Text>
                                        {img.caption && (
                                          <Text
                                            size="xs"
                                            c="dimmed"
                                            lineClamp={1}
                                          >
                                            {img.caption}
                                          </Text>
                                        )}
                                        {img.magnification && (
                                          <Text size="xs" c="dimmed">
                                            {img.magnification}
                                          </Text>
                                        )}
                                      </Box>
                                    </Group>
                                    <CaseImageReportControls
                                      imageId={img.id}
                                      caption={img.caption || ""}
                                      includeInReport={img.includeInReport}
                                      readOnly={Boolean(
                                        img.finalizedInReport || !canAddPhotos
                                      )}
                                      includeReadOnly={Boolean(
                                        img.finalizedInReport || !canAddPhotos
                                      )}
                                      onCaptionChange={handleImageCaptionChange}
                                      onIncludeInReportChange={
                                        handleToggleImageInReport
                                      }
                                      actions={
                                        <Group gap={4} wrap="nowrap">
                                          {imageDownloadUrls[img.id] && (
                                            <Tooltip label="Download">
                                              <ActionIcon
                                                size="xs"
                                                variant="light"
                                                color="teal"
                                                onClick={() =>
                                                  handleDownloadCaseImage(img)
                                                }
                                              >
                                                <IconDownload size={12} />
                                              </ActionIcon>
                                            </Tooltip>
                                          )}
                                          {!isReadOnly && (
                                            <Tooltip label="Edit">
                                              <ActionIcon
                                                size="xs"
                                                variant="light"
                                                color="blue"
                                                onClick={() =>
                                                  handleOpenImageEdit(img)
                                                }
                                              >
                                                <IconEdit size={12} />
                                              </ActionIcon>
                                            </Tooltip>
                                          )}
                                          {!isReadOnly && (
                                            <Tooltip label="Delete">
                                              <ActionIcon
                                                size="xs"
                                                variant="light"
                                                color="red"
                                                onClick={() =>
                                                  setDeleteImageId(img.id)
                                                }
                                              >
                                                <IconTrash size={12} />
                                              </ActionIcon>
                                            </Tooltip>
                                          )}
                                        </Group>
                                      }
                                    />
                                  </Stack>
                                </Card>
                              ))}
                            </Stack>
                          )}
                          {canAddPhotos && (
                            <Group grow>
                              <Button
                                size="xs"
                                variant="light"
                                leftSection={<IconCamera size={14} />}
                                onClick={() =>
                                  setCameraCaptureModalOpened(true)
                                }
                              >
                                Capture
                              </Button>
                              <Button
                                size="xs"
                                variant="light"
                                leftSection={<IconUpload size={14} />}
                                onClick={() => setImageUploadModalOpened(true)}
                              >
                                Upload
                              </Button>
                            </Group>
                          )}
                        </Stack>
                      </RightPanelWidget>
                    );

                  case "send-outs":
                    return (
                      <RightPanelWidget
                        key={widgetId}
                        title="Send Outs"
                        icon={
                          <IconExternalLink
                            size={16}
                            color="var(--mantine-color-dimmed)"
                          />
                        }
                      >
                        <Stack gap="xs">
                          <ActiveSendOutsBanner caseId={caseId} compact />
                          {caseId && (
                            <PendingStainsPanel
                              caseId={caseId}
                              caseNumber={caseData?.caseNumber}
                            />
                          )}
                          {!hasActiveSendOuts && pendingStains.length === 0 && (
                            <Text size="sm" c="dimmed">
                              No active send outs
                            </Text>
                          )}
                          <Button
                            size="xs"
                            variant="subtle"
                            color="gray"
                            leftSection={<IconExternalLink size={13} />}
                            onClick={
                              hasActiveSendOuts
                                ? openDuplicateConfirm
                                : openExternalOpinionModal
                            }
                          >
                            {hasActiveSendOuts
                              ? `External Opinion (${activeSendOuts.length})`
                              : "Request External Opinion"}
                          </Button>
                        </Stack>
                      </RightPanelWidget>
                    );

                  case "icd-codes":
                    return (
                      <RightPanelWidget
                        key={widgetId}
                        title="ICD-10 Codes"
                        icon={
                          <IconNotes
                            size={16}
                            color="var(--mantine-color-teal-6)"
                          />
                        }
                      >
                        <IcdCodesWidget
                          specimens={specimens}
                          diagnosisDataMap={diagnosisDataMap}
                          onCodesChange={(specimenId, codes) => {
                            updateDiagnosisData(specimenId, {
                              icdCodes: codes,
                              icdCode: codes[0] || null,
                            });
                          }}
                        />
                      </RightPanelWidget>
                    );

                  case "cpt-codes":
                    return (
                      <RightPanelWidget
                        key={widgetId}
                        title="CPT Codes"
                        icon={
                          <IconLayoutGrid
                            size={16}
                            color="var(--mantine-color-blue-6)"
                          />
                        }
                      >
                        <CptCodesWidget
                          specimens={specimens}
                          diagnosisDataMap={diagnosisDataMap}
                          onCodeChange={(specimenId, code) => {
                            updateDiagnosisData(specimenId, {
                              cptCode: code,
                            });
                          }}
                        />
                      </RightPanelWidget>
                    );

                  case "panels":
                    return (
                      <RightPanelWidget
                        key={widgetId}
                        title="Panels"
                        icon={
                          <IconPackages
                            size={16}
                            color="var(--mantine-color-blue-6)"
                          />
                        }
                      >
                        {caseId && <PanelSummarySection caseId={caseId} />}
                      </RightPanelWidget>
                    );

                  case "case-queue":
                    return (
                      <RightPanelWidget
                        key={widgetId}
                        title="Case Queue"
                        icon={
                          <IconLayoutGrid
                            size={16}
                            color="var(--mantine-color-gray-6)"
                          />
                        }
                      >
                        <CaseQueueWidget
                          currentCaseId={caseId || ""}
                          queueCases={queueCases}
                        />
                      </RightPanelWidget>
                    );

                  case "qa-review-status":
                    return (
                      <RightPanelWidget
                        key={widgetId}
                        title="QA Review"
                        icon={
                          <IconAlertTriangle
                            size={16}
                            color="var(--mantine-color-dimmed)"
                          />
                        }
                      >
                        <QAReviewStatusWidget
                          caseData={liveCaseData}
                          hasActiveDiscussion={hasActiveDiscussion}
                          hasActiveSendOuts={hasActiveSendOuts}
                          activeSendOutsCount={activeSendOuts.length}
                          onViewDiscussion={() =>
                            navigate(
                              activeDiscussion?.id
                                ? `/discussion-queue/${caseId}/${activeDiscussion.id}`
                                : `/discussion-queue/${caseId}`
                            )
                          }
                          onFlagDiscussion={openFlagDiscussionModal}
                          onExternalOpinion={
                            hasActiveSendOuts
                              ? openDuplicateConfirm
                              : openExternalOpinionModal
                          }
                        />
                      </RightPanelWidget>
                    );

                  case "flags":
                    return (
                      <RightPanelWidget
                        key={widgetId}
                        title="Flags"
                        icon={
                          <IconFlag
                            size={16}
                            color="var(--mantine-color-teal-6)"
                          />
                        }
                      >
                        <CaseFlagsSection caseId={caseId || null} embedded />
                      </RightPanelWidget>
                    );

                  case "notes":
                    return (
                      <RightPanelWidget
                        key={widgetId}
                        title="Notes"
                        icon={
                          <IconNotes
                            size={16}
                            color="var(--mantine-color-blue-6)"
                          />
                        }
                        onOpen={() => setActiveNavItem("notes")}
                      >
                        <CaseNotesSection caseId={caseId || null} embedded />
                      </RightPanelWidget>
                    );

                  case "comments":
                    return (
                      <RightPanelWidget
                        key={widgetId}
                        title="Comments"
                        icon={
                          <IconMessage
                            size={16}
                            color="var(--mantine-color-cyan-6)"
                          />
                        }
                        onOpen={() => setActiveNavItem("comments")}
                      >
                        <CaseCommentsSection caseId={caseId || null} embedded />
                      </RightPanelWidget>
                    );

                  case "discussions":
                    return (
                      <RightPanelWidget
                        key={widgetId}
                        title={`Discussions${
                          caseDiscussions.length > 0
                            ? ` (${caseDiscussions.length})`
                            : ""
                        }`}
                        icon={
                          <IconMessageCircle
                            size={16}
                            color="var(--mantine-color-grape-6)"
                          />
                        }
                        onOpen={() => setActiveNavItem("discussions")}
                      >
                        <Stack gap="xs">
                          <Button
                            size="xs"
                            variant="light"
                            color="grape"
                            leftSection={<IconMessageCircle size={13} />}
                            onClick={openFlagDiscussionModal}
                            fullWidth
                          >
                            Flag for Discussion
                          </Button>
                          {caseDiscussions.length === 0 ? (
                            <Text size="xs" c="dimmed">
                              No discussions for this case
                            </Text>
                          ) : (
                            <Stack gap={4}>
                              {caseDiscussions.map((d) => (
                                <Card key={d.id} p="xs" withBorder>
                                  <Stack gap={4}>
                                    <Group gap={4} wrap="wrap">
                                      <Badge
                                        size="xs"
                                        variant="filled"
                                        color={
                                          d.status === DiscussionStatus.PENDING
                                            ? "blue"
                                            : d.status ===
                                              DiscussionStatus.RESOLVED
                                            ? "green"
                                            : "gray"
                                        }
                                      >
                                        {d.status === DiscussionStatus.PENDING
                                          ? "Pending"
                                          : d.status ===
                                            DiscussionStatus.RESOLVED
                                          ? "Resolved"
                                          : "Cancelled"}
                                      </Badge>
                                      {d.isUrgent && (
                                        <Badge
                                          size="xs"
                                          variant="filled"
                                          color="red"
                                        >
                                          Urgent
                                        </Badge>
                                      )}
                                      {d.category && (
                                        <Badge
                                          size="xs"
                                          variant="light"
                                          color="gray"
                                        >
                                          {d.category}
                                        </Badge>
                                      )}
                                    </Group>
                                    <Text size="xs" c="dimmed">
                                      {d.initiatedBy?.fullName ?? "Unknown"} ·{" "}
                                      {dayjs(d.createdAt).fromNow()}
                                    </Text>
                                  </Stack>
                                </Card>
                              ))}
                            </Stack>
                          )}
                        </Stack>
                      </RightPanelWidget>
                    );

                  case "errors":
                    return (
                      <RightPanelWidget
                        key={widgetId}
                        title="Errors"
                        icon={
                          <IconAlertCircle
                            size={16}
                            color="var(--mantine-color-red-6)"
                          />
                        }
                        onOpen={() => setActiveNavItem("errors")}
                      >
                        <div data-errors-card>
                          <ErrorsCard caseId={caseId || null} compact />
                        </div>
                      </RightPanelWidget>
                    );

                  case "prior-cases":
                    return (
                      <Fragment key={widgetId}>
                        {currentOtherCases.length > 0 && (
                          <RightPanelWidget
                            title={`Current Other Cases (${currentOtherCases.length})`}
                            icon={
                              <IconActivity
                                size={16}
                                color="var(--mantine-color-blue-6)"
                              />
                            }
                          >
                            <Stack gap="xs">
                              {currentOtherCases.map((pc) => (
                                <RelatedCaseCard
                                  key={pc.id}
                                  caseData={pc}
                                  onNavigate={handleNavigateToCase}
                                />
                              ))}
                            </Stack>
                          </RightPanelWidget>
                        )}
                        <RightPanelWidget
                          title={`Prior Cases${
                            finalizedPriorCases.length > 0
                              ? ` (${finalizedPriorCases.length})`
                              : ""
                          }`}
                          icon={
                            <IconHistory
                              size={16}
                              color="var(--mantine-color-orange-6)"
                            />
                          }
                        >
                          <Stack gap="xs">
                            {priorCasesLoading ? (
                              <>
                                <Skeleton height={52} radius="sm" />
                                <Skeleton height={52} radius="sm" />
                              </>
                            ) : finalizedPriorCases.length === 0 ? (
                              <Text size="sm" c="dimmed">
                                No prior cases found for this patient
                              </Text>
                            ) : (
                              finalizedPriorCases.map((pc) => (
                                <RelatedCaseCard
                                  key={pc.id}
                                  caseData={pc}
                                  onNavigate={handleNavigateToCase}
                                />
                              ))
                            )}
                          </Stack>
                        </RightPanelWidget>
                      </Fragment>
                    );

                  case "clinical-history":
                    return (
                      <RightPanelWidget
                        key={widgetId}
                        title="Clinical History"
                        icon={
                          <IconStethoscope
                            size={16}
                            color="var(--mantine-color-blue-6)"
                          />
                        }
                      >
                        <Stack gap="xs">
                          {caseData.clinicalHistoryCodes &&
                          caseData.clinicalHistoryCodes.length > 0 ? (
                            <Group gap="xs">
                              {caseData.clinicalHistoryCodes.map(
                                (codeId: string, index: number) => (
                                  <Badge key={index} size="sm" variant="light">
                                    {icdCodeLabelMap.get(codeId) || codeId}
                                  </Badge>
                                )
                              )}
                            </Group>
                          ) : (
                            <Text size="sm" c="dimmed">
                              No ICD codes
                            </Text>
                          )}
                          {!isEmptyHtml(
                            documentClinicalHistory ||
                              caseData.clinicalHistoryNotes
                          ) ? (
                            <>
                              <Text size="xs" fw={600} c="dimmed">
                                Clinical History
                              </Text>
                              <Box
                                style={{ fontSize: 13, lineHeight: 1.6 }}
                                dangerouslySetInnerHTML={{
                                  __html:
                                    documentClinicalHistory ||
                                    caseData.clinicalHistoryNotes ||
                                    "",
                                }}
                              />
                            </>
                          ) : null}
                          {!isEmptyHtml(
                            documentIndications || caseData.indications
                          ) ? (
                            <>
                              <Text size="xs" fw={600} c="dimmed">
                                Indications
                              </Text>
                              <Box
                                style={{ fontSize: 13, lineHeight: 1.6 }}
                                dangerouslySetInnerHTML={{
                                  __html:
                                    documentIndications ||
                                    caseData.indications ||
                                    "",
                                }}
                              />
                            </>
                          ) : null}
                          {!isEmptyHtml(
                            documentImpressions || caseData.impressions
                          ) ? (
                            <>
                              <Text size="xs" fw={600} c="dimmed">
                                Impressions
                              </Text>
                              <Box
                                style={{ fontSize: 13, lineHeight: 1.6 }}
                                dangerouslySetInnerHTML={{
                                  __html:
                                    documentImpressions ||
                                    caseData.impressions ||
                                    "",
                                }}
                              />
                            </>
                          ) : null}
                          {isEmptyHtml(
                            documentClinicalHistory ||
                              caseData.clinicalHistoryNotes
                          ) &&
                          isEmptyHtml(
                            documentIndications || caseData.indications
                          ) &&
                          isEmptyHtml(
                            documentImpressions || caseData.impressions
                          ) ? (
                            <Text size="sm" c="dimmed">
                              No clinical notes
                            </Text>
                          ) : null}
                        </Stack>
                      </RightPanelWidget>
                    );

                  case "colon-diagram": {
                    // Build OrganMapSpecimen data from case specimens
                    const organMapSpecs: OrganMapSpecimen[] = (
                      caseData.specimens || []
                    ).map((s: any) => {
                      const diagData = diagnosisDataMap.get(s.id);
                      const categoryName = diagData?.category || null;
                      const catInfo = categoryName
                        ? diagnosisCategories.find(
                            (c: any) => c.name === categoryName
                          )
                        : null;
                      const spec = {
                        specimenId: s.id,
                        specimenLabel: s.number || "?",
                        sourceName: s.source?.name || s.sourceName || null,
                        organMapTemplateId:
                          s.source?.organMapTemplateId ||
                          s.source?.organMapTemplate?.id ||
                          null,
                        organMapTemplateName:
                          s.source?.organMapTemplate?.name || null,
                        organMapImageUrl:
                          s.source?.organMapTemplate?.imageUrl || null,
                        organMapWidth:
                          s.source?.organMapTemplate?.width || null,
                        organMapHeight:
                          s.source?.organMapTemplate?.height || null,
                        subsiteName: s.subsite?.name || null,
                        positionX: s.subsite?.positionX ?? null,
                        positionY: s.subsite?.positionY ?? null,
                        categoryName,
                        markerShape: catInfo?.markerShape || null,
                        markerColor: catInfo?.markerColor || null,
                      };
                      return spec;
                    });
                    // Template applicability does not depend on subsite coordinates.
                    // The map identifies unpositioned specimens honestly.
                    const hasOrganMap = organMapSpecs.some(
                      hasApplicableOrganMap
                    );
                    return hasOrganMap ? (
                      <RightPanelWidget
                        key={widgetId}
                        title="Organ Map"
                        icon={
                          <IconLayoutGrid
                            size={16}
                            color="var(--mantine-color-pink-6)"
                          />
                        }
                      >
                        <OrganMapSection
                          compact
                          specimens={organMapSpecs}
                          onMarkerClick={(specimenId) => {
                            setExpandedSpecimens((prev) => {
                              const newSet = new Set(prev);
                              newSet.add(specimenId);
                              return newSet;
                            });
                            const element = document.getElementById(
                              `specimen-${specimenId}`
                            );
                            if (element) {
                              element.scrollIntoView({
                                behavior: "smooth",
                                block: "center",
                              });
                            }
                          }}
                        />
                      </RightPanelWidget>
                    ) : null;
                  }

                  default:
                    return null;
                }
              })}
            </Stack>
          </Box>
        </Grid.Col>
      </Grid>

      {/* Validation Errors Modal */}
      {(() => {
        const grossTemplateGuidance =
          getGrossTemplateSignOutGuidance(validationErrors);
        return (
          <Modal.Root
            opened={validationModalOpened}
            onClose={closeValidationModal}
            centered
            size="lg"
            styles={{
              content: {
                display: "flex",
                flexDirection: "column",
                maxHeight: "85vh",
              },
            }}
          >
            <Modal.Overlay />
            <Modal.Content>
              <Modal.Header>
                <Modal.Title>Cannot Sign Out</Modal.Title>
                <Modal.CloseButton />
              </Modal.Header>
              <Modal.Body style={{ flex: 1, overflow: "auto" }}>
                <Stack gap="md">
                  <Alert
                    icon={<IconAlertCircle size={16} />}
                    color="red"
                    variant="light"
                  >
                    {
                      validationErrors.filter((e) => e.severity === "error")
                        .length
                    }{" "}
                    issue(s) must be resolved before sign-out.
                    {grossTemplateGuidance && (
                      <Text mt="xs">{grossTemplateGuidance}</Text>
                    )}
                  </Alert>

                  {/* Group errors by specimen */}
                  {(() => {
                    const grouped = new Map<
                      string,
                      Array<{
                        field: string;
                        message: string;
                        severity: "error" | "warning";
                      }>
                    >();
                    validationErrors.forEach((err) => {
                      const key = err.specimenNumber || "Case";
                      if (!grouped.has(key)) grouped.set(key, []);
                      grouped.get(key)!.push({
                        field: err.field,
                        message: err.message,
                        severity: err.severity,
                      });
                    });

                    // Render specimen groups first, then case-level at the end
                    const specimenEntries: [
                      string,
                      Array<{
                        field: string;
                        message: string;
                        severity: "error" | "warning";
                      }>
                    ][] = [];
                    let caseEntry:
                      | [
                          string,
                          Array<{
                            field: string;
                            message: string;
                            severity: "error" | "warning";
                          }>
                        ]
                      | null = null;
                    grouped.forEach((errs, key) => {
                      if (key === "Case") {
                        caseEntry = [key, errs];
                      } else {
                        specimenEntries.push([key, errs]);
                      }
                    });
                    if (caseEntry) specimenEntries.push(caseEntry);

                    return (
                      <Stack gap="xs">
                        {specimenEntries.map(([label, errs]) => {
                          const hasSpecimenError = errs.some(
                            (e) => e.severity === "error"
                          );
                          return (
                            <Paper
                              key={label}
                              withBorder
                              radius="sm"
                              p="sm"
                              style={{
                                borderColor: hasSpecimenError
                                  ? "var(--mantine-color-red-3)"
                                  : "var(--mantine-color-orange-3)",
                              }}
                            >
                              <Stack gap={6}>
                                <Text
                                  size="xs"
                                  fw={700}
                                  c={hasSpecimenError ? "red.7" : "orange.7"}
                                  tt="uppercase"
                                  style={{ letterSpacing: "0.04em" }}
                                >
                                  {label === "Case"
                                    ? "Case-Level Fields"
                                    : `Specimen ${label}`}
                                </Text>
                                {errs.map((err, i) => (
                                  <Group
                                    key={i}
                                    gap="xs"
                                    wrap="nowrap"
                                    align="flex-start"
                                  >
                                    {err.severity === "error" ? (
                                      <IconAlertCircle
                                        size={14}
                                        color="var(--mantine-color-red-6)"
                                        style={{ marginTop: 2, flexShrink: 0 }}
                                      />
                                    ) : (
                                      <IconAlertTriangle
                                        size={14}
                                        color="var(--mantine-color-orange-6)"
                                        style={{ marginTop: 2, flexShrink: 0 }}
                                      />
                                    )}
                                    <Text
                                      size="sm"
                                      c={
                                        err.severity === "error"
                                          ? undefined
                                          : "dimmed"
                                      }
                                    >
                                      {err.field && (
                                        <Text span fw={600}>
                                          {err.field}:{" "}
                                        </Text>
                                      )}
                                      {err.message}
                                    </Text>
                                  </Group>
                                ))}
                              </Stack>
                            </Paper>
                          );
                        })}
                      </Stack>
                    );
                  })()}
                </Stack>
              </Modal.Body>

              <Divider />
              <Group justify="space-between" p="md">
                <Button variant="default" onClick={closeValidationModal}>
                  OK, I'll fix these
                </Button>
              </Group>
            </Modal.Content>
          </Modal.Root>
        );
      })()}

      {/* Validation Warnings Modal - allows proceeding with confirmation */}
      <Modal.Root
        opened={warningModalOpened}
        onClose={closeWarningModal}
        centered
        size="lg"
        styles={{
          content: {
            display: "flex",
            flexDirection: "column",
            maxHeight: "85vh",
          },
        }}
      >
        <Modal.Overlay />
        <Modal.Content>
          <Modal.Header>
            <Modal.Title>
              <Group gap="xs">
                <IconAlertTriangle
                  size={18}
                  color="var(--mantine-color-orange-6)"
                />
                <Text fw={600}>{warningPresentation.title}</Text>
              </Group>
            </Modal.Title>
            <Modal.CloseButton />
          </Modal.Header>

          <Modal.Body style={{ flex: 1, overflow: "auto" }}>
            <Stack gap="md">
              {/* Pending stains section — shown when pendingStains exist */}
              {pendingStains && pendingStains.length > 0 && (
                <Stack gap="xs">
                  <Text size="sm">This case has incomplete stains:</Text>
                  <Stack gap={4} pl="sm">
                    {pendingStains.map((stain) => (
                      <Group key={stain.id} gap="xs">
                        <Text size="sm" c="dimmed">
                          •
                        </Text>
                        <Text size="sm">
                          <Text span fw={500}>
                            {stain.specimen.number}
                          </Text>
                          {" - "}
                          {stain.stain.code}
                          <Text span c="dimmed">
                            {" "}
                            (not marked as done)
                          </Text>
                        </Text>
                      </Group>
                    ))}
                  </Stack>
                  <Text size="sm" c="dimmed">
                    You can still sign out — this is a warning, not a block.
                  </Text>
                  <Text size="sm" c="dimmed">
                    A discussion thread will be auto-created for this case.
                  </Text>
                </Stack>
              )}

              {/* Other warnings (unfilled placeholders, pending stains, etc.) */}
              {validationWarnings.some((w) => w.field !== "Pending Stains") && (
                <Stack gap="sm">
                  {pendingStains && pendingStains.length > 0 && (
                    <Divider label="Additional warnings" labelPosition="left" />
                  )}
                  {validationWarnings
                    .filter((w) => w.field !== "Pending Stains")
                    .map((warning, index) => (
                      <Box key={index}>
                        {warning.specimenNumber && (
                          <Badge
                            color="yellow"
                            size="sm"
                            variant="light"
                            mb={4}
                          >
                            {warning.specimenNumber}
                          </Badge>
                        )}
                        <Text size="sm">
                          {warning.field && (
                            <>
                              <Text span fw={500}>
                                {warning.field}:
                              </Text>{" "}
                            </>
                          )}
                          {warning.message}
                        </Text>
                      </Box>
                    ))}
                </Stack>
              )}

              {/* Show partial sign-off option if available */}
              {canDoPartialSignOff() && (
                <Alert
                  color="blue"
                  variant="light"
                  icon={<IconAlertCircle size={16} />}
                >
                  <Text size="sm" fw={500}>
                    Partial Sign-Off Available
                  </Text>
                  <Text size="xs" mt={4}>
                    You can sign off specimens without pending stains now, and
                    complete the remaining specimens when the stains are ready
                    (via addendum).
                  </Text>
                </Alert>
              )}
            </Stack>
          </Modal.Body>

          <Divider />
          <Group justify="space-between" p="md">
            <Button
              size="sm"
              variant="default"
              onClick={handleCancelSignOutWarning}
            >
              Cancel — Go Back
            </Button>
            <Group gap="sm">
              {canDoPartialSignOff() && (
                <Button
                  size="sm"
                  color="blue"
                  variant="outline"
                  onClick={() => {
                    const specimenIds = getSpecimensWithoutPendingStains();
                    doSignOut(
                      undefined,
                      true,
                      specimenIds,
                      false,
                      discussionChecksSkipped
                    );
                  }}
                  loading={signOffLoading}
                >
                  Sign Off Available Parts
                </Button>
              )}
              <Button
                size="sm"
                color="green"
                onClick={handleConfirmSignOutWarning}
                loading={signOffLoading}
              >
                Sign Out Anyway
              </Button>
            </Group>
          </Group>
        </Modal.Content>
      </Modal.Root>

      {/* Report Preview Modal */}
      {caseId &&
        caseData?.isFinalized &&
        addendumDraftQuery.isSuccess &&
        !addendumDraftQuery.data && (
          <SignedReportViewerModal
            opened={previewModalOpened}
            onClose={closePreviewModal}
            caseId={caseId}
            caseNumber={caseData.caseNumber}
          />
        )}
      {caseId && canCreateAddendum && addendumDraftQuery.data && (
        <AddendumReportPreviewModal
          opened={previewModalOpened}
          onClose={closePreviewModal}
          caseId={caseId}
          draft={addendumDraftQuery.data}
        />
      )}
      <SignedReportViewerModal
        opened={!!historicalReportCase}
        onClose={handleCloseHistoricalReport}
        caseId={historicalReportCase?.id || ""}
        caseNumber={historicalReportCase?.caseNumber}
      />
      {caseId &&
        caseData &&
        (!canAmendReports || addendumEligibilityQuery.isSuccess) &&
        !caseData.isFinalized &&
        !canCreateFromExternalBaseline && (
          <ReportPreviewModal
            opened={previewModalOpened}
            onClose={closePreviewModal}
            caseId={caseId}
            trailingReportFile={preSignOffCastleReportFile}
            onSignOut={handleReviewedReportSignOut}
            reviewRequired={reportReviewStatus?.reviewRequired !== false}
            signOutPending={signOffLoading}
            onTemplateIdChange={(id) => {
              signOffTemplateIdRef.current = id;
            }}
            liveData={{
              clinicalHistoryNotes: documentClinicalHistory,
              indications: documentIndications,
              impressions: documentImpressions,
              pathologistComment: documentPathologistComment,
              frozenInterpretation: caseFrozenInterpretation,
              procedure:
                caseData.procedure?.name || caseData.procedureName || undefined,
              specimens: liveReportSpecimens,
              diagnosisDataMap: buildResolvedDiagnosisMap(
                diagnosisDataMap,
                specimens,
                caseData
              ),
              grossingDataMap: (() => {
                const resolved = new Map<string, SpecimenGrossingData>();
                Array.from(grossingDataMap.entries()).forEach(([id, g]) => {
                  resolved.set(id, {
                    ...g,
                    grossDescription: resolveGrossVariables(
                      g.grossDescription,
                      g.variableValues
                    ),
                  });
                });
                return resolved;
              })(),
              liveCptDisplay: derivedCptCodes,
              liveIcdDisplay: derivedIcdCodes,
            }}
          />
        )}

      {/* Sign-Off Authentication Modal */}
      <SignOffAuthModal
        opened={authModalOpened}
        onClose={closeAuthModal}
        onAuthenticate={handleAuthenticateAndSignOut}
        isLoading={signOffLoading}
        error={signOffError}
      />

      {/* Sign-Off Success Modal (scan-modal preference only) */}
      {signOffResult && (
        <SignOffSuccessModal
          opened={successModalOpened}
          onClose={closeSuccessModal}
          caseNumber={signOffResult.caseNumber}
          signedBy={signOffResult.signedBy}
          signedAt={signOffResult.signedAt}
          onScanBarcode={(barcode) => {
            closeSuccessModal();
            // Navigate to matching case number or look up barcode
            navigate(
              `/pathologist-review?search=${encodeURIComponent(barcode)}`
            );
          }}
          onReturnToQueue={handleReturnToQueue}
        />
      )}

      {/* Amendment Modal - for creating amendments after sign-off */}
      {caseId && canCreateAddendum && caseData && (
        <AddendumEditorModal
          opened={addendumEditorOpened}
          caseId={caseId}
          caseNumber={caseData.caseNumber}
          sessionId={signOffSessionId}
          onClose={closeAddendumEditor}
          onSigned={handleAddendumSigned}
          onSessionUpdated={handleAddendumSessionUpdated}
          reviewRequired={reportReviewStatus?.reviewRequired !== false}
        />
      )}
      {caseId && caseData?.isFinalized && (
        <>
          <AmendmentEditorModal
            opened={amendmentEditorOpened}
            caseId={caseId}
            caseNumber={caseData.caseNumber}
            sessionId={signOffSessionId}
            onClose={handleCloseAmendment}
            onSigned={handleAddendumSigned}
            onSessionUpdated={handleAddendumSessionUpdated}
            reviewRequired={reportReviewStatus?.reviewRequired !== false}
          />
          {caseData.isLegacyImported ? (
            <LegacyReportCorrectionModal
              caseId={caseId}
              opened={correctionAmendmentModalOpened}
              onClose={handleCorrectionClosed}
            />
          ) : (
            <UpdatePatientInfoModal
              caseId={caseId}
              opened={correctionAmendmentModalOpened}
              onClose={handleCorrectionClosed}
              isSignedOff
              caseData={caseData}
              correctionAmendmentMode
            />
          )}
        </>
      )}

      {/* Page-level macro search for grid view (F3) - read-only reference */}
      {gridMacroSearchOpened && (
        <MacroSearchModal
          opened={gridMacroSearchOpened}
          onClose={closeGridMacroSearch}
          macros={diagnosisMacros}
          variableMap={{
            case_number: caseData?.caseNumber ?? null,
            patient_name: caseData?.patient
              ? [caseData.patient.firstName, caseData.patient.lastName]
                  .filter(Boolean)
                  .join(" ") || null
              : null,
            date_collected: caseData?.dateCollected
              ? (
                  parseDateString(caseData.dateCollected as string) ||
                  new Date()
                ).toLocaleDateString()
              : null,
            specimen_count: String(specimens.length),
          }}
        />
      )}

      {/* Report History Modal - view sign-off and amendment history */}
      {caseId && caseData && (
        <ReportHistoryModal
          opened={historyModalOpened}
          onClose={closeHistoryModal}
          caseId={caseId}
          caseNumber={caseData.caseNumber}
          onCreateAmendment={
            caseData.isFinalized ? openAmendmentEditor : undefined
          }
        />
      )}

      {caseId && caseData && canAddSpecimen && (
        <AddSpecimenModal
          opened={addSpecimenOpened}
          onClose={closeAddSpecimen}
          caseId={caseId}
          addSpecimenMutation={addSpecimenMutation}
        />
      )}

      {/* Recover previously removed (archived) specimens */}
      {caseId && caseData && (
        <RecoverSpecimensModal
          opened={recoverSpecimensOpened}
          onClose={closeRecoverSpecimens}
          caseId={caseId}
          archivedSpecimens={archivedSpecimens}
          recoverMutation={recoverSpecimenMutation}
        />
      )}

      <Modal
        opened={!!specimenToRemove}
        onClose={handleCancelRemoveSpecimen}
        title="Remove specimen"
        centered
        size="md"
      >
        <Stack gap="md">
          <Text size="sm">
            Remove specimen {specimenToRemove?.number}? It will be hidden from
            the report and the grid, but its blocks, slides and diagnosis are
            kept and can be recovered later.
          </Text>
          {caseData?.isFinalized && (
            <Alert
              color="orange"
              variant="light"
              icon={<IconAlertTriangle size={16} />}
            >
              This case is signed off — removing a specimen amends a finalized
              report.
            </Alert>
          )}
          <Group justify="flex-end" gap="sm">
            <Button variant="default" onClick={handleCancelRemoveSpecimen}>
              Cancel
            </Button>
            <Button
              color="red"
              loading={archiveSpecimenMutation.isPending}
              onClick={handleConfirmRemoveSpecimen}
            >
              Remove specimen
            </Button>
          </Group>
        </Stack>
      </Modal>

      {/* Request Additional Stain Modal */}
      {caseId && caseData?.specimens && (
        <RequestStainModal
          opened={requestStainModalOpened}
          onClose={closeRequestStainModal}
          caseId={caseId}
          caseNumber={caseData.caseNumber}
          specimens={requestStainSpecimens}
          preselectedSpecimenId={preselectedSpecimenId}
          onSuccess={() => {
            // Refetch case data to update UI
            refetchCase();
          }}
        />
      )}

      {/* Request External Opinion Modal */}
      {caseId && caseData?.specimens && (
        <RequestExternalOpinionModal
          opened={externalOpinionModalOpened}
          onClose={handleCloseExternalOpinionModal}
          caseId={caseId}
          specimens={caseData.specimens.map((s: any) => ({
            id: s.id,
            number: s.number,
            source: s.source,
            blocks: s.blocks?.map((b: any) => ({
              id: b.id,
              number: b.number,
              slides: b.slides?.map((sl: any) => ({
                id: sl.id,
                number: sl.number,
                stain: sl.stain,
                isControlSlide: sl.isControlSlide,
              })),
            })),
          }))}
          onSuccess={handleExternalOpinionSuccess}
          onAddInstitution={handleAddExternalInstitution}
          duplicateConfirmed={externalOpinionDuplicateConfirmed}
        />
      )}

      {/* Flag for Internal Discussion Modal */}
      {caseId && caseData && (
        <FlagDiscussionModal
          opened={flagDiscussionModalOpened}
          onClose={handleFlagDiscussionModalClose}
          caseId={caseId}
          caseNumber={caseData.caseNumber}
          workingDiagnosis={(() => {
            const specimen = caseData.specimens?.[0];
            if (!specimen) return undefined;
            const diagData = diagnosisDataMap.get(specimen.id);
            const rawText =
              diagData?.diagnosisText || specimen?.diagnosis?.diagnosisText;
            if (!rawText) return undefined;
            const vars: Record<string, string> = {};
            const addVar = (key: string, val: string | null | undefined) => {
              if (val != null && val !== "") vars[key] = val;
            };
            addVar("specimen_letter", specimen.number);
            addVar("source", specimen.source?.name);
            addVar("subsite", diagData?.location ?? specimen.subsite?.name);
            addVar("collection_method", specimen.collectionMethod);
            addVar(
              "block_count",
              specimen.blocks?.length != null
                ? String(specimen.blocks.length)
                : null
            );
            addVar("case_number", caseData.caseNumber);
            addVar(
              "patient_name",
              caseData.patient
                ? `${caseData.patient.firstName || ""} ${
                    caseData.patient.lastName || ""
                  }`.trim()
                : null
            );
            addVar("date_collected", caseData.dateCollected);
            addVar("color", diagData?.color);
            addVar("size_cm", diagData?.sizeCm);
            addVar("size_mm", diagData?.sizeMm);
            addVar("consistency", diagData?.consistency);
            addVar("location", diagData?.location ?? specimen.subsite?.name);
            addVar("LOCATION", diagData?.location ?? specimen.subsite?.name);
            return resolveGrossVariables(rawText, vars);
          })()}
        />
      )}

      <ReflexPanelDecisionModal
        key={
          [
            ...reflexProposals.map((proposal) => proposal.proposalKey),
            ...tissueCypherProposals.map((proposal) => proposal.proposalKey),
          ].join("|") || "no-panel-proposals"
        }
        opened={reflexModalOpened}
        proposals={reflexProposals}
        tissueCypherProposals={tissueCypherProposals}
        submitting={reflexSubmitting}
        onCancel={handleCancelReflexDecision}
        onContinue={handleContinueAfterReflexDecision}
      />

      {/* Discussion Required Modal (C-813 Section 7.2) */}
      {caseId && requiredDiscussionInfo && (
        <DiscussionRequiredModal
          opened={discussionRequiredModalOpened}
          onClose={() => {
            closeDiscussionRequiredModal();
            setRequiredDiscussionInfo(null);
          }}
          onSuccess={() => {
            closeDiscussionRequiredModal();
            setRequiredDiscussionInfo(null);
            refetchCase();
          }}
          onGoToDiscussion={() => {
            closeDiscussionRequiredModal();
            setRequiredDiscussionInfo(null);
            navigate(`/discussion-queue/${caseId}`);
          }}
          onSignOutIgnoring={() => {
            closeDiscussionRequiredModal();
            setRequiredDiscussionInfo(null);
            setDiscussionChecksSkipped(true);
            doSignOut(undefined, false, undefined, false, true, true);
          }}
          onSignOutAfterCreating={() => {
            closeDiscussionRequiredModal();
            setRequiredDiscussionInfo(null);
            setDiscussionChecksSkipped(true);
            doSignOut(undefined, false, undefined, false, true);
          }}
          caseId={caseId}
          missingDiscussions={requiredDiscussionInfo.missingDiscussions}
          existingDiscussions={requiredDiscussionInfo.existingDiscussions}
        />
      )}

      {/* Pending Discussions Modal — non-blocking override */}
      {caseId && (
        <PendingDiscussionsModal
          opened={pendingDiscussionsModalOpened}
          onClose={closePendingDiscussionsModal}
          onGoToDiscussion={() => {
            closePendingDiscussionsModal();
            navigate(`/discussion-queue/${caseId}`);
          }}
          onSignOffAnyway={() => {
            closePendingDiscussionsModal();
            setDiscussionChecksSkipped(true);
            doSignOut(undefined, false, undefined, false, true);
          }}
          pendingCount={pendingDiscussionsOverrideCount}
        />
      )}

      {/* Malignancy Notification Modal */}
      {caseId && malignancyData && (
        <MalignancyNotificationModal
          opened={malignancyModalOpened}
          onClose={closeMalignancyModal}
          caseId={caseId}
          caseNumber={caseData?.caseNumber || ""}
          malignancies={malignancyData.malignancies}
          physician={malignancyData.physician}
          notification={malignancyData.notification}
          attempts={malignancyData.attempts}
          onInitiateCall={handleInitiateCall}
          onSendSms={handleSendSms}
          onSendFax={handleSendFax}
          onDocumentOutcome={handleDocumentOutcome}
          onGetRecordingUrl={handleGetRecordingUrl}
          onRefresh={refreshMalignancyData}
          isLoading={signOffLoading}
        />
      )}

      {/* Image Upload Modal */}
      {caseId && (
        <ImageUploadModal
          opened={imageUploadModalOpened}
          onClose={() => setImageUploadModalOpened(false)}
          caseId={caseId}
          onUploadSuccess={handleReportImagesChanged}
        />
      )}

      {/* Edit Image Modal */}
      <Modal
        opened={editImageModalOpened}
        onClose={handleCloseImageEdit}
        title={
          <Group gap="xs">
            <IconEdit size={20} />
            <Text fw={600}>Edit Image</Text>
          </Group>
        }
        centered
        size="md"
        closeOnClickOutside={!replacingImage}
      >
        <Stack gap="md">
          {editingImageData && (
            <Box
              style={{
                borderRadius: 8,
                overflow: "hidden",
                border: "1px solid var(--mantine-color-gray-3)",
                position: "relative",
              }}
            >
              <img
                src={
                  replacePreview ||
                  imageDownloadUrls[editingImageData.id] ||
                  undefined
                }
                alt={editingImageData.caption || "Image"}
                style={{
                  width: "100%",
                  maxHeight: 200,
                  objectFit: "contain",
                  display:
                    replacePreview || imageDownloadUrls[editingImageData.id]
                      ? "block"
                      : "none",
                }}
              />
              <Group
                justify="center"
                py="xs"
                style={{
                  background: "var(--mantine-color-gray-0)",
                  borderTop: "1px solid var(--mantine-color-gray-3)",
                }}
              >
                <FileButton
                  onChange={handleReplaceFileSelect}
                  accept="image/jpeg,image/png,image/tiff,image/jpg"
                >
                  {(props) => (
                    <Button
                      {...props}
                      variant="light"
                      size="xs"
                      leftSection={<IconReplace size={14} />}
                      disabled={replacingImage}
                    >
                      {replaceFile ? "Change File" : "Replace Image"}
                    </Button>
                  )}
                </FileButton>
                {replaceFile && (
                  <Text size="xs" c="dimmed">
                    {replaceFile.name} (
                    {(replaceFile.size / 1024 / 1024).toFixed(2)} MB)
                  </Text>
                )}
              </Group>
            </Box>
          )}
          <TextInput
            label="Label"
            placeholder="e.g., H&E low power, IHC CK20, Gross — bisected"
            value={editImageCaption}
            onChange={(e) => setEditImageCaption(e.currentTarget.value)}
            disabled={replacingImage}
            maxLength={100}
          />
          <Textarea
            label="Description"
            placeholder="Optional notes to help identify this image later"
            value={editImageDescription}
            onChange={(e) => setEditImageDescription(e.currentTarget.value)}
            minRows={2}
            autosize
            maxLength={500}
            disabled={replacingImage}
          />
          <TextInput
            label="Magnification"
            placeholder="e.g., 40x"
            value={editImageMagnification}
            onChange={(e) => setEditImageMagnification(e.currentTarget.value)}
            disabled={replacingImage}
          />
          <Checkbox
            label="Include in final report"
            checked={editImageIncludeInReport}
            onChange={(e) =>
              setEditImageIncludeInReport(e.currentTarget.checked)
            }
            disabled={replacingImage}
          />
          {replacingImage && (
            <Box>
              <Text size="xs" c="dimmed" mb={4}>
                Uploading replacement... {replaceProgress}%
              </Text>
              <Progress value={replaceProgress} animated />
            </Box>
          )}
          <Group justify="flex-end" gap="sm">
            <Button
              variant="light"
              onClick={handleCloseImageEdit}
              disabled={replacingImage}
            >
              Cancel
            </Button>
            <Button onClick={handleSaveImageEdit} loading={replacingImage}>
              Save
            </Button>
          </Group>
        </Stack>
      </Modal>

      {/* Delete Image Confirmation Modal */}
      <Modal
        opened={!!deleteImageId}
        onClose={() => setDeleteImageId(null)}
        title="Delete Image"
        centered
        size="sm"
      >
        <Stack gap="md">
          <Text size="sm">
            Are you sure you want to delete this image? This action cannot be
            undone.
          </Text>
          <Group justify="flex-end" gap="sm">
            <Button variant="light" onClick={() => setDeleteImageId(null)}>
              Cancel
            </Button>
            <Button color="red" onClick={handleDeleteImage}>
              Delete
            </Button>
          </Group>
        </Stack>
      </Modal>

      {/* Delete Document Confirmation Modal */}
      <Modal
        opened={!!deleteDocFileId}
        onClose={() => setDeleteDocFileId(null)}
        title="Delete Document"
        centered
        size="sm"
      >
        <Stack gap="md">
          <Text size="sm">
            Are you sure you want to delete this document? This action cannot be
            undone.
          </Text>
          <Group justify="flex-end" gap="sm">
            <Button variant="light" onClick={() => setDeleteDocFileId(null)}>
              Cancel
            </Button>
            <Button color="red" onClick={handleConfirmDeleteCaseFile}>
              Delete
            </Button>
          </Group>
        </Stack>
      </Modal>

      {/* Camera Capture Modal */}
      {caseId && (
        <CameraCaptureModal
          opened={cameraCaptureModalOpened}
          onClose={() => setCameraCaptureModalOpened(false)}
          caseId={caseId}
          onCaptureSuccess={handleReportImagesChanged}
        />
      )}

      {/* Duplicate Send-Out Confirmation Modal */}
      <Modal
        opened={duplicateConfirmOpened}
        onClose={closeDuplicateConfirm}
        title="External Opinion Already Requested"
        size="md"
      >
        <Stack gap="md">
          <Alert color="yellow" variant="light" icon={<IconAlertTriangle />}>
            <Text size="sm">
              An external opinion request already exists for this case.
            </Text>
          </Alert>
          {activeSendOuts.length > 0 && (
            <Stack gap="xs">
              <Text size="sm" fw={500}>
                Existing request{activeSendOuts.length > 1 ? "s" : ""}:
              </Text>
              {activeSendOuts.map((sendOut) => (
                <Group key={sendOut.id} gap="xs">
                  <Badge size="sm" variant="light" color="violet">
                    {sendOut.externalInstitution?.name || "Unknown Institution"}
                  </Badge>
                  <Text size="xs" c="dimmed">
                    {dayjs(sendOut.createdAt).format("MMM D, YYYY")}
                  </Text>
                </Group>
              ))}
            </Stack>
          )}
          <Divider />
          <Text size="sm" c="dimmed">
            Do you want to create another external opinion request for this
            case?
          </Text>
          <Group justify="flex-end" gap="xs">
            <Button variant="subtle" onClick={closeDuplicateConfirm}>
              Cancel
            </Button>
            <Button
              variant="light"
              color="violet"
              onClick={handleOpenConfirmedDuplicateExternalOpinion}
            >
              Create Another Request
            </Button>
          </Group>
        </Stack>
      </Modal>

      {/* Review Screen Preferences Modal */}
      <ReviewPreferencesModal
        opened={reviewPrefsOpened}
        onClose={closeReviewPrefs}
        currentPreferences={reviewPreferences}
        onSave={handleSaveReviewPreferences}
        isSaving={isSavingReviewPrefs}
      />

      {/* Send Report Modal */}
      {caseId && caseData && (
        <SendReportModal
          opened={sendReportModalOpened}
          onClose={closeSendReportModal}
          caseId={caseId}
          physicianId={caseData.physicianId ?? null}
          sendingPracticeId={caseData.sendingPracticeId ?? null}
        />
      )}

      <RecallReportModal
        opened={reportRecallModalOpened}
        onClose={closeReportRecallModal}
        state={reportRecallState}
        result={reportRecallResult}
        error={reportRecallError}
        isSubmitting={isReportRecallSubmitting}
        isReconciling={isReportRecallReconciling}
        onSubmit={submitReportRecall}
        onCheckNow={checkReportRecallNow}
        caseNumber={caseData?.caseNumber}
      />

      <ReportDeliveryHistoryModal
        opened={reportDeliveryHistoryOpened}
        onClose={closeReportDeliveryHistory}
        state={displayedReportRecallState}
        caseNumber={caseData?.caseNumber}
      />
    </>
  );
}
